import React, { useEffect, useRef, useState } from 'react';
import * as dot from 'dot-prop';
import { animated, useSpring } from '@react-spring/web';
import moment from 'moment-timezone';
import update from 'immutability-helper';
import { useDataStore } from 'files/StateManagement.js';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import Account from 'classes/Account.js';
import ActionSteps from 'views/ActionSteps.js';
import { AltBadge } from 'views/Main.js';
import AltFieldMapper, { AltFieldItem, validateRequiredFields } from 'views/AltFieldMapper';
import Appearance from 'styles/Appearance.js';
import AnimatedElement from 'views/AnimatedElement.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import Button from 'views/Button.js';
import Checkbox from 'views/Checkbox.js';
import DatePickerDropdownField from 'views/DatePickerDropdownField.js';
import Device from 'classes/Device.js';
import FieldMapper from 'views/FieldMapper.js';
import FilePickerField from 'views/FilePickerField.js';
import Instruction from 'classes/Instruction.js';
import { JitsiMeeting as JitsiMeetingComponent } from '@jitsi/react-sdk';
import Layer, { LayerItem, LayerItemSpacing } from 'structure/Layer';
import LegalAgreements from 'views/LegalAgreements.js';
import ListField from 'views/ListField.js';
import LottieView from 'views/Lottie.js';
import { NewSubscription, processInstructionRejection } from 'managers/Payments.js';
import { PanelContentContainer } from 'structure/Panel.js';
import PageControl from 'views/PageControl.js'; 
import QueryString from 'query-string';
import Request from 'files/Request.js';
import Subscription from 'classes/Subscription.js';
import TextField from 'views/TextField.js';
import TrustedContact from 'classes/TrustedContact.js';
import User from 'classes/User.js';
import UserLookup from 'views/UserLookup.js';
import Utils from 'files/Utils.js';

export const AccessGrantDetails = ({ account, authCredential }, { abstract, index, options, utils }) => {

    const resource = {
        category: 'access_grants',
        id: `access_grants.${abstract.getID()}`,
        target: abstract.object,
        title: `Manage Access Grant for ${abstract.object.entity.full_name}`
    }

    const [accessGrant, setAccessGrant] = useState(abstract.object);
    const [instruction, setInstruction] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { requiem } = useDataStore();

    const onActiveStatusClick = () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // request verification data from user
                    let verificationData = await getVerificationData();

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        id: abstract.getID(),
                        status: !accessGrant.active,
                        type: 'set_access_grant_status',
                        verification_data: verificationData
                    });

                    // update target and notify subscribers of data change, fetch is required to refresh in-use flags
                    abstract.object.active = !abstract.object.active;
                    setAccessGrant(abstract.object);
                    utils.content.fetch('access_grants');

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `Your access grant for ${accessGrant.entity.full_name} has been ${accessGrant.active ? 'activated' : 'deactivated'}.` });
        
                } catch(e) {
                    let error = new Error(`There was an issue changing your access grant's status. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let status = accessGrant.active;
            utils.alert.show({
                title: `${status ? 'Deactivate' : 'Activate'}`,
                message: `Are you sure that you want to ${status ? 'deactivate' : 'activate'} this access grant? This will ${status ? 'prevent' : 'allow'} ${accessGrant.entity.full_name} ${status ? 'from accessing' : 'to access'} the account data. We will email ${accessGrant.entity.first_name} and let them know about this change.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: status ? 'destructive' : 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: status ? 'default' : 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onCloseClick = () => {
        requiem.functions.smart_flow.cancel(resource);
        setLayerState('close');
    }
    
    const onInstructionClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: instruction,
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { isNewTarget: false }),
            id: `trigger_instructions.${instruction.id}`
        });
    }

    const onRenewClick = () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async days => {
                try {

                    // request verification data from user
                    let verificationData = await getVerificationData();

                    // submit request to server
                    let { duration_remaining, expiration_date } = await Request.post(utils, '/users/', {
                        days: days,
                        id: abstract.getID(),
                        type: 'renew_access_grant',
                        verification_data: verificationData
                    });

                    // update target and notify subscribers of data change, fetch is required to refresh in-use flags
                    abstract.object.duration_remaining = duration_remaining;
                    abstract.object.expiration_date = moment.utc(expiration_date).local();
                    setAccessGrant(abstract.object);
                    utils.content.fetch('access_grants');

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `Your access grant for ${accessGrant.entity.full_name} has renewed for ${days} ${days === 1 ? 'day' : 'days'} and will expire on ${abstract.object.expiration_date.format('MMMM Do, YYYY')}.`});
        
                } catch(e) {
                    let error = new Error(`There was an issue updating your access grant. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let days = 0;
            utils.alert.show({
                title: 'Renew Access Grant',
                message: `Rewewing this access grant will extend the amount of time that ${abstract.object.entity.full_name} has access to the associated account data. How many days of extra access would you like to add?`,
                content: (
                    <div style={{
                        padding: 12,
                        width: '100%'
                    }}>
                        <TextField
                        format={'number'}
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => days = text}
                        placeholder={'Number of Days'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && days > 0) {
                        setTimeout(onConfirm.bind(this, days), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onResendInvitationClick = () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async emailAddress => {
                try {

                    // request verification data from user
                    let verificationData = await getVerificationData();
                    
                    // submit request to server
                    await Request.post(utils, '/users/', {
                        email_address: emailAddress,
                        id: abstract.getID() ,
                        type: 'resend_access_grant_invitation',
                        verification_data: verificationData
                    });

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `Your access grant invitation for ${accessGrant.entity.full_name} has been sent to ${emailAddress}.`});
        
                } catch(e) {
                    let error = new Error(`There was an issue sending your access grant invitation. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let emailAddress = abstract.object.entity.email_address;
            utils.alert.show({
                title: 'Resend Invitation',
                message: `Resending the invitation will deliver a Secure Portal link to ${abstract.object.entity.full_name} via email. Which email address should we use?`,
                content: (
                    <div style={{
                        padding: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => emailAddress = text}
                        placeholder={'Email Address'} 
                        value={emailAddress} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Send',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && emailAddress) {
                        setTimeout(onConfirm.bind(this, emailAddress), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const getAccessManagementComponents = () => {
        return accessGrant.expired === false && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <SystemEventsAsyncInteractionItem abstract={abstract} utils={utils} />
                    <AsyncInteractionItem 
                    description={`This allows you to extend the amount of time that ${abstract.object.entity.full_name} has access to the associated account data.`}
                    image={'images/access-grant-renew-icon-blue.png'}
                    onAsyncClick={onRenewClick}
                    title={'Renew'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={`This will resend the Secure Portal invitation link to ${abstract.object.entity.full_name} so they can access the associated account data.`}
                    image={'images/send-icon-small.png'}
                    onAsyncClick={onResendInvitationClick}
                    title={'Resend Invitation'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={`This will ${accessGrant.active ? 'prevent' : 'allow'} ${abstract.object.entity.full_name} ${accessGrant.active ? 'from accessing' : 'to access'} the associated account data.`}
                    image={accessGrant.active ? 'images/deactivate-red.png' : 'images/activate-green.png'}
                    lastItem={true}
                    onAsyncClick={onActiveStatusClick}
                    title={accessGrant.active ? 'Deactivate' : 'Activate'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            key: 'details',
            title: 'Details',
            items: [{
                description: 'The account that this access grant provides data access to.',
                key: 'account',
                requiemSmartFlow: true,
                title: 'Account Name',
                value: account?.name,
                visible: account ? true : false
            },{
                description: 'The date that this access grant was officailly activated and started to provide access to the associated account data.',
                key: 'activation_date',
                requiemSmartFlow: true,
                title: 'Activation Date',
                value: accessGrant.activation_date && Utils.format.date(accessGrant.activation_date),
                visible: accessGrant.activation_date ? true : false
            },{
                description: 'The date that this access grant was created.',
                key: 'date',
                requiemSmartFlow: true,
                title: 'Created',
                value: Utils.format.date(accessGrant.date)
            },{
                description: 'The date that this access grant will expire and no longer provide access to the associated account data.',
                key: 'expiration_date',
                requiemSmartFlow: true,
                title: 'Expiration Date',
                value: accessGrant.expiration_date && Utils.format.date(accessGrant.expiration_date),
                visible: accessGrant.expiration_date ? true : false
            },{
                description: 'The unique identifier for this access grant within the LifeWipe system.',
                key: 'id',
                requiemSmartFlow: true,
                title: 'ID',
                value: `AG-${abstract.getID()}`
            },{
                description: 'The date that this access grant will begin and provide access to the associated account data.',
                key: 'start_date',
                requiemSmartFlow: true,
                title: 'Start Date',
                value: accessGrant.start_date && Utils.format.date(accessGrant.start_date),
                visible: accessGrant.activation_date === null && accessGrant.start_date ? true : false
            },{
                description: 'The status representing whether this access grant is active, inactive, or expired.',
                key: 'status',
                requiemSmartFlow: true,
                title: 'Status',
                value: getStatusText()
            },{
                description: 'The amount of time remaining until this access grant expires and no longer provides access to the associated account data.',
                key: 'duration_remaining',
                requiemSmartFlow: true,
                title: 'Time Until Expiration',
                value: Utils.parseDuration(accessGrant.duration_remaining || 0, { largestUnitOnly: true }),
                visible: accessGrant.duration_remaining > 0 ? true : false
            }]
        },{
            collapsed: false,
            key: 'entity',
            lastItem: false,
            title: 'Trusted Contact',
            items: [{
                description: `The email address associated with the trusted contact who was granted access.`,
                key: 'email_address',
                onClick: window.open.bind(this, `mailto:${accessGrant.entity.email_address}`),
                requiemSmartFlow: true,
                title: 'Email Address',
                value: accessGrant.entity.email_address
            },{
                description: `The first and last name associated with the ${accessGrant.entity_type} who was granted access.`,
                key: 'full_name',
                requiemSmartFlow: true,
                title: 'Full Name',
                value: accessGrant.entity.full_name
            },{
                description: `The unique identifier for this trusted contact within the LifeWipe system.`,
                key: 'id',
                requiemSmartFlow: true,
                title: 'ID',
                value: `TC-${accessGrant.entity.id}`,
            },{
                description: `The phone number associated with the trusted contact who was granted access.`,
                key: 'phone_number',
                onClick: window.open.bind(this, `tel:${accessGrant.entity.phone_number}`),
                requiemSmartFlow: true,
                title: 'Phone Number',
                value: Utils.format.phoneNumber(accessGrant.entity.phone_number)
            }]
        }];
    }

    const getInstruction = () => {
        return instruction !== null && (
            <LayerItem
            collapsed={false}
            lastItem={accessGrant.expired ? true : false}
            title={'Authorized By Instruction'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={instruction.description}
                    image={'images/user-placeholder.png'}
                    lastItem={true}
                    onAsyncClick={onInstructionClick}
                    title={instruction.name}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getStatusText = () => {
        if(accessGrant.activation_date === null) {
            return 'Pending';
        }
        if(accessGrant.expired === true) {
            return 'Expired';
        }
        return accessGrant.active === false ? 'Inactive' : 'Active';
    }

    const getVerificationData = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // return provided auth credential if applciable
                if(authCredential) {
                    resolve(authCredential);
                    return;
                }

                // request new auth credential from processor
                let result = await getAuthCredential({ 
                    abstract: Abstract.create({
                        object: account,
                        type: 'accounts' 
                    }),
                    utils
                });
                resolve(result);
            } catch(e) {
                reject(e);
            }
        });
    }

    const fetchDetails = async () => {
        try {
            setLoading(true);
            let { instruction } = await Request.get(utils, '/users/', {
                id: abstract.getID(),
                type: 'access_grant_details'
            });

            setLoading(false);
            setInstruction(instruction);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the details for this access grant. ${e.message || 'An unknown error occurred'}`,
                origin: 'access_grant_details.fetch_details'
            });
        }
    }

    useEffect(() => {   
        fetchDetails();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading
        }}
        utils={utils}>
            <FieldMapper 
            resourceID={resource.id}
            sections={getFields()}
            utils={utils} />
            {getInstruction()}
            {getAccessManagementComponents()}
        </Layer>
    )
}

export const AccountComponent = ({ account, bottomBorder = false, onClick }) => {

    const getContent = () => {
        return (
            <div 
            className={`view-entry ${window.theme}`}
            onClick={typeof(onClick) === 'function' ? onClick.bind(this, account) : null}
            style={{
                alignItems: 'center',
                backgroundColor: null,
                borderBottom: bottomBorder === true && `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                padding: 16,
                width: '100%'
            }}>
                <img 
                className={'d-none d-lg-flex'}
                src={account.provider?.image_url || window.theme === 'dark' ? 'images/logo-badge-dark.png' : 'images/logo-badge-light.png'}
                style={{
                    height: 50,
                    marginRight: 16,
                    objectFit: 'contain',
                    width: 50
                }} />
                <div style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minWidth: 0
                }}>
                    <span style={{
                        ...Appearance.textStyles.header(),
                        fontFamily: 'Zen Dots'
                    }}>{account.name.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{account.last_updated_date ? `Last Updated: ${Utils.format.date(account.last_updated_date)}` : `Created: ${Utils.format.date(account.created)}`}</span>
                </div>
                {getWarnings()}
                <img
                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                style={{
                    height: 20,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 15
                }} />
            </div>
        )
    }   

    const getWarnings = () => {

        // determine if a requiem review is pending
        let requiemReviewRequired = account.metadata?.requiem_generated === true && account.metadata?.requiem_confirmation_date === null ? true : false;

        // determine if two factor verified phone number needs to be referenced
        let twoFactorVerified = account.management_enabled ? account.metadata?.external_two_factor_verified : null;

        if(requiemReviewRequired === true || twoFactorVerified === false) {

            // prepare a hover title for the icon
            let title = requiemReviewRequired && twoFactorVerified === false ? ['Two Factor Verification Required', 'Requiem Review Required'].join(' & ') : requiemReviewRequired ? 'Requiem Review Required' : 'Two Factor Verification Required';

            return (
                <img 
                title={title}
                src={'images/warning-icon-yellow.png'}
                style={{
                    height: 25,
                    marginLeft: 8,
                    width: 25
                }} />
            )
        }
    }

    return getContent();
}

export const Accounts = ({ utils }) => {

    const limit = 5;
    const offset = useRef(0);

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [managedAccounts, setManagedAccounts] = useState([]);
    const [managedSearchText, setManagedSearchText] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const { system } = useDataStore();

    const onAccountClick = account => {
        utils.layer.open({
            abstract: Abstract.create({
                object: account,
                type: 'accounts'
            }),
            Component: AddEditAccount.bind(this, { isNewTarget: false }),
            id: `linked_accounts.${account.id}`
        });
    }

    const onAddNewAccountClick = async () => {
        try {

            // verify that user has access to this feature
            await utils.user.capabilities.verify('accounts', 'create');

            // show layer with options to add an account
            utils.layer.open({
                abstract: Abstract.create({
                    object: Account.new(),
                    type: 'accounts'
                }),
                Component: AddEditAccount.bind(this, { isNewTarget: true }),
                id: 'new_linked_account'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onImportAccountsClick = async () => {
         try {

            // verify that user has access to this feature
            await utils.user.capabilities.verify('accounts', 'create');

            // show layer with options to import accounts
            utils.layer.open({
                Component: ImportAccounts,
                id: 'import_accounts'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const getAccounts = (managed = false) => {

        let targets = managed === true ? managedAccounts : accounts;
        if(loading === true) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(targets.length === 0) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: 16,
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minWidth: 0
                        }}>
                            <span style={{
                                ...Appearance.textStyles.header(),
                                fontFamily: 'Zen Dots'
                            }}>{`No ${managed ? 'Managed Accounts' : 'Locket Passwords'} Found`.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: Appearance.colors.subText(),
                                whiteSpace: 'normal'
                            }}>{`We were unable to locate any ${managed ? 'managed accounts' : 'Locket passwords'} for your profile.`}</span>
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div 
            className={'text-left text-xl-center'}
            style={{
                ...Appearance.styles.unstyledPanel(),
                alignItems: 'center',
                borderRadius: 10,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxWidth: 1250,
                width: '100%'
            }}>
                {targets.map((account, index, accounts) => {
                    return (
                        <AccountComponent 
                        account={account}
                        bottomBorder={index !== accounts.length - 1} 
                        key={index} 
                        onClick={onAccountClick} />
                    )
                })}
            </div>
        )
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'add_account',
            onClick: onAddNewAccountClick,
            text: 'Add Account'
        },{
            color: 'grey',
            id: 'import_accounts',
            onClick: onImportAccountsClick,
            text: 'Import Accounts'
        }];
    }

    const getSearchField = (managed = false) => {
        return (
            <div style={{
                marginBottom: system.window.isMobile ? 16 : 24,
                width: '100%'
            }}>
                <TextField
                icon={'search'}
                loading={loading === (managed ? 'managed_search' : 'search')}
                onChange={text => {
                    offset.current = 0;
                    managed ? setManagedSearchText(text) : setSearchText(text);
                }}
                placeholder={'Search by account name, provider, or id...'}
                useDelay={true}
                value={managed ? managedSearchText : searchText} />
            </div>
        )
    }

    const fetchAccounts = async () => {
        try {

            let { accounts, paging } = await Request.get(utils, '/accounts/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                type: 'list'
            });

            setLoading(false);
            setAccounts(accounts.map(account => Account.create(account)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your Locket accounts list. ${e.message || 'An unknown error occurred'}`,
                origin: 'accounts.fetch_accounts'
            });
        }
    }

    const fetchManagedAccounts = async () => {
        try {

            let { accounts, paging } = await Request.get(utils, '/accounts/', {
                limit: limit,
                management_enabled: true,
                offset: offset.current,
                search_text: managedSearchText,
                type: 'list'
            });

            setLoading(false);
            setManagedAccounts(accounts.map(account => Account.create(account)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your managed accounts list. ${e.message || 'An unknown error occurred'}`,
                origin: 'accounts.fetch_managed_accounts'
            });
        }
    }

    useEffect(() => { 
        setLoading('managed_search');
        fetchManagedAccounts();
    }, [managedSearchText]);

    useEffect(() => { 
        setLoading('search');
        fetchAccounts();
    }, [searchText]);

    useEffect(() => {
        utils.content.subscribe('accounts_list', ['accounts'], {
            onFetch: fetchAccounts,
            onUpdate: abstract => {
                setAccounts(accounts => {
                    return accounts.map(account => {
                        return account.id === abstract.getID() ? abstract.object : account;  
                    });
                });
                setAccounts(accounts => {
                    return accounts.map(account => {
                        return account.id === abstract.getID() ? abstract.object : account;  
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('accounts_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Locket is LifeWipe’s free, built-in password manager — designed to securely store your logins and digital credentials in one place. To get started, click “Add Account” to manually save a login, or choose “Import Accounts” to bring in credentials from browsers like Chrome and Safari, or password managers like 1Password.`}
                buttons={getContainerButtons()}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/locket-password-manager.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'Locket Passwords'} />
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`This is where you manage the usernames and passwords you've stored in Locket Password Manager. You can view or update login details, share access securely, or remove accounts you no longer need. Use the search bar to quickly find specific accounts.`}
                title={'My Passwords'}>
                    {getSearchField(false)}
                    {getAccounts(false)}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchAccounts();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`These are the accounts you’ve intentionally chosen to manage with LifeWipe. When an account is marked as managed, it’s securely linked and eligible for protection, transfer, or closure based on the preferences you’ve set. LifeWipe only acts within the boundaries you define. You stay in control at all times and can update, pause, or remove managed accounts whenever your needs change.`}
                title={'Managed Accounts'}>
                    {getSearchField(true)}
                    {getAccounts(true)}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'managed_paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('managed_paging');
                            fetchManagedAccounts();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const AccountTwoFactorSetup = ({ onComplete }, { abstract, index, options, utils }) => {
                    
    const layerID = `account_two_factor_setup.${abstract.getID()}`;
    const twoFactorCounter = useRef(null);

    const [completionToken, setCompletionToken] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { sockets, setRequiem } = useDataStore();
    const [twoFactorCode, setTwoFactorCode] = useState(null);
    const [twoFactorExpirationDate, setTwoFactorExpirationDate] = useState(null);
    const [view, setView] = useState(null);

    const onAskRequiemClick = () => {
        setRequiem(requiem => {
            requiem.request = { 
                loading: true,
                metadata: { 
                    provider_name: abstract.object.provider?.title 
                },
                preset: 'linked_account_two_factor_trusted_phone_number_support' 
            }
            return requiem;
        });
    }

    const onCancelSecureLogin = async () => {
        try {

            // submit request to server
            setLoading('back');
            await Request.post(utils, '/accounts/', {
                id: abstract.getID(),
                type: 'cancel_two_factor_login'
            });

            // clear expiration date from local state and reset view to start
            setLoading(false);
            setTwoFactorExpirationDate(null);
            setView(null);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue cancelling secure login. ${e.message || 'An unknown error occurred'}`,
                origin: 'account_two_factor_setup.on_cancel_secure_login'
            })
        }
    }

    const onDoneClick = async () => {
        try {

            // submit request to server
            setLoading('done');
            await Request.post(utils, '/accounts/', {
                id: abstract.getID(),
                token: completionToken,
                type: 'set_trusted_phone_number_verification'
            });

            // update target with result and notify subscribers
            abstract.object.metadata.external_two_factor_verified = true;
            utils.content.update(abstract);

            // notify subsribers of data change if applicable
            if(typeof(onComplete) === 'function') {
                onComplete();
            }

            // end loading and show completion banner to user
            setLoading(false);
            utils.banner.show({
                title: 'Trusted Phone Number Verified',
                message: `You've successfully verified your trusted phone number for two factor authentication on your ${abstract.object.provider?.title} account. LifeWipe will now use this number to securely access your account when needed.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your linked account. ${e.message || 'An unknown error occurred'}`,
                origin: 'account_two_factor_setup.on_done_click'
            })
        }
    }

    const onNewAccountTwoFactorSMSCode = evt => {
        setCompletionToken(evt.token);
        setTwoFactorCode(evt.code);
    }

    const onStartTwoFactor = async () => {
        return new Promise(async (resolve, reject) => {
            try {
        
                // submit request to server
                let { expiration_date } = await Request.post(utils, '/accounts/', {
                    id: abstract.getID(),
                    type: 'start_two_factor_login'
                });

                // set expiration date for two factor flow and update view
                setTwoFactorExpirationDate(moment.utc(expiration_date).local());
                setView('two_factor_code');
                resolve();

            } catch(e) {
                let error = new Error(`There was an issue starting the two factor flow. ${e.message || 'An unknown error occurred'}`);
                reject(error);
            }
        });
    }

    const onUpdateTwoFactorCounter = () => {

        // clear any pending countdowns if applicable
        if(twoFactorCounter.current) {

            // clear interval and reset seconds remaining
            clearInterval(twoFactorCounter.current);
            twoFactorCounter.current = null;

            // remove connection listener for new 2fa codes
            sockets.off('accounts', 'new_two_factor_sms_code', onNewAccountTwoFactorSMSCode);
        }

        // create a new counter if applicable
        if(twoFactorExpirationDate) {

            // add connection listener for new 2fa codes
            sockets.on('accounts', 'new_two_factor_sms_code', onNewAccountTwoFactorSMSCode);

            // calculate diff seconds between now and the expiration date
            let secondsRemaining = twoFactorExpirationDate.diff(moment(), 'seconds');

            // set new interval to update counter every second
            twoFactorCounter.current = setInterval(() => {

                // end counter if applicable
                secondsRemaining--;
                if(secondsRemaining <= 0) {

                    // clear interval and remove ref
                    clearInterval(twoFactorCounter.current);
                    twoFactorCounter.current = null;

                    // show alert to user that session has expired
                    utils.alert.show({
                        title: 'Session Expired',
                        message: `The two factor verification session has expired. Please start the process again to receive a new verification code.`,
                        buttons: [{
                            key: 'confirm',
                            title: 'Okay',
                            style: 'default'
                        }],
                        onClick: () => {
                            setView(null);
                            return;
                        }
                    })
                }
            }, 1000);
        }
    }

    const getButtons = () => {
        if(view === 'two_factor_code') {
            return [{
                color: 'dark',
                id: 'back',
                loading: loading === 'back',
                onClick: onCancelSecureLogin,
                text: 'Back'
            },{
                color: 'primary',
                id: 'done',
                loading: loading === 'done',
                onClick: onDoneClick,
                text: 'Done',
                visible: twoFactorCode ? true : false
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getContent = () => {
        if(view === 'two_factor_code') {
            return (
                <>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'Add Trusted Phone Number'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block'
                        }}>
                            {`To proceed, you'll need to add `}
                            <b>{`${Utils.format.phoneNumber(abstract.object.metadata?.two_factor_phone_number)}`}</b>
                            {` as a trusted phone number on your ${abstract.object.provider?.title} account. This number is essential — it allows LifeWipe to authenticate into your ${abstract.object.provider?.title} account or execute secure actions — including emergency wipes, legacy transfers, or credential recovery.`}</span>
                    </div>
                </LayerItem>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'Wait for Verification Code'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block',
                            marginBottom: 16
                        }}>{`After you add the number, ${abstract.object.provider?.title} will send a verification code directly to LifeWipe. You don’t need to check your phone or inbox — just remain on this screen. We’ll display the code below as soon as we receive it.`}</span>
                        {getTwoFactorCounter()}
                    </div>
                </LayerItem>
                <LayerItem 
                collapsed={false}
                lastItem={false}
                title={'Enter Code to Confirm'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block'
                        }}>
                            {`Once the code appears, enter it into your ${abstract.object.provider?.title} account to confirm the trusted number. After ${abstract.object.provider?.title} accepts the code, `}
                            <b>{`click the “Done” button below to let us know the process is complete`}</b>
                            {`. That final confirmation allows us to flag your ${abstract.object.provider?.title} account as fully verified and ready for secure actions.`}
                        </span>
                    </div>
                </LayerItem>
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.5,
                    width: '100%'
                }}>{`Until these steps are finished, LifeWipe cannot authenticate into your ${abstract.object.provider?.title} account — even in the event of an emergency or posthumous action. Completing this process ensures we can honor your digital wishes when it counts.`}</span>
                </>
            )
        }
        return (
            <>
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Trusted Phone Number'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block'
                    }}>
                        {`Some accounts — like banks, cloud services, or email providers — require a trusted phone number to confirm identity when someone tries to access them. LifeWipe uses special phone numbers to meet that requirement safely. These numbers aren’t used for calls or messages. They’re only used to `}
                        <b>{`gain access when it’s needed`}</b>
                        {`, based on your `}
                        <b>{`trigger instructions`}</b>
                        {` — like a death confirmation, life-check failure, or emergency button.`}
                    </span>
                </div>
            </LayerItem>
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Next Steps'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'Start the phone number verification process to enable two factor authentication for this account.'}
                    image={'images/account-2fa-verify-phone-number-blue.png'}
                    onAsyncClick={onStartTwoFactor}
                    title={'Verify Phone Number'}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'Questions about two factor verified phone numbers? Ask Requiem for assistance.'}
                    image={'images/requiem-ai-green.png'}
                    lastItem={true}
                    onClick={onAskRequiemClick}
                    title={'Learn More with Requiem'}
                    utils={utils} />
                </div>
            </LayerItem>
            </>
        )
    }

    const getTwoFactorCounter = () => {

        let characters = (twoFactorCode || '------').split('');
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                alignItems: 'center',
                backgroundColor: Appearance.colors.divider(),
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                height: 75,
                width: '100%'
            }}>
                {characters.map((char, index) => {
                    return (
                        <span
                        key={index}
                        style={{
                            color: Appearance.colors.text(),
                            fontFamily: "IBM Plex Mono",
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: 28,
                            paddingLeft: 4,
                            paddingRight: 4,
                        }}>
                            {char}
                        </span>
                    )
                })}
            </div>
        )
    }

    useEffect(() => {
        onUpdateTwoFactorCounter();
        return () => {
            twoFactorCounter.current && clearInterval(twoFactorCounter.current);
        }
    }, [twoFactorExpirationDate]);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading
        }}
        utils={utils}>
            {getContent()}
        </Layer>
    )
}

export const AddEditAccount = ({ configuring = false, isNewTarget, onAccountAdded, onChange }, { abstract, index, options, utils }) => {

    const resource = {
        category: 'linked_accounts',
        id: isNewTarget ? 'new_linked_account' : `linked_accounts.${abstract.getID()}`,
        target: abstract.object,
        title: isNewTarget ? 'Add To Locket Passwords' : `Update Locket Password: ${abstract.getTitle()}`,
    }

    const layerRef = useRef(null);
    const requestProps = useRef({});

    const [accessGrants, setAccessGrants] = useState([]);
    const [authCredential, setAuthCredential] = useState(null);
    const [categories, setCategories] = useState([]);
    const [canEditSensitiveValues, setCanEditSensitiveValues] = useState(isNewTarget === true);
    const [edits, setEdits] = useState({ ...abstract.object });
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [twoFactorPhoneNumber, setTwoFactorPhoneNumber] = useState(null);
    const { requiem, user, setRequiem, setUser } = useDataStore();
    const [view, setView] = useState(null);

    const onAccessGrantClick = grant => {
        utils.layer.open({
            abstract: Abstract.create({
                object: grant,
                type: 'access_grants'
            }),
            Component: AccessGrantDetails.bind(this, { 
                account: abstract.object,
                authCredential: authCredential
            }),
            id: `access_grants.${grant.id}`
        });
    }
    
    const onCloseClick = () => {

        // no further logic is required if no edits have been made
        if(hasMadeEdits === false) {
            setLayerState('close');
            return;
        }

        // show alert requesting confirmation to close the layer without saving changes
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitRequest, 500);
                    return;
                }
                setLayerState('close');
            }
        });
    }

    const onDeleteAccount = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // request authentication value from user
                    let result = await requestAuthenticationValue();

                    // set loading flag and submit request to server
                    await Request.post(utils, '/accounts/', {
                        id: abstract.getID(),
                        type: 'delete',
                        verification_data: result
                    });

                    // notify subscribers of data change
                    utils.content.fetch('accounts');

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        banner: true,
                        message: `The linked account '${abstract.object.name}' has been deleted and removed from your profile.`,
                        title: 'Linked Account Deleted',
                        onShow: setLayerState.bind(this, 'close')
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue deleting your linked account. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Delete Account',
                message: `Are you sure that you want to unlink this account and delete it from your profile?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onRequestAuthValidation = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // start flow for key request and validation
                let result = await requestAuthenticationValue();

                // submit authentication value to server for validation
                let { authentication, config, credentials } = await Request.post(utils, '/accounts/', {
                    id: abstract.getID(),
                    type: 'verify_authentication_credentials',
                    verification_data: result
                });

                // end loading and set sensitive values in local state
                setEdits(edits => ({ ...edits, authentication, config, credentials }));

                // set auth crendential for future use if it is not a two factor sms code
                if(abstract.object.authentication_method !== 'two_factor_sms') {
                    setAuthCredential(result);
                }

                // update state for editing flag
                setCanEditSensitiveValues(true);
                resolve();

            } catch(e) {
                setAuthCredential(null);
                setCanEditSensitiveValues(false);
                reject(e);
            }
        });
    }

    const onRequestKeyClick = async () => {
        utils.alert.show({
            title: 'Access Key',
            message: 'Your access key is an encryption key used to encrypt the configuration and credentials for this account. Would you like to generate a new access key or use an existing access key?',
            buttons: [{
                key: 'new',
                title: 'Generate New Key',
                style: 'default'
            },{
                key: 'existing',
                title: 'Use Existing Key',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(key !== 'cancel') {
                    try {

                        // submit request to retrieve passkey
                        let result = key === 'new' ? await getNewPassKey() : await getExistingPassKey();

                        // end loading and update target with key selection
                        setLoading(false);
                        onUpdateTarget({
                            authentication: update(edits.authentication || {}, {
                                mfa_passkey: {
                                    $set: result
                                }
                            })
                        });

                    } catch(e) {
                        setLoading(false);
                        console.error(e.message);
                    }
                }
            }
        });
    }

    const onRequiemActionClick = (action, metadata) => {
        setRequiem(requiem => {
            requiem.request = { 
                loading: true,
                metadata,
                preset: action
            }
            return requiem;
        });
    }

    const onSubmitRequest = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // prepare payload object
            let payload = { 
                ...edits,
                verification_data: authCredential 
            };

            // determine if a two factor phone number needs to be added to the configuration
            let { two_factor_required, two_factor_phone_number } = edits.config || {};
            if(two_factor_required && !two_factor_phone_number) {
                payload.config.two_factor_phone_number = twoFactorPhoneNumber;
            }

            // no additional logic is required if the account is only being configured
            if(configuring) {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange(payload);
                }
                return;
            }

            // require that existing targets have submitted an auth value
            if(isNewTarget === false && !payload.verification_data) {
                payload.verification_data = await requestAuthenticationValue();
            }

            // set request props and update view state
            requestProps.current = payload;
            setLayerState('submit_api_request');

        } catch(e) {
            utils.alert.show({
                message: e.message || 'An unknown error occurred',
                title: 'Oops!'
            });
        }
    }

    const onTokenizedFieldClick = async id => {
        try {

        } catch(e) {
            console.error(e.message);
        }
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(({ ...edits, ...props }));
    }

    const onVerifyTwoFactorPhoneNumberClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: AccountTwoFactorSetup.bind(this, {
                onComplete: () => {
                    abstract.object.metadata.external_two_factor_verified = true;
                    setEdits(edits => ({ ...edits, metadata: abstract.object.metadata }));
                }
            }),
            id: `account_two_factor_setup.${abstract.getID()}`,
        });
    }

    const getAccessGrants = () => {

        if(isNewTarget === true || accessGrants.length === 0) {
            return null;
        }

        let shouldAutoCollapse = shouldAutoCollapseSections();
        if(canEditSensitiveValues === false) {
            return (
                <LayerItem 
                collapsed={shouldAutoCollapse}
                title={'Access Grants'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        <AsyncInteractionItem 
                        description={'This area is locked behind additional authentication. Click here to authenticate.'}
                        image={'images/prohibitory-red-small.png'}
                        lastItem={true}
                        onAsyncClick={onRequestAuthValidation}
                        title={'Authentication Required'}
                        utils={utils} />
                    </div>
                </LayerItem>
            )
        }
        return (
            <LayerItem 
            collapsed={shouldAutoCollapse}
            title={'Access Grants'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    overflow: 'visible',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: Appearance.styles.bottomBorder(),
                        display: 'block',
                        padding: 16,
                        paddingTop: 12
                    }}>{`Access Grants let you share specific data from your LifeWipe account with trusted contacts — securely and on your terms. These temporary permissions can be revoked at any time, and you’ll be notified each time someone views or retrieves data through a grant. Control stays in your hands, even when you choose to share.`}</span>
                    {accessGrants.map((grant, index) => {
                        return (
                            <AsyncInteractionItem
                            description={Utils.format.date(grant.date)}
                            image={grant.active ? 'images/data-grant-icon-green.png' : 'images/data-grant-icon-grey.png'}
                            lastItem={index === accessGrants.length - 1}
                            key={index}
                            onAsyncClick={onAccessGrantClick.bind(this, grant)}
                            rightContent={getAccessGrantStatusBadge(grant)}
                            title={grant.entity?.full_name || 'Name not available'}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getAccessGrantStatusBadge = grant => {
        if(grant.active === false) {
            return (
                <AltBadge 
                content={{
                    color: Appearance.colors.grey(),
                    text: 'Inactive'
                }} 
                style={{
                    marginRight: 0,
                    marginLeft: 8 
                }} />
            )
        }
        if(grant.activation_date === null) {
            return (
                <AltBadge 
                content={{
                    color: Appearance.colors.grey(),
                    text: 'Pending'
                }} 
                style={{
                    marginRight: 0,
                    marginLeft: 8 
                }} />
            )
        }
        if(grant.expired === true) {
            return (
                <AltBadge 
                content={{
                    color: Appearance.colors.red,
                    text: 'Expired'
                }} 
                style={{
                    marginRight: 0,
                    marginLeft: 8 
                }} />
            )
        }
        if(grant.duration_remaining > 0) {
            return (
                <AltBadge 
                content={{
                    color: Appearance.colors.orange,
                    text: `${Utils.parseDuration(grant.duration_remaining, { largestUnitOnly: true })} Left`
                }} 
                style={{
                    marginRight: 0,
                    marginLeft: 8 
                }} />
            )
        }
        return (
            <AltBadge 
            content={{
                color: Appearance.colors.green,
                text: 'Active'
            }} 
            style={{
                marginRight: 0,
                marginLeft: 8 
            }} />
        );
    }

    const getApiRequestProps = () => {
        return {
            message: edits.management_enabled === true ? `LifeWipe is preparing to manage this account for you, following your instructions if an emergency or trigger occurs.` : `We’re securely storing these login details so they can be accessed later, only under the conditions you’ve approved.`,
            title: `${isNewTarget ? 'Securing' : 'Updating'} Your Locket ${edits.management_enabled === true ? 'Secured Account' : 'Password'}`,
            request: onProgressChange => {
                return new Promise(async (resolve, reject) => {
                    try {

                        // set loading flag and submit request to server
                        let result = await Request.post(utils, '/accounts/', {
                            ...requestProps.current,
                            id: abstract.getID(),
                            type: isNewTarget ? 'new' : 'update'
                        }, {
                            onProgressChange: onProgressChange
                        });

                        // prepare completion function call for component
                        resolve(() => {

                            // notify subscribers of data change
                            utils.content.fetch('accounts');

                            // update configuration flag for user
                            setUser(user => {
                                user.onboarding.has_configured_accounts = true;
                                return user;
                            });

                            // notify requiem that smart flow has been completed for this resource
                            requiem.functions.smart_flow.complete(resource);

                            // show confirmation screen for new targets
                            if(isNewTarget === true) {
                                abstract.object = Account.create(result.account);
                                setView('new_account_confirmation');
                                setLayerState(null);
                                return;
                            }

                            // show confirmation alert for existing targets
                            if(isNewTarget === false) {

                                // automatically close layer for existing targets
                                setLayerState('close');

                                // determine if auth method changed to mfa passkey during this update
                                let authMethodChanged = abstract.object.authentication_method !== edits.authentication?.method;
                                if(authMethodChanged === true && edits.authentication?.method === 'mfa_passkey') {
                                    utils.alert.show({
                                        title: 'All Done!',
                                        message: `Your account been securely updated and changes are available immediately for use. Your backup key is shown below. If you ever lose access to your passkey, this key can be used to regain control.`,
                                        content: (
                                            <span style={{
                                                ...Appearance.textStyles.title(),
                                                display: 'block',
                                                marginBottom: 24,
                                                textAlign: 'center',
                                                whiteSpace: 'normal',
                                                width: '100%'
                                            }}>{edits.authentication?.mfa_passkey}</span>
                                        )
                                    });
                                    return;
                                }

                                // fallback to standard confirmation alert content
                                utils.banner.show({
                                    title: 'Linked Account Updated',
                                    message: `Your linked account "${edits.name}" has been securely updated and changes are immediately available for use.`
                                });
                            }
                        });

                    } catch(e) {

                        // notify parent that promise failed and update view state
                        reject(e);
                        setLayerState(null);

                        // no further logic is required if user cancelled the action
                        if(e.message === 'user_cancelled') {
                            return;
                        }

                        // reset auth credential for future attempts
                        setAuthCredential(null);

                        // show error alert
                        utils.alert.error({
                            error: e,
                            message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your account. ${e.message || 'An unknown error occurred'}`,
                            origin: 'add_edit_account.on_submit_click'
                        });
                    }
                });
            }
        }
    }

    const getAuthenticationItems = () => {
        return [{
            id: 'mfa_passkey',
            title: 'Multi Factor Authentication PassKey'
        },{
            id: 'secret_phrase',
            title: 'Secret Passcode or Phrase'
        },{
            id: 'two_factor_sms',
            title: 'Two Factor Authentication SMS'
        }];
    }

    const getButtons = () => {
        if(view === 'new_account_confirmation') {
            return [{
                color: 'dark',
                id: 'done',
                onClick: () => {
                    setLayerState('close');
                    if(typeof(onAccountAdded) === 'function') {
                        onAccountAdded(abstract.object);
                    }
                },
                text: 'Done'
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitRequest,
            text: requiresRequiemReview() ? 'Approve' : (isNewTarget ? 'Submit' : 'Save Changes'),
            visible: hasMadeEdits === true || requiresRequiemReview() === true
        }];
    }

    const getContent = () => {
        if(view === 'new_account_confirmation') {

            // prepare requiem metadata object for action execution
            let metadata = { 
                account_nickname: abstract.object.name,
                resource_id: `linked_accounts.${abstract.getID()}` 
            };

            // determine if content is needed for trusted phone numbers or passkey backup codes
            let externalTwoFactorVerified = abstract.object.metadata?.external_two_factor_verified;
            let hasPasskey = abstract.object.authentication?.mfa_passkey ? true : false;

            // prepare a description that explains the account has been added and is now part of locket passwords
            let description = `LifeWipe will store and protect your ${abstract.object.provider.title} username and password using encrypted vault security, but no account access or management is enabled. This means LifeWipe cannot interact with, access, or take action on this account under any circumstance. The username and password are held safely for your reference or future use only. You can choose to enable account management later if you want LifeWipe to coordinate actions during emergencies or posthumous events.`;

            // update the description to cover management if enabled
            if(edits.management_enabled === true) {
                description =`Your ${abstract.object.provider.title} account has been successfully linked to LifeWipe. This means LifeWipe is now authorized to coordinate with ${abstract.object.provider.title} — but only under emergency or posthumous conditions you've defined. No actions will ever be taken unless your triggers are activated and verified. All access is fully logged, and you'll be notified anytime LifeWipe interacts with a linked account.`;
            }

            return (
                <>
                <LayerItem
                collapsed={false}
                lastItem={false}
                title={'Locket Secured'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText()
                        }}>
                            <b>{`🎉 Congratulations!`}</b>
                            {` ${description}`}
                        </span>
                    </div>
                </LayerItem>
                {hasPasskey === true && (
                    <LayerItem
                    collapsed={false}
                    lastItem={false}
                    title={'Multi-Factor Backup Code'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: '12px 16px 12px 16px',
                            width: '100%'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                marginBottom: 16
                            }}>{`This is your personal MFA backup code. It can be used to verify your identity if you're ever locked out or unable to receive standard authentication messages. Store this code somewhere safe and private. Anyone with access to it can request control over your account in critical situations.`}</span>
                            <div style={{
                                ...Appearance.styles.unstyledPanel(),
                                alignItems: 'center',
                                backgroundColor: Appearance.colors.divider(),
                                borderTopLeftRadius: 2,
                                borderTopRightRadius: 2,
                                borderBottomLeftRadius: 8,
                                borderBottomRightRadius: 8,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                height: 75,
                                width: '100%'
                            }}>
                                <span style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: "IBM Plex Mono",
                                    fontSize: 16,
                                    fontStyle: 'normal',
                                    fontWeight: 400
                                }}>
                                    {edits.authentication.mfa_passkey}
                                </span>
                            </div>
                        </div>
                    </LayerItem>
                )}
                {externalTwoFactorVerified === false && (
                    <LayerItem
                    collapsed={false}
                    lastItem={false}
                    title={'Trusted Phone Number'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: '12px 16px 12px 16px',
                            width: '100%'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                marginBottom: 16
                            }}>
                                {`You’ve indicated that two-factor authentication (2FA) is enabled on your linked account. Because of this, your provider requires that LifeWipe be added as a trusted phone number before we can access the account — under any circumstance. `}
                                <b>{`This step is mandatory.`}</b>
                                {` Without it, LifeWipe will not be able to authenticate, interact with, or take action on this account — even if your emergency triggers are activated`}
                            </span>
                            <Button 
                            color={'secondary'}
                            label={'Get Started'}
                            onClick={onVerifyTwoFactorPhoneNumberClick}
                            type={'large'}
                            utils={utils} />
                        </div>
                    </LayerItem>
                )}
                <ActionSteps
                exclude={['create_linked_account']}
                onClick={setLayerState.bind(this, 'close')}
                onRequiemClick={action => onRequiemActionClick(action, metadata)} 
                utils={utils} />
                </>
            )
        }
        if(loading === true) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return (
            <>
            <AltFieldMapper
            fields={getFields()}
            resourceID={resource.id}
            utils={utils} />
            {getAccessGrants()}
            {getResources()}
            <span style={{
                color: Appearance.colors.subText(),
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
                marginTop: 24,
                width: '100%'
            }}>{`By submitting your credentials, you grant LifeWipe permission to securely store this information within Locket Password Manager. Viewing or managing these credentials is entirely optional and requires your explicit opt-in. LifeWipe will only access or interact with your saved data if you enable related features, such as sharing or automated account management. This functionality is governed by our Terms of Service and applicable legal agreements. Please review them carefully before proceeding. Continued use of the service constitutes acceptance of these terms.`}</span>
            </>
        )
    }

    const getExistingPassKey = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // request web authentication for access key
                let credential = await navigator.credentials.get({
                    publicKey: {
                        challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                        rpId: window.location.hostname,
                        userVerification: 'required'
                    }
                });
                resolve(credential.id);
                
            } catch(e) {
                reject(e);
            }
        });
    }

    const getFields = () => {

        // prepare list items for components
        let authenticationItems = getAuthenticationItems();
        let provider = getProviderItems().find(provider => provider.id === edits.provider);

        // determine if sections should be auto collapsed
        let shouldAutoCollapse = shouldAutoCollapseSections();
        
        let sections = [{
            collapsed: false,
            description: 'Locket lets you securely store login details for platforms you already use — like Google, Apple, and more. Each saved account includes the provider and a personal nickname, making it easy to manage, update, or share credentials when needed.',
            key: 'details',
            lastItem: false,
            title: 'Account Details',
            items: [{
                component: 'list',
                description: 'The provider for this external account. This is typically the service or platform that hosts the account, such as a social media site, email provider, or cloud storage service.',
                key: 'provider',
                items: getProviderItems(),
                onChange: item => {

                    // define category for the selected item
                    let category = categories.find(category => {
                        let match = category.providers.find(provider => provider.id === item?.id);
                        return match ? true : false;
                    });

                    // update target with provider id, human readable name, and category id
                    onUpdateTarget({ 
                        category: category?.id,
                        name: item?.title || edits.name,
                        provider: item?.id 
                    });
                },
                requiemSmartFlow: true,
                title: 'Provider',
                value: getProviderItems().find(item => item.id === edits.provider)?.title
            },{
                component: 'textfield',
                description: 'A nickname for this account to help you easily identify it later.',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                requiemSmartFlow: true,
                title: 'Account Nickname',
                value: edits.name
            }]
        },{
            authentication: {
                completed: canEditSensitiveValues,
                onAsyncClick: onRequestAuthValidation, 
                required: isNewTarget ? false : true
            },
            collapsed: shouldAutoCollapse,
            description: `This section contains the login information you’ll save in Locket for this account. Your credentials are securely encrypted and never accessed or shared by LifeWipe unless you explicitly choose to do so.`,
            key: 'credentials',
            lastItem: false,
            title: 'Access',
            visible: edits.provider ? true : false,
            items: [{
                component: 'textfield',
                description: 'The username used to access this account.',
                key: 'username',
                onChange: text => {
                    onUpdateTarget({ 
                        credentials: update(edits.credentials || {}, {
                            username: {
                                $set: text
                            }
                        })
                    });
                },
                props: {
                    disabled: canEditSensitiveValues === false,
                    isSecure: canEditSensitiveValues === false
                },
                requiemSmartFlow: true, 
                title: 'Username',
                value: canEditSensitiveValues ? edits.credentials?.username : '******************'
            },{
                component: 'textfield',
                description: 'The password used to access this account.',
                key: 'password',
                onChange: text => {
                    onUpdateTarget({ 
                        credentials: update(edits.credentials || {}, {
                            password: {
                                $set: text
                            }
                        })
                    });
                },
                props: {
                    disabled: canEditSensitiveValues === false,
                    isSecure: true
                },
                requiemSmartFlow: true, 
                title: 'Password',
                value: canEditSensitiveValues ? edits.credentials?.password : '******************'
            },{
                component: 'textview',
                key: 'notes',
                onChange: text => {
                    onUpdateTarget({ 
                        config: update(edits.config || {}, {
                            notes: {
                                $set: text?.length > 0 ? text : null
                            }
                        })
                    });
                },
                required: false,
                title: 'Notes',
                value: edits.config?.notes
            }]
        },{
            authentication: {
                completed: canEditSensitiveValues,
                onAsyncClick: onRequestAuthValidation, 
                required: isNewTarget ? false : true
            },
            collapsed: shouldAutoCollapse,
            description: 'While your LifeWipe account is already protected with multiple layers of security, this section lets you add an extra safeguard for the login details stored in Locket — like putting a second lock on your password vault. Choose the protection method that fits your level of control and peace of mind.',
            key: 'security',
            lastItem: false,
            title: 'Security',
            visible: edits.provider ? true : false,
            items: [{
                component: 'list',
                description: 'This is how you will verify that it is really you when accessing this account through LifeWipe. Choose the authentication method that works best for you and provides the right level of security for this account. This authentication method is optional but recommended.',
                items: authenticationItems,
                key: 'authentication_method',
                onChange: item => {
                    onUpdateTarget({ 
                        authentication: update(edits.authentication || {}, {
                            $unset: ['mfa_passkey', 'secret_phrase_value', 'two_factor_sms_phone_number'],
                            method: {
                                $set: item?.id
                            },
                            ...item?.id === 'two_factor_sms' && {
                                two_factor_sms_phone_number: {
                                    $set: user.phone_number
                                }
                            }
                        })
                    });
                },
                requiemSmartFlow: true,
                required: false,
                title: 'Authentication Method',
                value: authenticationItems.find(item => item.id === edits.authentication?.method)?.title
            },{
                component: 'textfield',
                description: 'This is the PassKey you will use to verify your identity when accessing this account through LifeWipe.',
                key: 'mfa_passkey',
                onClick: onRequestKeyClick,
                props: {
                    canShowSensitiveContent: false,
                    isSecure: true,
                    placeholder: 'Click to configure a PassKey'
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Multi Factor Authentication PassKey',
                value: edits.authentication?.mfa_passkey,
                visible: edits.authentication?.method === 'mfa_passkey'
            },{
                component: 'textfield',
                description: 'This is the secret passcode or phrase you will use to verify your identity when accessing this account through LifeWipe.',
                key: 'secret_phrase_value',
                onChange: text => {
                    onUpdateTarget({ 
                        authentication: update(edits.authentication || {}, {
                            secret_phrase_value: {
                                $set: text
                            }
                        })
                    });
                },
                props: { 
                    isSecure: true
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Secret Passcode or Phrase',
                value: edits.authentication?.secret_phrase_value,
                visible: edits.authentication?.method === 'secret_phrase'
            },{
                component: 'textfield',
                description: 'This is the phone number where you will receive your two factor authentication codes when accessing this account through LifeWipe.',
                key: 'two_factor_sms_phone_number',
                onChange: text => {
                    onUpdateTarget({ 
                        authentication: update(edits.authentication || {}, {
                            two_factor_sms_phone_number: {
                                $set: text
                            }
                        })
                    });
                },
                props: {
                    format: 'phone_number'
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Two Factor Authentication SMS Phone Number',
                value: edits.authentication?.two_factor_sms_phone_number,
                visible: edits.authentication?.method === 'two_factor_sms'
            }]
        }];

        // add components to use tokenized flow for account access if applicable
        if(provider?.supports_tokenized_flow === true) {
            sections.push({
                authentication: {
                    completed: canEditSensitiveValues,
                    onAsyncClick: onRequestAuthValidation, 
                    required: isNewTarget ? false : true
                },
                collapsed: shouldAutoCollapse,
                description: `This section allows you to securely link your account using a flow offered by ${provider.title}. When a trigger is activated, LifeWipe will use this tokenized access method to interact with your account without needing to store your username and password. Nothing is shared or accessed until your rules say so.`,
                key: 'credentials',
                lastItem: false,
                title: 'Management',
                items: [{
                    component: 'textfield',
                    description: `This configures access to your account using ${provider.title}'s secure tokenized access flow. Click "Authenticate" to begin the process of linking your account through ${provider.title}.`,
                    key: 'token',
                    onClick: onTokenizedFieldClick.bind(this, provider.id),
                    props: {
                        placeholder: 'Click to authenticate...'
                    },
                    title: 'Authenticate',
                    value: edits.token ? 'Configured' : null
                }]
            });
        }

        // add fields for manually providing username and password if account does not support tokenized flows
        if(provider?.supports_tokenized_flow === false) {
            sections.push({
                authentication: {
                    completed: canEditSensitiveValues,
                    onAsyncClick: onRequestAuthValidation, 
                    required: isNewTarget ? false : true
                },
                collapsed: shouldAutoCollapse,
                description: `LifeWipe can manage this account on your behalf when a trigger is activated — such as logging in, updating settings, or securely closing it. You'll need to indicate whether two-factor or multi-factor authentication is enabled, so we can better prepare for secure access when needed.`,
                key: 'credentials',
                lastItem: false,
                title: 'Management',
                visible: edits.provider ? true : false,
                items: [{
                    component: 'bool_list',
                    description: `Whether LifeWipe is authorized to manage this account on your behalf when a trigger is activated. This may include logging in, updating settings, or securely closing the account as per your instructions.`,
                    key: 'management_enabled',
                    onChange: val => {
                        onUpdateTarget({
                            config: update(edits.config || {}, {
                                $unset: ['mfa_required', 'two_factor_required'], 
                            }),
                            management_enabled: val
                        });
                    },
                    required: false,
                    requiemSmartFlow: true,
                    title: 'Manage This Account',
                    value: edits.management_enabled || false
                },{
                    component: 'bool_list',
                    description: `Whether two factor authentication is required to access this account. If so, the ${Utils.format.phoneNumber(twoFactorPhoneNumber)} lifewipe phone number must be added as a verified phone number in order to receive two factor verification codes.`,
                    key: 'two_factor_required',
                    onChange: val => {
                        onUpdateTarget({ 
                            config: update(edits.config || {}, {
                                two_factor_required: {
                                    $set: val
                                },
                                two_factor_phone_number: {
                                    $set: null
                                }
                            })
                        });
                    },
                    props: {
                        loading: loading === 'authentication_resources'
                    },
                    requiemSmartFlow: true,
                    title: 'Is Two Factor Authentication Enabled?',
                    visible: edits.management_enabled === true,
                    value: edits.config?.two_factor_required
                },{
                    component: 'bool_list',
                    description: 'Whether multi factor authentication is required to access this account. if so, a backup key must be provided or multi factor authentication must be disabled.',
                    key: 'mfa_required',
                    onChange: val => {
                        onUpdateTarget({ 
                            config: update(edits.config || {}, {
                                mfa_required: {
                                    $set: val
                                }
                            })
                        });
                    },
                    requiemSmartFlow: true,
                    title: 'Is Multi Factor Authentication Enabled?',
                    visible: edits.management_enabled === true,
                    value: edits.config?.mfa_required
                },{
                    component: 'textfield',
                    description: 'If multi factor authentication is enabled, provide the backup key here.',
                    key: 'mfa_key',
                    onChange: val => {
                        onUpdateTarget({ 
                            config: update(edits.config || {}, {
                                mfa_key: {
                                    $set: val?.length > 0 ? val : null
                                }
                            })
                        });
                    },
                    props: {
                        isSecure: true
                    },
                    requiemSmartFlow: true,
                    required: edits.config?.mfa_required || false,
                    title: 'Multi Factor Authentication Backup Key',
                    value: edits.config?.mfa_key,
                    visible: edits.config?.mfa_required || false
                }]
            });
        }

        return sections;
    }

    

    const getNewPassKey = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // throw an error if an account name has not been configured
                if(!edits.name) {
                    throw new Error('Please provide a name for your account to continue');
                }

                // generate a single use token for credential
                let key = Utils.randomString();

                // request credential creation from authentication api
                let credential = await navigator.credentials.create({
                    publicKey: {
                        challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                        rp: { id: window.location.hostname, name: "LifeWipe Inc" },
                        user: {
                            displayName: user.full_name,
                            id: Uint8Array.from(key, c => c.charCodeAt(0)),
                            name: edits.name
                        },
                        pubKeyCredParams: [{ 
                            alg: -7,
                            type: 'public-key'
                        }]
                    }
                });
                resolve(credential.id);
                
            } catch(e) {
                reject(e);
            }
        });
    }

    const getProviderItems = () => {
        return categories.reduce((array, category) => {
            return array.concat(category.providers || []);
        }, []).sort((a,b) => {
            if(a.title === 'Other' || b.title === 'Other') {
                return 1;
            }
            return a.title.localeCompare(b.title);
        });
    }

    const getRequiemReviewBanner = () => {
        return requiresRequiemReview() === true && {
            description: `Your account was linked with help from Requiem, your digital assistant. Before we work with this account, we ask that you review and confirm the setup below. Click here to approve the account.`,
            onClick: onSubmitRequest
        }
    }

    const getResources = () => {

        if(isNewTarget === true) {
            return null;
        }

        let shouldAutoCollapse = shouldAutoCollapseSections();
        if(canEditSensitiveValues === false) {
            return (
                <LayerItem 
                collapsed={shouldAutoCollapse}
                title={'Resources'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        <AsyncInteractionItem 
                        description={'This area is locked behind additional authentication. Click here to authenticate.'}
                        image={'images/prohibitory-red-small.png'}
                        lastItem={true}
                        onAsyncClick={onRequestAuthValidation}
                        title={'Authentication Required'}
                        utils={utils} />
                    </div>
                </LayerItem>
            )
        }
        return (
            <LayerItem 
            collapsed={shouldAutoCollapse}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <SystemEventsAsyncInteractionItem 
                    abstract={abstract} 
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'This will unlink the account from your profile and prevent LifeWipe from managing the account.'}
                    image={'images/delete-red-small.png'}
                    lastItem={true}
                    onAsyncClick={onDeleteAccount}
                    title={'Delete Account'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getTrustedPhoneNumberVerificationBanner = () => {

        // determine if two factor verified phone number needs to be referenced
        let twoFactorVerified = abstract.object.management_enabled ? abstract.object.metadata?.external_two_factor_verified : null;

        return twoFactorVerified === false && {
            description: `To activate full managed protection, you must add the LifeWipe phone number as a trusted two-factor authentication number in your ${abstract.object.provider.title} settings. Click to get started.`,
            onClick: onVerifyTwoFactorPhoneNumberClick
        }
    }

    const getViewOptions = () => {
        return view === null && {
            support: {
                description: 'Watch our quick tutorial video or activate Requiem AI for real-time guidance as you add a new account to Locket. Secure linking ensures your credentials are safely stored, encrypted, and easy to manage whenever you need them.',
                requiem: {
                    enabled: true,
                    smart_flow: {
                        items: getFields(),
                        ...resource
                    } 
                },
                title: abstract.object.management_enabled ? 'Locket Managed Accounts' : 'Locket Passwords',
                videoUrl: 'https://www.youtube.com/embed/VM7kd3ENOl4?si=maET1POJH4zAyi9V'
            },
            warnings: {
                trusted_phone_number_verification: getTrustedPhoneNumberVerificationBanner(),
                requiem_review_required: getRequiemReviewBanner()
            }
        }
    }

    const requestAuthenticationValue = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // no additional logic is required if no authentication is set
                if(!abstract.object.authentication_method) {
                    resolve(null);
                    return;
                }

                // return existing auth credential if already set
                if(authCredential) {
                    resolve(authCredential);
                    return;
                }

                // request authentication credential from processor
                let result = await getAuthCredential({ abstract, utils });
                if(result?.requires_validation === false) {
                    setAuthCredential(result);
                }
                resolve(result);

            } catch(e) {
                if(e.message.includes('user denied') === true) {
                    let error = new Error('user_cancelled');
                    reject(error);
                    return;
                }
                reject(e);
            }
        });
    }

    const requiresRequiemReview = () => {
        return edits.metadata?.requiem_generated === true && edits.metadata?.requiem_confirmation_date === null ? true : false;
    }

    const setupEditing = async () => {
        try {

            // setup editing for target and determine review state
            let edits = { ...abstract.object };
            let requiresRequiemReview = edits.metadata?.requiem_generated === true && edits.metadata?.requiem_confirmation_date === null ? true : false;

            // determine if an authentication object needs to be set for review targets or new targets
            if(requiresRequiemReview === true || isNewTarget === true) {
                edits.authentication = {
                    method: edits.authentication_method,
                    ...edits.authentication_method === 'two_factor_sms' && {
                        two_factor_sms_phone_number:  user.phone_number
                    }
                }
            }

            // set empty config object if none exists
            if(!edits.config) {
                edits.config = {};
            }

            // fetch sensitive data for account if no authentication is enabled
            if(!abstract.object.authentication_method && isNewTarget === false) {

                // submit authentication value to server for validation
                let { authentication, config, credentials } = await Request.get(utils, '/accounts/', {
                    id: abstract.getID(),
                    type: 'get_authentication_credentials'
                });

                // end loading and set sensitive values in local state
                edits = { ...edits, authentication, config, credentials };
            }

            // update local state with edits
            setEdits({ 
                ...edits,
                category: abstract.object.category?.id,
                provider: abstract.object.provider?.id 
            });

            // determine if user can edit credentials without re-authentication
            setCanEditSensitiveValues(isNewTarget === true || requiresRequiemReview === true || !abstract.object.authentication_method ? true : false);

            // fetch editing resources for target
            fetchResources();

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the list of account categories. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'add_edit_account.fetch_resources'
            });
        }
    }

    const shouldAutoCollapseSections = () => {
        return isNewTarget === true || requiresRequiemReview() === true ? false : true;
    }

    const fetchResources = async () => {
        try {
            
            setLoading(true);
            let { categories, access_grants, two_factor_phone_number } = await Request.get(utils, '/accounts/', {
                account_id: abstract.getID(),
                type: 'resources'
            });

            // end loading and update local state
            setLoading(false);
            setCategories(categories);
            setTwoFactorPhoneNumber(abstract.object.config?.two_factor_phone_number || two_factor_phone_number);

            // format and set access grants
            setAccessGrants(access_grants.map(grant => ({
                ...grant,
                activation_date: grant.activation_date && moment.utc(grant.activation_date).local(),
                date: moment.utc(grant.date).local(),
                expiration_date: grant.expiration_date && moment.utc(grant.expiration_date).local(),
                start_date: grant.start_date && moment.utc(grant.start_date).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the list of account categories. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'add_edit_account.fetch_resources'
            });
        }    
    }

    useEffect(() => {
        layerState === 'close' && requiem.functions.smart_flow.cancel(resource);
    }, [layerState]);

    useEffect(() => {
        if(view === 'new_account_confirmation') {
            layerRef.current?.scrollToTop();
        }
    }, [view]);

    useEffect(() => {
        setupEditing();
        utils.content.subscribe(resource.id, ['access_grants', 'accounts'], {
            onFetch: fetchResources
        });
        return () => {
            utils.content.unsubscribe(resource.id);
        }
    }, []);

    return (
        <Layer
        ref={layerRef}
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            apiRequest: getApiRequestProps(),
            layerState: layerState,
            sizing: 'medium',
            ...getViewOptions()
        }}>
            {getContent()}
        </Layer>
    )
}

export const AddEditInstruction = ({ configuring = false, isNewTarget, isTemplate = false, onChange }, { abstract, index, options, utils }) => {
    
    const resource = {
        category: 'trigger_instructions',
        id: isNewTarget ? 'new_trigger_instruction' : `trigger_instructions.${abstract.getID()}`,
        title: isNewTarget ? 'Create an Instruction' : 'Update Your Instruction'
    };

    const legalAgreementsRef = useRef(null);
    const requestProps = useRef({});

    const [category, setCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [edits, setEdits] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(isTemplate);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState('submit');
    const { requiem, user, setUser } = useDataStore();
    const [view, setView] = useState('category_selection');

    const onActiveStatus = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        id: abstract.getID(),
                        status: !abstract.object.active,
                        type: 'set_instruction_active_status'
                    });

                    // update target and notify subscribers of data change, fetch is required to refresh in-use flags
                    abstract.object.active = !abstract.object.active;
                    utils.content.fetch('trigger_instructions');

                    // end loading and notify origin that promise has been resolved
                    resolve({ message: `Your instruction has been ${abstract.object.active ? 'activated' : 'deactivated'}.` });
        
                } catch(e) {
                    let error = new Error(`There was an issue changing your instruction's active status. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let status = abstract.object.active;
            utils.alert.show({
                title: `${status ? 'Deactivate' : 'Activate'} Device`,
                message: `Are you sure that you want to ${status ? 'deactivate' : 'activate'} this device?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: status ? 'destructive' : 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: status ? 'default' : 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onBackClick = () => {

        // no additional logic is required if no edits have been made (other than category)
        if(hasMadeEdits === false) {
            setView('category_selection');
            setEdits({});
            return;
        }

        // request confirmation to go back and start over
        utils.alert.show({
            title: 'Just a Moment',
            message: `Are you sure you want to go back to the beginning? Any changes you've made so far will be lost.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setEdits({});
                    setHasMadeEdits(false);
                    setView('category_selection');
                    return;
                }
            }
        });
    }

    const onCloseClick = () => {
        
        // no further logic is required if no edits have been made
        if(hasMadeEdits === false) {
            onCloseLayer();
            return;
        }

        // show alert requesting confirmation to close the layer without saving changes
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitRequest, 500);
                    return;
                }

                // close layer and cancel requiem form
                setLayerState('close');
                requiem.functions.smart_flow.cancel(resource);
            }
        });
    }

    const onCloseLayer = () => {
        setLayerState('close');
        requiem.functions.smart_flow.cancel(resource);
    }

    const onComponentChange = (props, value) => {

        // prepare result object for target updater
        let result = {
            ...edits,
            preferences: edits.preferences || {}
        };

        // reset actions if the account, blueprint, or device have been changed
        if(props.key === 'target_id') {
            result.preferences.actions = [];
        }

        // reset data points if the system trigger has been changed
        if(props.key === 'event') {
            result.preferences = {};
        }

        // determine how to add value using the component key
        switch(props.component.key) {
            
            case 'instructions_list':
            case 'multiple_list':
            case 'trusted_contacts_list':
            dot.setProperty(result.preferences, props.key, value.length > 0 ? value.map(item => item.id) : null);
            break;

            case 'date_picker':
            dot.setProperty(result.preferences, props.key, value?.format('YYYY-MM-DD') || null);
            break;

            case 'list':
            dot.setProperty(result.preferences, props.key, value?.id || null);
            break;

            case 'time_picker':
            dot.setProperty(result.preferences, props.key, value?.utc().format('HH:mm') || null);
            break;

            default:
            dot.setProperty(result.preferences, props.key, value);
        }

        // determine if a name should be auto generated using the target_id and category
        if(result.category && result.preferences.target_id) {
            let [selector] = getTargetSelector();
            let target = selector?.items.find(item => item.id == result.preferences.target_id);
            result.name = edits.name || (target && selector ? `${(target.name || target.title)} ${selector.title}` : null);
        }

        // determine if a name should be auto generated using the event and category
        if(result.category && result.preferences.event) {
            let [selector] = getTargetSelector();
            let target = selector?.items.find(item => item.id == result.preferences.event);
            result.name = edits.name || (target && selector ? `${(target.name || target.title)} Trigger` : null);
        }

        // submit result to target updater
        onUpdateTarget(result);
    }

    const onConfirmCategoryClick = () => {

        // show an alert if an unsupported category was selected
        if(category.id === 2) {
            utils.alert.show({
                title: 'Coming Soon',
                message: 'Trigger Instructions for devices are not supported at this time. Please select a different category to continue.'
            });
            return;
        }

        // set configuration view and add category to edits object
        setView('configuration');
        setEdits(edits => {
            return update(edits, {
                category: {
                    $set: category.id
                }
            });
        });
    }

    const onDeleteInstruction = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        id: abstract.getID(),
                        type: 'delete_instruction'
                    });

                    // notify subscribers of data change
                    utils.content.fetch('trigger_instructions');

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        banner: true,
                        message: `The '${abstract.object.name}' instruction has been removed from your account.`,
                        title: 'Instruction Deleted',
                        onShow: setLayerState.bind(this, 'close') 
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue deleting your instruction. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Delete Instruction',
                message: `Are you sure that you want to delete this instruction?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onRequestTargetChange = () => {
        return new Promise((resolve, reject) => {

            // no further logic is required if no actions have been configured
            if(!edits.preferences?.actions || edits.preferences?.actions.length === 0) {
                resolve();
                return;
            }

            // request confirmation to change account value
            let category = getCategory();
            let singular = category?.title.substring(0, category.length - 1);
            utils.alert.show({
                title: `Change ${category.title}`,
                message: `Changing may impact what actions are available for this instruction. Any configured actions tied to the current selection will be lost and cannot be recovered. If you're looking to work with a different ${singular} while preserving your current setup, we recommend creating a new instruction instead. Do you want to continue and change the ${singular}?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        resolve();
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitRequest = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // verify that all legal agreements have been reviewed and completed
            let legalAgreements = await legalAgreementsRef.current?.getCompletedAgreements() || {};

            // return configuration to parent if configuring flag is true
            if(configuring === true) {
                setLayerState('close');
                if(typeof(onChange) === 'function') {
                    onChange(edits);
                }
                return;
            }
            
            // set request props and update view state
            requestProps.current = { legal_agreements: legalAgreements };
            setLayerState('submit_api_request');

        } catch(e) {
            utils.alert.show({
                message: e.message || 'An unknown error occurred',
                title: 'Oops!'
            });
        }
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(({ ...edits, ...props }));
    }

    const getActionsForCategory = items => {

        return items.reduce((array, action) => {

            // determine if action has been selected by the user
            if(edits.preferences?.actions?.includes(action.id) === true) {
                array.push({
                    content: (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%'
                        }}>
                            {action.is_destructive === true && (
                                <div style={{
                                    backgroundColor: Appearance.colors.red,
                                    borderRadius: 8,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: 24,
                                    padding: '12px 16px 12px 16px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        color: 'white'
                                    }}>{`When triggered, this action cannot be undone — even by you. Please review your settings carefully. This action will: ${action.description}`}</span>
                                </div>
                            )}
                            {action.is_destructive === false && (
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    display: 'block',
                                    marginBottom: 24
                                }}>{action.description}</span>
                            )}
                        </div>
                    ),
                    key: action.id,
                    lastItem: false,
                    title: action.title,
                    items: action.data_points.map(item => formatComponent(item))
                });
            }
            return array;
        }, []);
    }

    const getApiRequestProps = () => {
        return {
            message: `These directions tell us exactly what to do in a crisis or after death — and we’re making sure they’re safely recorded.`,
            title: `${isNewTarget ? 'Saving' : 'Updating'} Your Instructions`,
            request: onProgressChange => {
                return new Promise(async (resolve, reject) => {
                    try {

                        // prepare request props for request
                        let { legal_agreements } = requestProps.current;

                        // submit request using the progress change callback
                        let { instruction } = await Request.post(utils, '/users/', {
                            ...edits,
                            legal_agreements,
                            id: abstract.getID(),
                            type: isNewTarget ? 'new_trigger_instruction' : 'update_instruction'
                        }, {
                            onProgressChange: onProgressChange
                        });

                        // prepare completion function call for component
                        resolve(() => {

                            // update user object with agreements data
                            setUser(user => {
                                Object.entries(legal_agreements || {}).forEach(([document_id, agreement]) => {
                                    user.legal_agreements[document_id] = agreement;
                                });
                                return user;
                            });

                            // notify requiem that a resource interaction has been completed
                            requiem.functions.smart_flow.complete(resource);

                            // determine if target is a new target
                            if(isNewTarget === true) {

                                // notify subscribers that new data is available
                                utils.content.fetch('trigger_instructions');
                                
                                // update view state to confirmation and reset layer state
                                setView('new_trigger_instruction_confirmation');
                                setLayerState(null);
                                return;
                            }

                            // automatically close layer for existing targets
                            setLayerState('close');

                            // update target and notify subscribers of data change
                            abstract.object = Instruction.create(instruction);
                            utils.content.update(abstract);

                            // show confirmation alert for requiem review if applicable
                            if(requiresRequiemReview() === true) {
                                utils.banner.show({
                                    title: 'Instruction Approved',
                                    message: `Your system instruction has been approved and is ready to use. You can review or edit this instruction at any time by clicking on it in your instruction list.`,
                                    onShow: () => {
                                        if(typeof(onChange) === 'function') {
                                            let target = Instruction.create(instruction);
                                            onChange(target);
                                        }
                                    }
                                });
                                return;
                            }


                            // fallback to standard confirmation alert
                            utils.banner.show({
                                title: 'Instruction Updated',
                                message: `Your system instruction has been successfully updated and changes are available to use immediately. You can review or edit this instruction at any time by clicking on it in your instruction list.`,
                                onShow: () => {
                                    if(typeof(onChange) === 'function') {
                                        let target = Instruction.create(instruction);
                                        onChange(target);
                                    }
                                }
                            });
                        });

                    } catch(e) {

                        // notify parent that promise failed and update view state
                        reject(e);
                        setView('configuration');

                        // determine if instruction rejection processing is required
                        if(e.code === Request.status.get().instruction_update_rejected) {
                            return processInstructionRejection({ 
                                abstract,
                                error: e,
                                utils 
                            });
                        }

                        // fallback to general errors
                        utils.alert.error({
                            error: e,
                            message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your instruction. ${e.message || 'An unknown error occurred'}`,
                            origin: 'add_edit_instruction.on_submit_click'
                        });
                    }
                });
            }
        }
    }

    const getButtons = () => {
        if(view === 'new_trigger_instruction_confirmation') {
            return [{
                color: 'dark',
                id: 'close',
                onClick: onCloseLayer,
                text: 'Close'
            }];
        }
        if(view === 'category_selection') {
            return [{
                color: 'dark',
                id: 'close',
                onClick: onCloseClick,
                text: 'Close'
            },{
                color: 'primary',
                id: 'continue',
                onClick: onConfirmCategoryClick,
                text: 'Continue',
                visible: category ? true : false
            }];
        }
        return [{
            color: 'dark',
            id: isNewTarget ? 'back' : 'close',
            onClick: isNewTarget ? onBackClick : onCloseClick,
            text: isNewTarget ? 'Back' : 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitRequest,
            text: requiresRequiemReview() ? 'Approve' : 'Submit',
            visible: hasMadeEdits === true || requiresRequiemReview() === true
        }];
    }
    
    const getCategory = () => {
        return categories.find(category => category.id === edits.category);
    }

    const getCategorySelector = () => {
        return view === 'category_selection' && (
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Trigger Instructions'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`Pick the category that best matches what you’re planning for. This helps keep the setup simple and focused, and you’ll be guided through the rest step by step.`}</span>
                    {categories.map((cat, index) => {
                        return (
                            <AsyncInteractionItem
                            description={cat.description}
                            image={`images/${cat.image}`}
                            imageStyle={{
                                backgroundColor: category?.id === cat.id ? Appearance.colors.primary() : Appearance.colors.darkGrey
                            }}
                            key={index}
                            lastItem={index === categories.length - 1}
                            onClick={setCategory.bind(this, cat)}
                            title={cat.title}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(view === 'new_trigger_instruction_confirmation') {
            return (
                <>
                <LayerItem
                collapsed={false}
                lastItem={false}
                title={'Trigger Instruction Created'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText()
                        }}>
                            <b>{`🎉 Congratulations!`}</b>
                            {` Your trigger instruction has been successfully created and stored securely. It will only be executed per your wishes, and even then, safeguards and verification apply. You stay in control and can update this instruction whenever your needs change.`}
                        </span>
                    </div>
                </LayerItem>
                <ActionSteps
                exclude={['create_trigger_instruction']}
                onClick={setLayerState.bind(this, 'close')}
                utils={utils} />
                </>
            )
        }
        return (
            <>
            {getCategorySelector()}
            <AltFieldMapper
            fields={getFields()}
            resourceID={resource.id}
            utils={utils} />
            {getLegalAgreements()}
            {getResources()}
            </>
        )
    }

    const getFields = () => {

        // declare target selector for category
        let category = getCategory();
        let [selector] = getTargetSelector();

        // prepare base entries for items
        let sections = [{
            collapsed: false,
            description: `Let’s make this easy to recognize. Pick the specific ${selector?.title} this instruction applies to, and give it a simple name so it’s easy to find later. Nothing is locked in here—you can always update these details.`,
            key: 'configuration',
            lastItem: isNewTarget === false || edits.preferences?.actions?.length > 0 ? false : true,
            title: 'Configuration',
            visible: edits.category ? true : false,
            items: [{
                component: 'textfield',
                description: 'A nickname for your instruction to help you identify it later',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                requiemSmartFlow: true,
                title: 'Instruction Nickname',
                value: edits.name
            }]
        }];

        // determine if a target selector is applicable for the selected category (accounts, devices, system triggers)
        if(selector) {

            // add target selector components to list of components in section 1
            let component = formatComponent(selector, { onAsyncChange: onRequestTargetChange });
            sections[0].items.unshift(component);

            // set title for section
            sections[0].title = `${selector.title} Details`;

            // determine which additional fields are needed using the selected category and target
            selector.items.forEach(target => {

                // determine if system trigger fields are required
                if(target.id === edits.preferences?.event) {
                    sections.push({
                        id: target.id,
                        lastItem: false,
                        title: target.title,
                        items: target.data_points.map(formatComponent)
                    });
                }

                // determine if account or blueprint specific fields are required
                if(target.id === edits.preferences?.target_id) {

                    // add list of supported actions for user selection
                    sections[0].items.push({
                        component: 'multiple_list',
                        description: `This is a list of available actions that can be performed for ${category.name}: ${target.name}.`,
                        key: 'actions',
                        items: target.supported_actions.map(action => ({
                            id: action.id,
                            title: action.title
                        })),
                        onChange: items => {
                            onUpdateTarget({
                                preferences: update(edits.preferences || {}, {
                                    actions: {
                                        $set: items.length > 0 ? items.map(item => item.id) : null
                                    }
                                })
                            });
                        },
                        title: 'Actions',
                        requiemSmartFlow: true,
                        value: target.supported_actions.filter(action => edits.preferences?.actions?.includes(action.id))
                    });

                    // add all configurable sections that match the actions selection
                    sections = sections.concat(getActionsForCategory(target.supported_actions));
                }
            });
        }

        return sections;
    }

    const getLegalAgreements = () => {
        return edits.preferences?.actions?.length > 0 && (
            <LegalAgreements
            documentIDs={['digital_estate_consent_and_device_control']}
            editable={user.legal_agreements?.digital_estate_consent_and_device_control ? false : true}
            lastItem={isNewTarget === true}
            showDescription={true}
            ref={legalAgreementsRef}
            utils={utils} 
            value={user.legal_agreements} />
        )
    }

    const getRequiemReviewBanner = () => {
        return requiresRequiemReview() === true && {
            description: `Your instruction was setup with help from Requiem, your digital assistant. Before it can be triggered, we ask that you review and confirm the setup below. Click here to approve the instruction.`,
            onClick: onSubmitRequest
        }
    }

    const getResources = () => {
        return isNewTarget === false && configuring === false && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <SystemEventsAsyncInteractionItem 
                    abstract={abstract} 
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={abstract.object.active ? 'This will temporarily prevent this instruction from executing until it has been reactivated.' : 'This will allow this instruction to execute when triggered until it has been deactivated or deleted.'}
                    image={abstract.object.active ? 'images/deactivate-red.png' : 'images/activate-green.png'}
                    onAsyncClick={onActiveStatus}
                    title={`${abstract.object.active ? 'Deactivate' : 'Activate'} Instruction`}
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'This will remove the instruction from your account and any configurations where it is referenced.'}
                    image={'images/delete-red-small.png'}
                    lastItem={true}
                    onAsyncClick={onDeleteInstruction}
                    title={'Delete Instruction'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getTargetSelector = () => {
        let category = getCategory();
        return [category?.target_selector, category];
    }

    const getViewOptions = () => {

        if(['category_selection','new_trigger_instruction_confirmation'].includes(view) === true) {
            return null;
        }
        return {
            support: {
                description: `Need help? Activate Requiem AI for real-time guidance as you ${isNewTarget ? "create a new trigger instruction" : "update your trigger instruction"}. You can customize each action’s settings, choose who receives access or notifications, and define exactly what happens behind the scenes.`,
                requiem: {
                    enabled: true,
                    smart_flow: {
                        items: getFields(),
                        ...resource
                    } 
                },
                title: 'Trigger Instructions'
            },
            warnings: {
                requiem_review_required: getRequiemReviewBanner()
            }
        }
    }

    const fetchResources = async () => {
        try {
            setLoading(true);
            let { categories } = await Request.get(utils, '/users/', {
                type: 'trigger_instruction_resources'
            });

            setLoading(false);
            setCategories(categories);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of accounts. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_instruction.fetch_resources'
            });
        }
    }

    const formatComponent = (props, options) => {

        // declare optional selector
        let [selector] = getTargetSelector();

        // get nested value and declare for component config
        let value = dot.getProperty(edits.preferences || {}, props.key);

        // prepare default payload for component
        let payload = {
            ...props,
            component: props.component.key,
            requiemSmartFlow: true,
            onChange: onComponentChange.bind(this, props),
            value: value ?? props.default_value 
        }

        // add options object to payload props if applicable
        payload.props = { 
            ...payload.props, 
            ...options
        };

        // determine if a specialty value is needed based on the component type
        switch(props.component.key) {
            case 'address_lookup':
            payload.props = {
                ...payload.props,
                shouldShowAddLocation: false,
                shouldShowCustomFields: false
            }
            break;

            case 'legacy_message_editor':
            payload.props = { 
                ...payload.props, 
                config: {
                    ...props,
                    provider: selector?.items?.find(item => item.id === edits.preferences?.[selector.key])?.provider?.id
                } 
            };
            break;

            case 'instructions_list':
            payload.props = {
                ...payload.props,
                excludeIDs: [abstract.getID() ?? 0],
                excludeCategories: [3]
            }
            break;

            case 'list':
            let item = props.items?.find(item => item.id === value);
            payload = {
                ...payload,
                items: props.items?.map(item => ({ ...item, title: item.title || item.name })),
                value: item?.title || item?.name
            };
            break;

            case 'location_radius_lookup':
            payload.props = { 
                ...payload.props,
                insideText: 'When INSIDE the circle',
                outsideText: 'When OUTSIDE the circle'
            };
            break;

            case 'scope_editor':
            payload.props = { ...payload.props, config: props };
            break;

            case 'time_picker':
            payload.value = value ? moment.utc(value, 'HH:mm').local().format('HH:mm') : null;
            break;

        }
        return payload;
    }

    const setupEditing = async () => {

        // set view state for existing targets
        if(isNewTarget === false) {
            setView('configuration');
        }
        
        // set general editing state using abstract object
        setEdits({ 
            category: abstract.object.category,
            metadata: abstract.object.metadata || {},
            name: abstract.object.name,
            preferences: JSON.parse(JSON.stringify(abstract.object.preferences))
        });
    }

    const requiresRequiemReview = () => {
        return edits.metadata?.requiem_generated === true && edits.metadata?.requiem_confirmation_date === null ? true : false;
    }

    useEffect(() => {
        setupEditing();
        fetchResources();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            apiRequest: getApiRequestProps(),
            layerState: layerState,
            ...getViewOptions()
        }}>
            {getContent()}
        </Layer>
    )
}

export const AddEditTrustedContact = ({ isNewTarget, onChange }, { abstract, index, options, utils }) => {

    const resource = {
        category: 'trusted_contacts',
        id: isNewTarget ? 'new_trusted_contact' : `trusted_contacts.${abstract.getID()}`,
        target: abstract.object,
        title: isNewTarget ? 'Add a Trusted Contact' : 'Edit Trusted Contact'
    };

    const [edits, setEdits] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [secureVisibilityState, setSecureVisibilityState] = useState(false);

    const onDeleteTrustedContact = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // set loading flag and submit request to server
                    await Request.post(utils, '/users/', {
                        id: abstract.getID(),
                        type: 'delete_trusted_contact'
                    });

                    // notify subscribers of data change
                    utils.content.fetch('trusted_contacts');

                    // end loading and notify origin that promise has been resolved
                    resolve({
                        banner: true, 
                        message: `${edits.first_name} ${edits.last_name} has been deleted and removed from your profile.`,
                        title: 'Trusted Contact Deleted',
                        onShow: setLayerState.bind(this, 'close')
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue deleting your trusted contact. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Delete Trusted Contact',
                message: `Are you sure that you want to remove ${edits.first_name} ${edits.last_name} as a Trusted Contact?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set layer state for api request processing
            setLayerState('submit_api_request');

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your Trusted Contact. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_trusted_contact.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(({ ...edits, ...props }));
    }

     const getApiRequestProps = () => {
        return {
            message: isNewTarget ? `We’re securely linking this person to your account so they can act on your behalf when it truly matters.` : `Your changes are being confirmed to ensure the right person has access when you need support most.`,
            title: `${isNewTarget ? 'Adding' : 'Updating'} Your Trusted Contact`,
            request: onProgressChange => {
                return new Promise(async (resolve, reject) => {
                    try {

                        // submit request to server to create contact
                        let response = await Request.post(utils, '/users/', {
                            ...edits,
                            type: isNewTarget ? 'new_trusted_contact' : 'update_trusted_contact'
                        },{
                            onProgressChange: onProgressChange
                        });

                        // prepare completion function call for component
                        resolve(() => {

                            // close layer and notify subscribers of data change
                            setLayerState('close');
                            utils.content.fetch('trusted_contacts');

                            // notify subscribers of data change if applicable
                            if(typeof(onChange) === 'function') {
                                onChange({ 
                                    ...response, 
                                    ...edits,
                                    full_name: `${edits.first_name} ${edits.last_name}`  
                                });
                                return;
                            }

                            // end loading and show confirmation alert
                            utils.banner.show({
                                title: `Trusted Contact ${isNewTarget ? 'Added' : 'Updated'}`,
                                message: isNewTarget ? `${edits.first_name} ${edits.last_name} has been added as a trusted contact.` : `The details for ${edits.first_name} ${edits.last_name} have been updated.`
                            });
                        });

                    } catch(e) {

                        // notify parent that promise failed and update layer state
                        reject(e);
                        setLayerState(null);

                        // show generic error with message
                        utils.alert.error({
                            error: e,
                            message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your Trusted Contact. ${e.message || 'An unknown error occurred'}`,
                            origin: 'add_edit_trusted_contact.get_api_request'
                        });
                    }
                });
            }
        }
     }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            onClick: onSubmitClick,
            visible: hasMadeEdits || requiresRequiemReview(),
            text: requiresRequiemReview() ? 'Approve' : (isNewTarget ? 'Submit' : 'Save Changes')
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            description: 'Your Trusted Contact is your digital backup in an emergency. If their name, phone, or email is incorrect, LifeWipe may be unable to verify them—delaying or blocking critical actions like a device wipe or legacy message release. Double-check their info. It could be the difference between protection and exposure.',
            key: 'details',
            title: 'Contact Information',
            items: [{
                component: 'textfield',
                description: 'The first name of your Trusted Contact',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                requiemSmartFlow: true,
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                description: 'The last name of your Trusted Contact',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                requiemSmartFlow: true,
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                description: 'The email address of your Trusted Contact. This is used to contact them in case of an emergency (failed LifeCheck or Emergency Trigger activation).',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                requiemSmartFlow: true,
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                description: 'The phone number of your Trusted Contact. This is used to verify their identity if they need to initiate an Emergency Trigger on your behalf or access your Secure Portal.',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                requiemSmartFlow: true,
                title: 'Phone Number',
                value: edits.phone_number
            }]
        },{
            collapsed: false,
            description: `You can authorize a Trusted Contact to initiate an emergency trigger on your behalf in a crisis. To do so, they must text a secret phrase to +1 (551) 363-3732—but only from the phone number you've listed on file. This phrase acts as a digital passcode, and no action will be taken without both a verified number and the correct phrase.`,
            key: 'emergency_triggers',
            lastItem: isNewTarget === true,
            title: 'Emergency Triggers',
            items: [{
                component: 'bool_list',
                key: 'enabled',
                onChange: val => {
                    onUpdateTarget({ 
                        preferences: {
                            ...edits.preferences,
                            emergency_triggers: {
                                enabled: val,
                                ...val === true && {
                                    cancel_phrase: getRandomPhrase(),
                                    start_phrase: getRandomPhrase()
                                }
                            }
                        }
                    });
                },
                requiemSmartFlow: true,
                title: 'Can Initiate Emergency Triggers',
                value: edits.preferences?.emergency_triggers?.enabled || false
            },{
                component: 'textfield',
                description: 'The secret phrase your Trusted Contact must text to +1 (551) 363-3732 to initiate an emergency trigger on your behalf.',
                key: 'start_phrase',
                onChange: text => {
                    onUpdateTarget({ 
                        preferences: {
                            ...edits.preferences,
                            emergency_triggers: {
                                ...edits.preferences?.emergency_triggers,
                                start_phrase: text
                            }
                        }
                    });
                },
                props: {
                    isSecure: true,
                    secureVisibilityState: secureVisibilityState
                },
                requiemSmartFlow: true,
                required: edits.preferences?.emergency_triggers?.enabled ? true : false,
                title: 'Emergency Start Phrase',
                value: edits.preferences?.emergency_triggers?.start_phrase,
                visible: edits.preferences?.emergency_triggers?.enabled ? true : false
            },{
                component: 'textfield',
                description: 'The secret phrase your Trusted Contact must text to +1 (551) 363-3732 to cancel an emergency trigger they previously initiated on your behalf.',
                key: 'cancel_phrase',
                onChange: text => {
                    onUpdateTarget({ 
                        preferences: {
                            ...edits.preferences,
                            emergency_triggers: {
                                ...edits.preferences?.emergency_triggers,
                                cancel_phrase: text
                            }
                        }
                    });
                },
                props: {
                    isSecure: true,
                    secureVisibilityState: secureVisibilityState
                },
                requiemSmartFlow: true,
                required: edits.preferences?.emergency_triggers?.enabled ? true : false,
                title: 'Emergency Cancel Phrase',
                value: edits.preferences?.emergency_triggers?.cancel_phrase,
                visible: edits.preferences?.emergency_triggers?.enabled ? true : false
            }]
        }];
    }

    const getRandomPhrase = () => {

        // prepare list of random words 
        let words = [
            'anchor', 'breeze', 'carbon', 'drift', 'ember', 'fluent', 'glide', 'harbor', 'ignite', 'jungle', 'knock', 'lunar', 'mingle', 'noble', 'orbit', 'pulse', 'quartz', 'ripple', 'summit', 'tidal', 'uplift', 'vortex', 'wander', 'xerox', 'yield', 'zenith', 'aspect', 'brisk', 'candid', 'dawn', 'eclipse', 'flicker', 'grit', 'hollow', 'inkling', 'jigsaw', 'kernel', 'legend', 'mosaic', 'nimble', 'opaque', 'prism', 'quiver', 'resin', 'solace', 'thorn', 'untamed', 'vault', 'whirl', 'xenon', 'yonder', 'zeal', 'apex', 'bliss', 'cascade', 'dwell', 'echo', 'flare', 'groove', 'hatch', 'idle', 'jade', 'keen', 'latch', 'mirth', 'novel', 'omen', 'plume', 'quest', 'rune', 'soar', 'trickle', 'urge', 'veil', 'wisp', 'xylem', 'yearn', 'zest', 'arc', 'bluff', 'clutch', 'dapple', 'elm', 'forge', 'gale', 'husk', 'iris', 'jolt', 'knot', 'leer', 'muse', 'nudge', 'oak', 'pivot', 'quaint', 'reef', 'swirl', 'tide', 'unit', 'vaulted', 'amber', 'barrel', 'clover', 'divide', 'echoes', 'feather', 'glimmer', 'hazel', 'inlet', 'jumper', 'kayak', 'lantern', 'meadow', 'nectar', 'oasis', 'panther', 'quarry', 'raven', 'saddle', 'talon', 'upstream', 'velvet', 'willow', 'xenial', 'yodel', 'zeppelin', 'archer', 'burrow', 'chisel', 'dynamo', 'embered', 'frost', 'grove', 'humble', 'ignite', 'jungle', 'keeper', 'luster', 'murmur', 'nimbus', 'oracle', 'petal', 'quench', 'rustle', 'sapphire', 'tundra', 'undone', 'voyage', 'warden', 'xiphoid', 'yawn', 'zodiac', 'alpine', 'boulder', 'cipher', 'daisy', 'enchant', 'fable', 'glisten', 'harvest', 'iceberg', 'javelin', 'kindle', 'lilac', 'minnow', 'nuzzle', 'onyx', 'pinnacle', 'quokka', 'reefed', 'serene', 'thicket', 'utopia', 'verge', 'whistle', 'xenolith', 'yolk', 'zen', 'attic', 'basil', 'cricket', 'drizzle', 'elmwood', 'fjord', 'garnet', 'horizon', 'inkwell', 'jar', 'karma', 'loam', 'mango', 'nectarine', 'outpost', 'parrot', 'quilt', 'reed', 'sleet', 'timber', 'usher', 'vial'
        ];

        // generate a list of 5 words
        let result = [];
        for (let i = 0; i < 5; i++) {
            const index = Math.floor(Math.random() * words.length);
            result.push(words[index]);
        }

        // set secure visibility state to show randomly generated phrases
        setSecureVisibilityState(true);

        // return joined string for phrase
        return result.join(' ');
    }

    const getRequiemReviewBanner = () => {
        return requiresRequiemReview() === true && {
            description: `Your trusted contact was setup with help from Requiem, your digital assistant. Before ${edits.first_name} ${edits.last_name} can be fully activated, we ask that you review and confirm the information below. Click here to approve the trusted contact.`,
            onClick: onSubmitClick
        }
    }

    const getResources = () => {
        return isNewTarget === false && (
            <LayerItem 
            collapsed={false}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <SystemEventsAsyncInteractionItem 
                    abstract={abstract} 
                    utils={utils} />
                    <AsyncInteractionItem 
                    description={'This will remove the trusted contact from your profile and prevent them from interacting with your account.'}
                    image={'images/delete-red-small.png'}
                    lastItem={true}
                    onAsyncClick={onDeleteTrustedContact}
                    title={'Delete Contact'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const requiresRequiemReview = () => {
        return edits.requiem_generated === true && edits.requiem_confirmation_date === null ? true : false;
    }

    const setupEditing = async () => {
        setEdits({ ...abstract.object });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            apiRequest: getApiRequestProps(),
            layerState: layerState,
            support: {
                description: `Need help? Activate Requiem AI for real-time guidance as you setup your Trusted Contact. Requiem can assist with any questions or concerns you may have during this process.`,
                requiem: {
                    enabled: true,
                    smart_flow: {
                        actions: [
                            'attach_trusted_contact_to_emergency_trigger',
                            'attach_trusted_contact_to_life_checks',
                            'start_smart_flow'
                        ],
                        context: {
                            full_name: edits.full_name,
                            resource_id: resource.id
                        },
                        items: getFields(),
                        ...resource
                    } 
                },
                title: 'Trusted Contacts'
            },
            warnings: {
                requiem_review_required: getRequiemReviewBanner()
            }
        }}>
            <AltFieldMapper
            fields={getFields()}
            resourceID={resource.id}
            utils={utils} />
            {getResources()}
        </Layer>
    )
}

export const AddEditTrustedDevice = ({ abstract, index, options, utils }) => {

    const layerID = `edit_trusted_device_${abstract.getID()}`;

    const [contentHasEdits, setContentHasEdits] = useState(false);
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onActiveStatusClick = () => {
        return new Promise((resolve, reject) => {

            const onSetActiveStatus = async () => {
                try {

                    // set loading flag and submit request to server
                    await Request.post(utils, '/trusted_devices/', {
                        id: abstract.getID(),
                        status: !abstract.object.active,
                        type: 'set_active_status'
                    });

                    // update target and notify subscribers of data change
                    abstract.object.active = !abstract.object.active;
                    utils.content.update(abstract);

                    // prepare confirmation alert content
                    resolve({
                        banner: true,
                        title: `Trusted Device ${abstract.object.active ? 'Activated' : 'Deactivated'}`,
                        message: `The trusted device '${abstract.object.name}' has been ${abstract.object.active ? 'activated' : 'deactivated'}.`
                    });

                } catch(e) {
                    let error = new Error(`There was an issue updating your trusted device. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // request confirmation from user
            let status = abstract.object.active;
            utils.alert.show({
                title: `${status ? 'Deactivate' : 'Activate'} Device`,
                message: `Are you sure that you want to ${status ? 'deactivate' : 'activate'} this device?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: status ? 'destructive' : 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: status ? 'default' : 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onSetActiveStatus, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onDeleteDeviceClick = () => {
        return new Promise((resolve, reject) => {

            const onDeleteDeviceConfirm = async () => {
                try {

                    // set loading flag and submit request to server
                    await Request.post(utils, '/trusted_devices/', {
                        id: abstract.getID(),
                        type: 'delete'
                    });

                    // notify subscribers of data change
                    utils.content.fetch('trusted_devices');

                    // prepare confirmation alert content
                    resolve({
                        banner: true,
                        title: 'Trusted Device Deleted',
                        message: `The trusted device '${abstract.object.name}' has been deleted from your account.`,
                        onShow: setLayerState.bind(this, 'close')
                    });

                } catch(e) {
                    let error = new Error(`There was an issue removing this trusted device from your account. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // request confirmation from user
            utils.alert.show({
                title: 'Delete Device',
                message: `Are you sure that you want to delete this device?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onDeleteDeviceConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            setLoading('submit');
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            await Request.post(utils, '/trusted_devices/', {
                ...edits,
                id: abstract.getID(),
                type: 'update'
            });

            // update target and notify subscribers of data change
            abstract.object.name = edits.name;
            utils.content.update(abstract);

            // show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: `Trusted Device Updated`,
                message: `Your trusted device "${edits.name}" has been updated.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your trusted device. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_trusted_device.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setContentHasEdits(true);
        setEdits(({ ...edits, ...props }));
    }

    const getAccessManagementComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Access Management'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={abstract.object.active ? 'This will temporarily prevent this device from accessing your account until it has been reactivated or reauthenticated through the two factor authentication flow.' : 'This will allow this device to acess your account until it has been deactivated or deleted.'}
                    image={abstract.object.active ? 'images/device-deactivate.png' : 'images/device-activate.png'}
                    onAsyncClick={onActiveStatusClick}
                    title={abstract.object.active ? 'Deactivate' : 'Activate'}
                    utils={utils} />

                    <AsyncInteractionItem 
                    description={'This will prevent this device from access your account until it has been added back to your account using the two factor authentication flow.'}
                    image={'images/delete-red-small.png'}
                    lastItem={true}
                    onAsyncClick={onDeleteDeviceClick}
                    title={'Delete from Account'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit',
            visible: contentHasEdits === true
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            key: 'details',
            lastItem: false,
            title: 'Device Details',
            items: [{
                component: 'textfield',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                title: 'Name',
                value: edits.name
            }]
        }];
    }

    const setupEditing = async () => {
        setEdits({ ...abstract.object });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            {getAccessManagementComponents()}
        </Layer>
    )
}

export const AddEditUser = ({ isNewTarget, onComplete, shouldShowAccessManagement, shouldShowCustomizations }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_user' : `edit_user_${abstract.getID()}`;
    const imagePickerRef = useRef(null);
    const twoFactorVerificationCode = useRef(null);
    
    const { advertising_metadata, referredByUserCode, setUser, user } = useDataStore();
    const [avatar, setAvatar] = useState(null);
    const [edits, setEdits] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [referralCode, setReferralCode] = useState(null);
    const [route, setRoute] = useState(null);

    const onChangeReferralCode = async () => {
        try {

            // set loading flag and submit request to change referral code
            let result = await onChangeReferralCodeAsync({ onLoad: setLoading.bind(this, 'referral_code') });

            // end loading and show confirmation banner
            setLoading(false);
            utils.banner.show({
                title: 'Referral Code Updated',
                ...result
            });

        } catch(e) {
            setLoading(false);
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: e.message,
                    origin: 'add_edit_user.on_change_referral_code'
                });
            }
        }
    }

    const onChangeReferralCodeAsync = props => {

        let { onLoad } = props || {};
        return new Promise(async (resolve, reject) => {

            const onConfirm = async code => {
                try {

                    // call onload function if a function was provided
                    if(typeof(onLoad) === 'function') {
                        onLoad();
                    }

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        referral_code: code,
                        type: 'update_referral_code',
                    });

                    // update edits for local state
                    setReferralCode(code);

                    // update value with user state 
                    setUser(user => {
                        user.referralCode = code;
                        return user;
                    });

                    // return success message to sender
                    resolve({ message: `Your referral code has updated. Please note that any previous links using your old code will no longer work. Make sure to use your new code when referring new clients to LifeWipe.` });

                } catch(e) {
                    let error = new Error(`We were unable to update your referral code. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }
            
            // request phone number input from user
            let code = null;
            utils.alert.show({
                title: 'Change Referral Code',
                message: `Swapping out your old code can refresh your invite strategy and help you tap into new audiences. Heads up, though: changing your referral code will break any existing links you've shared. So be sure to update your invites everywhere you've posted them.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'New Referral Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: async key => {
                    if(code && key === 'confirm') {
                        setTimeout(onConfirm.bind(this, code), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onContinueWithFacebookClick = async () => {
        try {

            // set loading state
            setLoading('facebook');

            // initialize facebook sdk if not already loaded
            await initializeFacebookSDK();

            // request facebook login
            await new Promise((resolve, reject) => {
                window.FB.login(response => {
                    if(response.authResponse) {
                        resolve(response.authResponse);
                    } else {
                        let error = new Error('user_cancelled');
                        reject(error);
                    }
                }, { 
                    scope: 'public_profile,email' 
                });
            });

            // fetch user profile from facebook
            const profile = await new Promise((resolve, reject) => {
                window.FB.api('/me', { fields: 'id,name,email,first_name,last_name' }, response => {
                    if(response && !response.error) {
                        resolve(response);
                    } else {
                        let error = new Error(response.error || 'Failed to fetch Facebook profile');
                        reject(error);
                    }
                });
            });

            // throw an error if no name or email were provided
            if(!profile.email || !profile.first_name || !profile.last_name) {
                throw new Error('Facebook did not return sufficient profile information. Please ensure that you have granted permission to access your name and email address. Alternatively, you can sign up using your email address and password.');
            }

            // submit request to create user with the social signup credentials
            let { trusted_device_token, user } = await onCreateUserFromSocialSignIn({
                email_address: profile.email,
                first_name: profile.first_name,
                last_name: profile.last_name,
                social_signin_external_id: profile.id,
                social_signin_service: 'facebook'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify meta pixel that registration has completed
            window.fbq('track', 'CompleteRegistration');

            // notify subscribers that completion has taken place
            if(typeof(onComplete) === 'function') {
                setTimeout(onComplete.bind(this, user, { trusted_device_token, remember_me: false }), 500);
            }

        } catch(e) {
            setLoading(false);
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue signing in with Facebook. ${e.message || 'An unknown error occurred'}`,
                    origin: 'add_edit_user.on_continue_with_facebook_click'
                });
            }
        }
    }

    const onCreateUserFromSocialSignIn = async props => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server
                let { onboarding, trusted_device_token, user } = await Request.post(utils, '/users/', {
                    ...props,
                    device: utils.device.get(),
                    metadata: { advertising_metadata },
                    referral_code: referredByUserCode,
                    type: 'new_social_signup'
                });

                // set user object at root
                let target = User.create({ ...user, legal_agreements: {}, onboarding });
                setUser(target);

                // write trusted device token to local storage if applicable
                if(trusted_device_token) {
                    utils.user.device.set(user.user_id, trusted_device_token);
                }
                resolve({ trusted_device_token, user: target });
                
            } catch(e) {
                reject(e);
            }
        });
    }

    const onContinueWithGoogleClick = async () => {
        try {

            // set loading state
            setLoading('google');

            // ensure google sdk is loaded (should already be preloaded from useEffect)
            if(!window.google?.accounts || !window.gapi) {
                await initializeGoogleSDK();
            }

            // request google sign-in with oauth popup using token client
            const tokenResponse = await new Promise((resolve, reject) => {

                // init token client using google client id
                const client = window.google.accounts.oauth2.initTokenClient({
                    client_id: API.google_client_id,
                    scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
                    callback: response => {
                        if(response.access_token) {
                            resolve(response);
                        } else {
                            reject(new Error('user_cancelled'));
                        }
                    },
                    error_callback: e => {
                        let error = new Error(e.message || 'user_cancelled');
                        reject(error);
                    }
                });
                
                // request access token with user gesture hint
                client.requestAccessToken({ prompt: 'consent' });
            });

            // use google's people api to get user info with the access token
            const payload = await new Promise((resolve, reject) => {
                window.gapi.client.request({
                    path: 'https://www.googleapis.com/oauth2/v3/userinfo',
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${tokenResponse.access_token}`
                    }
                }).then(response => {
                    resolve(response.result);
                }, e => {
                    let error = new Error(e.message || 'Failed to fetch user information');
                    reject(error);
                });
            });

            // throw an error if no name or email were provided
            if(!payload.email || !payload.given_name || !payload.family_name) {
                throw new Error('Google did not return sufficient profile information. Please ensure that you have granted permission to access your name and email address. Alternatively, you can sign up using your email address and password.');
            }

            // submit request to create user with the social signup credentials
            let { trusted_device_token, user } = await onCreateUserFromSocialSignIn({
                email_address: payload.email,
                first_name: payload.given_name,
                last_name: payload.family_name,
                social_signin_external_id: payload.sub,
                social_signin_service: 'google'
            });

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify meta pixel that registration has completed
            window.fbq('track', 'CompleteRegistration');

            // notify subscribers that completion has taken place
            if(typeof(onComplete) === 'function') {
                setTimeout(onComplete.bind(this, user, { trusted_device_token, remember_me: false }), 500);
            }

        } catch(e) {
            setLoading(false);
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue signing in with Google. ${e.message || 'An unknown error occurred'}`,
                    origin: 'add_edit_user.on_continue_with_google_click'
                });
            }
        }
    }

    const onForgotPasswordClick = () => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server
                await Request.post(utils, '/users/', {
                    email_address: API.use_email_2fa ? user.email_address : null,
                    phone_number: API.use_email_2fa ? null : user.phone_number,
                    type: 'new_reset_password_request'
                });

                // log event for analytics
                utils.analytics.log('forgot_password_request_sent');

                // return success message to sender
                resolve({ message: `A password verification ${API.use_email_2fa ? 'email' : 'text message'} has been sent to the ${API.use_email_2fa ? 'email address' : 'phone number'} on file with your account. The reset link will expire after 15 minutes.` });

            } catch(e) {
                let error = new Error(`We were unable to send your password reset verification message. ${e.message || 'An unknown error occurred'}`);
                reject(error);
            }
        });
    }

    const onImageChange = evt => {

        let { files } = evt.target;
        if(files && files.length > 0) {

            let file = files[0];
            let reader = new FileReader();
            reader.onload = async data => {
                setHasMadeEdits(true);
                setAvatar({
                    data: data.target.result,
                    file_name: file.name,
                    file_type: file.type,
                });
            };
            reader.readAsDataURL(files[0]);
        }
    }

    const onLegalClick = key => {
        utils.navigation.set({ 
            view: 'legal', 
            waypoint: key 
        });
    }

    const onProfilePhotoClick = () => {
        imagePickerRef.current.click();
    }

    const onRemovePassKeyAsync = () => {
        return new Promise((resolve, reject) => {

            // define reusable function for submitting removal request
            const onRemovePassKey = async () => {
                try {
                    
                    // set loading flag and submit request to server
                    let { two_factor_verification_required } = await Request.post(utils, '/users/', {
                        two_factor_verification_code: twoFactorVerificationCode.current,
                        type: 'remove_passkey',
                        user_id: abstract.getID()
                    });

                    // determine if a verification was requested
                    if(two_factor_verification_required) {
                        await Utils.sleep(0.5);
                        await processTwoFactorAuthenticationRequest(onRemovePassKey);
                        return;
                    }

                    // update values with user object
                    setUser(user => {
                        user.passkey = null;
                        user.passkey_configured = false;
                        return user;
                    });

                    // resolve confirmation alert content
                    resolve({
                        title: 'PassKey Removed',
                        message: 'The PassKey has been removed from your account',
                        onClick: hasMadeEdits ? null : setLayerState.bind(this, 'close')
                    });

                } catch(e) {
                    twoFactorVerificationCode.current = null;
                    reject(e);
                }
            }
            
            // initiate first request
            onRemovePassKey();
        });
    }

    const onRemovePassKeyAsyncClick = () => {
        return new Promise((resolve, reject) => {
            utils.alert.show({
                title: 'Remove PassKey',
                message: `Are you sure that you want to remove this PassKey from your account?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: async key => {
                    if(key !== 'confirm') {
                        let error = new Error('user_cancelled');
                        reject(error);
                        return;
                    }
                    try {
                        let result = await onRemovePassKeyAsync();
                        resolve(result);
                    } catch(e) {
                        reject(e);
                    }
                }
            });
        });
    }

    const onReferralCodeClick = () => {
        utils.alert.show({
            title: 'Referral Code',
            message: `Your referral code links new LifeWipe users to you, earning you commission on every subscription they maintain. It also helps you unlock milestones, bonuses, and special perks for your account. Share it anywhere—your impact and income grow with every signup.`,
            buttons: [{
                key: 'change',
                title: 'Change Referral Code',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Close',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'change') {
                    setTimeout(onChangeReferralCode, 500);
                    return;
                }
            }
        });
    }

    const onSetPassKey = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // throw an error if an account name has not been configured
                if(!edits.first_name || !edits.last_name) {
                    throw new Error('Please provide a first name and last name for your account to continue');
                }

                // generate a single use token for credential
                let key = Utils.randomString();

                // request credential creation from authentication api
                let credential = await navigator.credentials.create({
                    publicKey: {
                        challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                        rp: { id: window.location.hostname, name: "LifeWipe Inc" },
                        user: {
                            displayName: 'Account Login',
                            id: Uint8Array.from(key, c => c.charCodeAt(0)),
                            name: `${edits.first_name} ${edits.last_name}'s Account`
                        },
                        pubKeyCredParams: [{ 
                            alg: -7,
                            type: 'public-key'
                        }]
                    }
                });

                // update local state with passkey id
                onUpdateTarget({ passkey: credential.id });

                // resolve confirmation alert content
                resolve({
                    title: 'PassKey Configured',
                    message: `Your PassKey has been configured and is ready to be added to your account. ${isNewTarget ? 'Finish signing up for your account to continue.' : 'Submit your account changes to conintune.'}`
                });
                
            } catch(e) {
                let error = e.message.includes('user denied') ? new Error('user_cancelled') : e;
                reject(error);
            }
        });
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            let { onboarding, trusted_device_token, user } = await Request.post(utils, '/users/', {
                ...edits,
                avatar: avatar,
                device: utils.device.get(),
                dob: edits.dob?.format('YYYY-MM-DD'),
                metadata: { advertising_metadata },
                type: 'new',
                user_id: abstract.getID()
            });

            // set user object at root
            let target = User.create({ ...user, onboarding });
            setUser(target);

            // write trusted device token to local storage if applicable
            if(trusted_device_token) {
                utils.user.device.set(user.user_id, trusted_device_token);
            }

            // no additional logic is required if target is new
            if(isNewTarget === true) {

                // notify meta pixel that registration has completed
                window.fbq('track', 'CompleteRegistration');

                // show confirmation alert that instructs the user to check their text messages for opt-in
                utils.alert.show({
                    title: 'Your Account Is Ready',
                    message: `Thanks for signing up! To complete setup, we just sent a welcome text message to your phone. Reply YES to that message to confirm your number and enable text messaging features. This is needed for secure login (2FA), device verification, and emergency actions.`,
                    onClick: () => {
                        setLayerState('close');
                        if(typeof(onComplete) === 'function') {
                            setTimeout(onComplete.bind(this, target, { 
                                passkey: edits.passkey,
                                trusted_device_token, 
                            }), 500);
                        }
                    }
                });
                return;
            }

            // end loading show confirmation alert for existing targets
            setLoading(false);
            utils.banner.show({
                title: 'Account Updated',
                message: 'The changes to your account have been saved.',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your account. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_user.on_submit_click'
            });
        }
    }

    const onUpdateClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            let { avatar_url, two_factor_verification_required } = await Request.post(utils, '/users/', {
                ...edits,
                avatar: avatar,
                two_factor_verification_code: twoFactorVerificationCode.current,
                type: 'update',
                user_id: abstract.getID()
            });

            // end loading and determine if a verification was requested
            setLoading(false);
            if(two_factor_verification_required) {
                await processTwoFactorAuthenticationRequest(onUpdateClick);
                return;
            }

            // update values for local user state
            setUser(user => {

                // update values for user object
                user.avatar = avatar_url || user.avatar;
                user.address = edits.address;
                user.dob = edits.dob;
                user.email_address = edits.email_address;
                user.first_name = edits.first_name;
                user.last_name = edits.last_name;
                user.location = edits.location;
                user.passkey_configured = edits.passkey ? true : edits.passkey_configured;
                user.phone_number = edits.phone_number;
                user.username = edits.username;

                // update onboarding to indicate that profile is complete
                user.onboarding.profile_complete = true;

                return user;
            });

            // show confirmation alert that updates have been accepted
            utils.banner.show({
                title: 'Account Updated',
                message: 'The changes to your account have been saved.',
                onShow: () => {
                    setLayerState('close');
                    if(typeof(onComplete) === 'function') {
                        onComplete();
                    }
                }
            });

        } catch(e) {

            // end loading and reset pending verification code
            setLoading(false);
            twoFactorVerificationCode.current = null;

            // show error alert with error description
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue updating your account. ${e.message || 'An unknown error occurred'}`,
                    origin: 'add_edit_user.on_update_click'
                });
            }
        }
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(({ ...edits, ...props }));
    }

    const getAccessManagementComponents = () => {

        let items = [{
            description: 'This can help in scenarios where you suspect that your PassKey has been compromised.',
            image: 'images/update-passkey.png',
            onAsyncClick: onSetPassKey,
            title: 'Update PassKey',
            visible: (edits.passkey || edits.passkey_configured) ? true : false
        },{
            description: 'This allows you to revoke access for a PassKey that you no longer wish to use.',
            image: 'images/delete-red-small.png',
            onAsyncClick: onRemovePassKeyAsyncClick,
            title: 'Remove PassKey',
            visible: edits.passkey_configured ? true : false
        },{
            description: `Passkeys are phishing-resistant. They're tied to your device and are harder to compromise than traditional passwords.`,
            image: 'images/add-passkey.png',
            onAsyncClick: onSetPassKey,
            title: 'Configure a PassKey',
            visible: (!edits.passkey && !edits.passkey_configured) ? true : false
        },{
            description: 'We can help you regain access to your acount if you\'ve misplaced your password.',
            image: 'images/reset-password.png',
            onAsyncClick: onForgotPasswordClick,
            title: 'Forgot Password?'
        }];

        return isNewTarget === false && shouldShowAccessManagement !== false && (
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Access Management'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {items.map((item, index) => {
                        if(item.visible === false) {
                            return null;
                        }
                        return (
                            <AsyncInteractionItem 
                            description={item.description}
                            image={item.image}
                            key={index}
                            lastItem={index === (items.length - 1)}
                            onAsyncClick={item.onAsyncClick}
                            title={item.title}
                            utils={utils} />
                        );
                    })}
                </div>
            </LayerItem>
        )
    }

    const getAvatar = styles => {
        let image = avatar?.data || user?.avatar || 'images/update-profile-photo.png';
        return (
            <img 
            src={image}
            style={{
                ...styles,
                border: `1px solid ${Appearance.colors.divider()}`
            }} />
        )
    }

    const getButtons = () => {
        if(route === null && isNewTarget === true) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: isNewTarget ? onSubmitClick : onUpdateClick,
            text: isNewTarget ? 'Submit' : 'Save Changes',
            visible: hasMadeEdits || isNewTarget === true
        }];
    }

    const getContent = () => {

        // show standard editing fields for existing accounts and manual signup route
        if(isNewTarget === false || route === 'manual_signup') {
            return (
                <>
                {getHeader()}
                <div style={{
                    padding: 16,
                    width: '100%'
                }}> 
                    <AltFieldMapper
                    fields={getFields()}
                    utils={utils} />
                    {getCustomizationComponents()}
                    {getAccessManagementComponents()}
                    {getDisclaimer()}
                </div>
                </>
            )
        }

        // fallback to presenting sign-in and sign-up options
        let signInServices = [{ 
            id: 'facebook',
            image: 'images/sign-in-with-facebook.png',
            name: 'Sign Up with Facebook',
            onClick: onContinueWithFacebookClick
        },{
            id: 'google',
            image: 'images/sign-in-with-google.png',
            name: 'Sign Up with Google',
            onClick: onContinueWithGoogleClick
        }];
        
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Sign Up'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 16px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginBottom: 16
                    }}>
                        {`By continuing, you agree to our `}
                        <span 
                        className={'text-button'}
                        onClick={onLegalClick.bind(this, 'terms_of_service')}
                        style={{
                            color: Appearance.colors.primary(),
                            textDecoration: 'underline'
                        }}>
                            {`Terms of Service`}
                        </span>
                        {` and acknowledge that you understand our `}
                        <span 
                        className={'text-button'}
                        onClick={onLegalClick.bind(this, 'privacy_policy')}
                        style={{
                            color: Appearance.colors.primary(),
                            textDecoration: 'underline'
                        }}>
                            {`Privacy Policy`}
                        </span>
                    </span>
                    {signInServices.map((service, index) => {
                        return (
                            <div 
                            className={`view-entry ${window.theme}`}
                            onClick={service.onClick}
                            key={index}
                            style={{
                                alignItems: 'center',
                                borderColor: Appearance.colors.divider(),
                                borderRadius: 20,
                                borderStyle: 'solid',
                                borderWidth: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                height: 40,
                                justifyContent: 'center',
                                marginBottom: index === signInServices.length - 1 ? 0 : 8,
                                padding: 12,
                                textAlign: 'center',
                                width: '100%'
                            }}>
                                {loading === service.id && (
                                    <LottieView
                                    autoPlay={true}
                                    loop={true}
                                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                                    style={{
                                        height: 25,
                                        width: 25
                                    }}/>
                                )}
                                {loading !== service.id && (
                                    <>
                                    <img 
                                    src={service.image}
                                    style={{
                                        height: 20,
                                        marginRight: 8,
                                        objectFit: 'contain',
                                        width: 20
                                    }} />
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        flexGrow: 1,
                                        fontWeight: 600
                                    }}>{service.name}</span>
                                    </>
                                )}
                            </div>
                        )
                    })}
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: 24,
                        marginTop: 24,
                        width: '100%'
                    }}>
                        <div style={{
                            backgroundColor: Appearance.colors.divider(),
                            borderRadius: 1,
                            height: 2,
                            marginRight: 12,
                            width: '100%'
                        }} />
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            flex: 'none'
                        }}>{'OR'}</span>
                        <div style={{
                            backgroundColor: Appearance.colors.divider(),
                            borderRadius: 1,
                            height: 2,
                            marginLeft: 12,
                            width: '100%'
                        }} />
                    </div>
                    <div 
                    className={`view-entry ${window.theme}`}
                    onClick={setRoute.bind(this, 'manual_signup')}
                    style={{
                        alignItems: 'center',
                        borderColor: Appearance.colors.divider(),
                        borderRadius: 20,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        height: 40,
                        padding: 12,
                        textAlign: 'center',
                        width: '100%'
                    }}>
                        <img 
                        src={window.theme === 'dark' ? 'images/sign-in-with-email-dark.png' : 'images/sign-in-with-email-light.png'}
                        style={{
                            height: 20,
                            marginRight: 8,
                            objectFit: 'contain',
                            width: 20
                        }} />
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            flexGrow: 1,
                            fontWeight: 600
                        }}>{'Sign Up with Email Address'}</span>
                    </div>
                </div>
            </LayerItem>
        )
    }

    const getCustomizationComponents = () => {
        if(isNewTarget === true || shouldShowCustomizations === false) {
            return null;
        }
        return (
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Customizations'}
            style={{
                marginTop: LayerItemSpacing
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {isNewTarget === false && (
                        <AsyncInteractionItem 
                        description={`A fresh code can feel more relevant and personal, helping you attract more clients.`}
                        image={'images/change-referral-code.png'}
                        onAsyncClick={onChangeReferralCodeAsync}
                        title={'Change Referral Code'}
                        utils={utils} />
                    )}
                    <AsyncInteractionItem 
                    description={`A profile photo helps us confirm it's really you—especially during emergencies. Choose something recognizable and true to you.`}
                    image={getAvatar}
                    lastItem={true}
                    onAsyncClick={onProfilePhotoClick}
                    required={false}
                    title={isNewTarget ? 'Choose Profile Photo' : 'Update Profile Photo'}
                    utils={utils} 
                    value={avatar || edits.avatar} />
                    <input
                    accept={'image/*'}
                    className={`custom-file-input ${window.theme}`}
                    multiple={false}
                    onChange={onImageChange}
                    ref={imagePickerRef} 
                    style={{
                        display: 'none',
                        height: 0
                    }}
                    type={'file'} />
                </div>
            </LayerItem>
        )
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
                marginTop: 24,
                width: '100%'
            }}>{`By submitting your information to our service, you acknowledge that your data will be used to provide you with updates and promotional materials related to our products and services. We are committed to protecting your privacy and ensuring your information is handled securely. Please review our privacy policy for more details on how we collect, use, and protect your information.`}</span>
        )
    }

    const getFields = () => {
        return [{
            key: 'about',
            title: 'About You',
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text?.trim() }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text?.trim() }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'date_picker',
                key: 'dob',
                onChange: result => onUpdateTarget({ dob: result }),
                props: {
                    removable: false
                },
                required: true,
                title: 'Date of Birth',
                value: edits.dob,
                visible: isNewTarget === false
            }]
        },{
            key: 'contact',
            title: 'Contact Information',
            items: [{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number?.length > 0 ? edits.phone_number : null
            },{
                component: 'address_lookup',
                key: 'address',
                onChange: result => onUpdateTarget(result),
                title: 'Address',
                value: edits.address && {
                    address: edits.address,
                    location: edits.location
                }
            }]
        },{
            key: 'credentials',
            lastItem: true,
            title: 'Credentials',
            items: [{
                component: 'textfield',
                key: 'username',
                onChange: text => onUpdateTarget({ username: text }),
                props: {
                    autoCapitalize: false,
                    autoComplete: 'username'
                },
                title: 'Username',
                value: edits.username
            },{
                component: 'textfield',
                key: 'password',
                onChange: text => onUpdateTarget({ password: text }),
                props: {
                    autoComplete: isNewTarget ? 'new-password' : 'current-password',
                    isSecure: true
                },
                required: isNewTarget,
                title: 'Password',
                value: edits.password,
                visible: isNewTarget
            },{
                component: 'textfield',
                key: 'referral_code',
                onChange: text => onUpdateTarget({ referral_code: text }),
                required: false,
                title: 'Referred by Someone? (Enter Code)',
                value: edits.referral_code,
                visible: isNewTarget === true
            },{
                component: 'textfield',
                key: 'referral_code',
                onClick: onReferralCodeClick,
                props: {
                    loading: loading === 'referral_code'
                },
                required: false,
                title: 'Referral Code',
                value: referralCode,
                visible: isNewTarget === false
            }]
        }];
    }

    const getHeader = () => {
        if(isNewTarget) {
            return (
                <div style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    padding: 16,
                    paddingTop: 12,
                    width: '100%'
                }}>
                    <LayerItem 
                    collapsed={null}
                    lastItem={true}
                    title={'Account Signup'}>
                        <span style={{
                            ...Appearance.textStyles.defaultText()
                        }}>{`A LifeWipe account gives you access to our comprehensive digital legacy management system and ensures your data is handled exactly as you wish. Join thousands of forward-thinking individuals who have already protected their digital afterlife with LifeWipe.`}</span>
                    </LayerItem>
                </div>
            )
        }
    }

    const initializeFacebookSDK = () => {
        return new Promise((resolve, reject) => {

            // check if sdk is already loaded
            if(window.FB) {
                resolve();
                return;
            }
            
            // load facebook sdk
            window.fbAsyncInit = () => {
                window.FB.init({
                    appId: API.facebook_app_id_public,
                    cookie: true,
                    xfbml: true,
                    version: 'v18.0'
                });
                resolve();
            };

            // inject sdk script
            const script = document.createElement('script');
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
            document.body.appendChild(script);
        });
    }

    const initializeGoogleSDK = () => {
        return new Promise((resolve, reject) => {
            
            // check if sdk is already loaded
            if(window.google?.accounts && window.gapi) {
                resolve();
                return;
            }

            // load google api client library first
            const gapiScript = document.createElement('script');
            gapiScript.src = 'https://apis.google.com/js/api.js';
            gapiScript.async = true;
            gapiScript.defer = true;
            gapiScript.onload = () => {
                window.gapi.load('client', () => {

                    // load google identity services sdk
                    const script = document.createElement('script');
                    script.src = 'https://accounts.google.com/gsi/client';
                    script.async = true;
                    script.defer = true;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error('Failed to load Google SDK'));
                    document.head.appendChild(script);
                });
            };
            gapiScript.onerror = () => reject(new Error('Failed to load Google API'));
            document.head.appendChild(gapiScript);
        });
    }

    const processTwoFactorAuthenticationRequest = callback => {
        return new Promise((resolve, reject) => {
            let code = null;
            utils.alert.show({
                title: 'Verification Required',
                message: `We've sent a verification code to the ${API.use_email_2fa ? 'email address' : 'phone number'} on file with your account. You'll need to enter it to confirm your identity before we can apply any changes to your account.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'Verification Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(code && key === 'continue') {
                        twoFactorVerificationCode.current = code;
                        setTimeout(callback, 500);
                        resolve();
                        return;
                    }
                    twoFactorVerificationCode.current = null;
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const setupEditing = () => {
        if(isNewTarget === true) {

            initializeGoogleSDK().catch(e => console.warn('Failed to preload Google SDK:', e));
            initializeFacebookSDK().catch(e => console.warn('Failed to preload Facebook SDK:', e));

            setEdits({ 
                email_address: abstract.object.email_address,
                first_name: abstract.object.first_name,
                last_name: abstract.object.last_name,
                phone_number: abstract.object.phone_number,
                referral_code: referredByUserCode 
            });
        }
        if(isNewTarget === false) {
            setEdits({
                avatar: abstract.object.avatar,
                address: abstract.object.address,
                dob: abstract.object.dob,
                email_address: abstract.object.email_address,
                first_name: abstract.object.first_name,
                last_name: abstract.object.last_name,
                location: abstract.object.location,
                passkey_configured: abstract.object.passkey_configured,
                phone_number: abstract.object.phone_number,
                username: abstract.object.username
            });
            setReferralCode(abstract.object.referral_code);
        }
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            removePadding: route === 'manual_signup' || isNewTarget === false
        }}>
            {getContent()}
        </Layer>
    )
}

export const BrandAssetsVault = ({ utils }) => {

    const onAssetsDownloadClick = () => {
        window.open(`${API.server}/downloads/brand-assets.zip`);
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'share',
            onClick: onAssetsDownloadClick,
            text: 'Download Assets'
        }];
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Inside the Brand Assets Vault, you will find downloadable high-res logos to use with your marketing outreach initiatives. Whether you're launching a campaign or prepping for a local event, this vault is your creative command center. Be sure to pair your assets with your referral code to maximize your growth.`}
                buttons={getContainerButtons()}
                title={'Brand Assets Vault'} />
            </AnimatedElement>
        </div>
    )
}

export const CompleteLifeCheck = ({ utils }) => {

    const [authenticationMethod, setAuthenticationMethod] = useState(null);
    const [completed, setCompleted] = useState(false);
    const [isEmergencyOverride, setIsEmergencyOverride] = useState(false);
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const { user, setUser } = useDataStore();

    const onLoginClick = () => {
        utils.user.request.login({ shouldRedirectUser: false });
    }

    const onRequestBackupKey = () => {
        let value = null;
        utils.alert.show({
            title: 'Backup Access Key',
            message: 'Please provide your backup access key below and click "Done" to continue.',
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    isSecure={true}
                    onChange={text => value = text}
                    placeholder={'Access Key'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(value && key === 'confirm') {
                    setLoading('backup_key');
                    setTimeout(onSubmitVerification.bind(this, { mfa_passkey: value }), 500);
                    return;
                }
            }
        });
    }

    const onRequestMFAPassKey = async () => {
        try {

            // request credentials from web authenticaton api
            let credential = await navigator.credentials.get({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rpId: window.location.hostname,
                    userVerification: 'required'
                }
            });

            // set loading flag and submit verification to server
            setLoading('passkey');
            setTimeout(onSubmitVerification.bind(this, { mfa_passkey: credential.id }), 500);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onStartPasscodeVerification = () => {
        let result = null;
        utils.alert.show({
            title: 'Authentication Required',
            message: `You've configured a secret passcode or phrase as your LifeCheck authentication method. Please input your passcode or phrase below to complete your verification.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    isSecure={true}
                    onChange={text => result = text}
                    placeholder={'Secret Passcode or Phrase'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Submit',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(result && key === 'confirm') {
                    setLoading('submit');
                    setTimeout(onSubmitVerification.bind(this, { secret_phrase_value: result }), 500);
                    return;
                }
            }
        });
    }

    const onStartTwoFactorVerification = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('two_factor_sms');
            let { phone_number_last_4 } = await Request.get(utils, '/subscriptions/', {
                token: request?.token,
                type: 'new_life_check_2fa_code'
            });

            // show alert requesting sms code from user
            let result = null;
            utils.alert.show({
                title: 'Authentication Required',
                message: `You've configured your LifeChecks to use two-factor authentication for verification. We've sent a one-time code to your phone ending in ${phone_number_last_4}. Please enter the code below to confirm your identity and complete your LifeCheck.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        isSecure={true}
                        onChange={text => result = text}
                        placeholder={'Two Factor Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'cancel') {
                        setLoading(false);
                        return;
                    }
                    if(result && key === 'confirm') {
                        setLoading('submit');
                        setTimeout(onSubmitVerification.bind(this, { two_factor_sms_code: result }), 500);
                        return;
                    }
                }
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue sending your two factor authentication code. ${e.message || 'An unknown error occurred'}`,
                origin: 'complete_life_check.on_start_two_factor_verification'
            });
        }
    }

    const onSubmitVerification = async props => {
        try {

            // determine if certain variables are required before submitting the request
            switch(authenticationMethod) {
                case 'secret_phrase':
                if(!props.secret_phrase_value) {
                    throw new Error('Please provide your secret passcode or phrase before submitting your verification');
                }
                break;
            }
            
            // submit request to server
            let { user_status } = await Request.post(utils, '/subscriptions/', {
                ...props,
                token: request?.token,
                type: 'verify_lifecheck'
            });

            // end loading and set completion flag
            setLoading(false);
            setCompleted(true);

            // update status for user object
            setUser(user => {
                user.status = user_status;
                return user;
            });

        } catch(e) {
            setLoading(false);
                utils.alert.error({
                error: e,
                message: `There was an issue submitting your LifeCheck verification. ${e.message || 'An unknown error occurred'}`,
                origin: 'complete_life_check.on_submit_verification'
            });
        }
    }

    const getContainerButtons = () => {
        if(!user || !subscription) {
            return [{
                color: 'primary',
                id: 'login',
                loading: loading === 'login',
                onClick: onLoginClick,
                text: 'Login and Verify'
            }];
        }
        switch(authenticationMethod) {
            case 'secret_phrase':
            return [{
                color: 'primary',
                id: 'submit',
                loading: loading === 'submit',
                onClick: onStartPasscodeVerification,
                text: 'Start Verification'
            }];

            case 'two_factor_sms':
            return [{
                color: 'primary',
                id: 'submit',
                loading: ['submit','two_factor_sms'].includes(loading),
                onClick: onStartTwoFactorVerification,
                text: 'Start Verification'
            }];

            case 'mfa_passkey':
            return [{
                color: 'primary',
                id: 'mfa_passkey',
                loading: loading === 'passkey',
                onClick: onRequestMFAPassKey,
                text: 'Select a PassKey'
            },{
                color: 'grey',
                id: 'backup_key',
                loading: loading === 'backup_key',
                onClick: onRequestBackupKey,
                text: 'Provide a Backup Key'
            }];
        }
    }

    const getContentDescription = () => {
        return isEmergencyOverride ? getEmergencyOverrideContentDescription() : getLifeCheckContentDescription();
    }

    const getContentTitle = () => {
        if(completed === true) {
            return isEmergencyOverride ? 'Emergency Override Complete' : 'LifeCheck Verification Complete';
        }
        return isEmergencyOverride ? 'Emergency Override' : 'LifeCheck Verification';
    }

    const getEmergencyOverrideContentDescription = () => {
        if(!user || !subscription) {
            return `To begin your Emergency Override verification, you'll need to log in to your LifeWipe account. For your security, completing an Emergency Override also requires access to your configured authentication method. This ensures that only you can confirm you're alive and in control.`;
        }
        if(completed === true) {
            return `You've successfully verified control. Your LifeWipe protocols have been paused, and all pending emergency actions have been canceled. Your digital assets remain secured, and your protections are now back on standby. No further action is required at this time.`;
        }
        return `To complete your Emergency Override and stop your pre-set instructions from continuing, you must verify your identity using your configured emergency authentication. Please note: your emergency actions are already in progress and will continue unless this check is completed in time. Once verified, all active triggers will be canceled and your timeline reset.`;
    }

    const getLifeCheckContentDescription = () => {
        if(!user || !subscription) {
            return `To begin your LifeCheck verification, you'll need to log in to your LifeWipe account. For your security, completing a Life-Check also requires access to your configured authentication method. This ensures that only you can confirm you're alive and in control.`;
        }
        if(completed === true) {
            return `You've confirmed you're in control — your LifeWipe protections remain on standby. Your emergency protocols have been paused, and your digital assets remain secured according to your instructions. No further action is needed at this time.`;
        }
        return `To complete your ${subscription?.tier.title} Subscription LifeCheck and prevent any automated actions (like data wipes or legacy transfers), you'll need to provide your configured authentication credentials. This step ensures your LifeWipe protections don't fall into the wrong hands — or activate without cause. Once verified, your LifeCheck will be marked as complete and your timeline resets.`;
    }

    const fetchSubscriptionDetails = async () => {
        try {
            setLoading('login');
            let { authentication_method, is_emergency_override, subscription } = await Request.get(utils, '/subscriptions/', {
                token: request?.token,
                type: 'lifecheck_resources'
            });

            setLoading(false);
            setAuthenticationMethod(authentication_method);
            setIsEmergencyOverride(is_emergency_override);
            setSubscription(Subscription.create(subscription));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue preparing your LifeCheck verification. ${e.message || 'An unknown error occurred'}`,
                origin: 'complete_life_check.fetch_subscription_details'
            });
        }
    }

    useEffect(() => {
        if(user) {
            fetchSubscriptionDetails();
        }
    }, [user]);

    useEffect(() => {
        
        // update local state with query parameters
        let parameters = QueryString.parse(window.location.search);
        setRequest(parameters);

    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={getContentDescription()}
                buttons={completed === true ? null : getContainerButtons()}
                title={getContentTitle()} />
            </AnimatedElement>
        </div>
    )
}

export const ConfigureSocialMediaAccounts = ({ index, options, utils }) => {

    const layerID = 'configure_social_media_accounts';
    const [layerState, setLayerState] = useState(null);
    const { sockets, user, setUser } = useDataStore();

    const onSocialMediaChannelClick = async channel => {
        try {

            // show options for managing account if applicable
            let account = user.social_media[channel.id];
            if(account) {
                utils.layer.open({
                    Component: EditSocialChannelPreferences.bind(this, { 
                        channel, 
                        id: account.id
                    }),
                    id: `edit_social_channel_preferences_${account.id}`
                });
                return;
            }

            // fetch state token for optional provider in-app authentication flow
            let { state } = await Request.get(utils, '/users/', {  
                channel: channel.id,
                type: 'new_social_media_oauth_state_token'
            });

            // show alert to minimize blocked popups and to give context around the oauth flow
            utils.alert.show({
                title: `Connect with ${channel.title}`,
                message: `You'll be redirected to ${channel.title} to authorize LifeWipe's access to your account. Once authorized, you'll return here to manage your posting preferences. Be sure to have your ${channel.title} username and password ready to go.`,
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'continue') {
                        switch(channel.id) {
                            case 'facebook':
                            runFacebookAuth({ state });
                            break;

                            case 'instagram':
                            runInstagramAuth({ state });
                            break;

                            case 'threads':
                            runThreadsAuth({ state });
                            break;

                            case 'tiktok':
                            runTikTokAuth({ state });
                            break;

                            case 'youtube':
                            runYoutubeAuth({ state });
                            break;
                        }
                        return;
                    }
                }
            });

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue connecting your ${channel.title} account. ${e.message || 'An unknown error occurred'}`,
                origin: 'configure_social_media_accounts.on_social_media_channel_click'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getContent = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Social Media Accounts'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getSocialMediaChannels().map((channel, index, channels) => {
                        let connected = user.social_media[channel.id] ? true : false;
                        return (
                            <AsyncInteractionItem 
                            description={connected ? `Manage your ${channel.title} account integration with LifeWipe and make changes to your automated posting schedule.` : `Connect your ${channel.title} account to automate your scheduled post requirements.`}
                            image={styles => {
                                return (
                                    <img 
                                    src={connected ? user.social_media[channel.id].avatar_url : channel.image}
                                    style={{
                                        ...styles,
                                        ...!connected && {
                                            borderRadius: 0
                                        }
                                    }} />
                                )
                            }}
                            key={index}
                            lastItem={index === channels.length - 1}
                            onClick={onSocialMediaChannelClick.bind(this, channel)}
                            title={connected ? `${channel.title}: ${user.social_media[channel.id].name}` : channel.title}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getSocialMediaChannels = () => {
        return [{
            id: 'facebook',
            image: 'images/facebook-posts-icon.png',
            title: 'Facebook'
        },{
            id: 'instagram',
            image: 'images/instagram-posts-icon.png',
            title: 'Instagram'
        },{
            id: 'threads',
            image: 'images/threads-posts-icon.png',
            title: 'Threads'
        },{
            id: 'tiktok',
            image: 'images/tiktok-posts-icon.png',
            title: 'TikTok'
        },{
            id: 'youtube',
            image: 'images/youtube-posts-icon.png',
            title: 'YouTube'
        }];
    }

    const onSocialMediaAuthorizationComplete = evt => {
        try {

            let { account, channel: channelID } = evt;
            if(!channelID) {
                throw new Error('channel value not provided');
            }

            // declare channel object using supplied id
            let channel = getSocialMediaChannels().find(c => c.id === channelID);
            if(!channel) {
                throw new Error('channel not found');
            }

            // update account for user state
            setUser(user => {
                user.social_media[channel.id] = account;
                return user;
            });

            // notify subscriberes that data has changed
            utils.content.fetch('social_media_users');

            // show confirmation alert with option to configure preferences
            utils.alert.show({
                title: 'Account Connected',
                message: `Your ${channel.title} account has been connected to your LifeWipe profile. Configure your posting preferences to get started.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Configure Preferences',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        utils.layer.open({
                            Component: EditSocialChannelPreferences.bind(this, { channel, id: account.id }),
                            id: `edit_social_channel_preferences_${account.id}`
                        });
                        return;
                    }
                }
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const setupWebSockets = () => {

        // no further logic is required if no user is available
        if(!user) {
            return null;
        }
    
        // setup listeners for authorization completion
        sockets.send('nova', 'join', { user_id: user.user_id });
        sockets.on('nova', 'authorization_complete', onSocialMediaAuthorizationComplete);

        return () => {
            sockets.send('nova', 'leave', { user_id: user.user_id });
            sockets.off('nova', 'authorization_complete', onSocialMediaAuthorizationComplete);
        };
    };

    useEffect(() => {
        let unsubscribe = setupWebSockets();
        return unsubscribe;
    }, [user]);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }} 
        utils={utils}>
            {getContent()}
        </Layer>
    )
}

export const ConfigureTriggers = ({ category, onChange }, { abstract, index, options, utils }) => {
    
    const resource = {
        category: category,
        id: `${category}.${abstract.getID()}`,
        target: abstract.object,
        title: `Configure Your ${category === 'life_checks' ? 'LifeChecks' : 'Emergency Trigger'}`
    };
    const legalAgreementsRef = useRef(null);

    const [edits, setEdits] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [instructions, setInstructions] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { requiem, user, setUser } = useDataStore();
    const [view, setView] = useState(null);

    const onCloseClick = () => {

        // no further logic is required if no edits have been made
        if(hasMadeEdits === false) {
            setLayerState('close');
            requiem.functions.smart_flow.cancel(resource);
            return;
        }

        // show alert requesting confirmation to close the layer without saving changes
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDoneClick, 500);
                    return;
                }

                // close layer and cancel requiem form
                setLayerState('close');
                requiem.functions.smart_flow.cancel(resource);
            }
        });
    }

    const onDeliveryMethodChange = item => {
        return new Promise((resolve, reject) => {

            // no additional logic is required if email was selected
            if(['email', 'sms'].includes(item.id)) {
                resolve();
                return;
            }

            // show alert information the user that other methods are under development
            utils.alert.show({
                title: 'In Development',
                message: 'Additional delivery methods are currently under development and will become available at a later date.',
                onClick: () => {
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onDoneClick = async () => {
        try {

            // prepare list of fields to be validated prior to submitting 
            let fields = getOverviewItems().reduce((array, section) => {
                return array.concat([{
                    key: section.key,
                    items: getFieldItems(section.key).filter(item => item.visible !== false)
                }]);
            }, []);

            // verify that required variables were provided
            setLoading('done');
            await validateRequiredFields(() => fields);

            // verify that all legal agreements have been reviewed and completed
            let legalAgreements = await legalAgreementsRef.current?.getCompletedAgreements() || {};

            // prepare instructions target for request
            let targets = instructions.filter(instruction => edits.trigger_instructions?.includes(instruction.id)).map(instruction => ({
                category: instruction.category,
                name: instruction.name,
                id: instruction.id,
                preferences: instruction.preferences
            }));

            // set loading flag and submit request to server
            let { preferences } = await Request.post(utils, '/subscriptions/', {
                emergency_triggers: category === 'emergency_triggers' && edits,
                id: abstract.getID(),
                legal_agreements: legalAgreements,
                life_checks: category === 'life_checks' && edits,
                trigger_instructions: targets,
                type: 'update'
            });

            // update target and notify subscribers of data change
            abstract.object.preferences = preferences;
            utils.content.update(abstract);

            // update user object as needed based on category
            setUser(user => {

                // update lifechecks configuration flag if applicable
                if(category === 'life_checks') {
                    user.onboarding.has_configured_life_checks = true;
                }

                // update emergency triggers configuration flag if applicable
                if(category === 'emergency_triggers') {
                    user.onboarding.has_configured_emergency_trigger = true;
                }

                // set updated preferences object on user subscription
                user.subscription.preferences = preferences;

                // update legal agreements as needed
                Object.entries(legalAgreements).forEach(([document_id, agreement]) => {
                    user.legal_agreements[document_id] = agreement;
                });

                return user;
            });

            // update user object with agreements data
            setUser(user => {
                Object.entries(legalAgreements).forEach(([document_id, agreement]) => {
                    user.legal_agreements[document_id] = agreement;
                });
                return user;
            });

            // notify requiem that a resource interaction has been completed
            requiem.functions.smart_flow.complete(resource);

            // end loading and show confirmation alert
            setLoading(false)
            utils.banner.show({
                title: `${getTitle()} Updated`,
                message: `The changes to your ${getTitle()} configuration have been saved.`,
                onShow: setLayerState.bind(this, 'close')
            });
            
            // notify subscribers of data change and close layer
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(preferences);
            }

        } catch(e) {

            // set loading flag and determine if a conflict arose
            setLoading(false);
            if(e.code === Request.status.get().subscription_update_rejected) {
                return processInstructionRejection({ 
                    abstract, 
                    error: e, 
                    utils 
                });
            }

            // fallback to showing general error message
            utils.alert.error({
                error: e,
                message: `There was an issue updating your ${getTitle()} configuration. ${e.message || 'An unknown error occurred'}`,
                origin: 'configure_triggers.on_done_click'
            });
        }
    }

    const onEditInstructionClick = id => {

        // prepare instruction target using supplied id
        let target = instructions.find(instruction => instruction.id === id);

        // show editing layer for instruction preferences
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { 
                configuring: true,
                isNewTarget: false,
                onChange: result => {

                    // set flag indicating that at least one edit has been made
                    setHasMadeEdits(true);

                    // update local state for list of available instructions
                    setInstructions(instructions => {
                        return update(instructions, {
                            $apply: instructions => instructions.map(instruction => {
                                if(instruction.id === id) {
                                    instruction.category = result.category;
                                    instruction.name = result.name;
                                    instruction.preferences = result.preferences;
                                }
                                return instruction;
                            }).sort((a,b) => {
                                return a.name.localeCompare(b.name);
                            })
                        });
                    });
                }
            }),
            id: `trigger_instructions.${id}`
        });
    }

    const onExecutionHourChange = hour => {
        onUpdateTarget({ execution_hour_utc: hour && parseInt(moment(hour, 'H').utc().format('H')) });
    }

    const onGenerateNewMFAKey = async () => {
        try {

            // generate a single use token for credential
            let key = Utils.randomString();

            // request credential creation from authentication api
            let credential = await navigator.credentials.create({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rp: { id: window.location.hostname, name: "LifeWipe Inc" },
                    user: {
                        displayName: user.full_name,
                        id: Uint8Array.from(key, c => c.charCodeAt(0)),
                        name: `${getTitle()} Multi Factor Authentication`
                    },
                    pubKeyCredParams: [{ 
                        alg: -7,
                        type: 'public-key'
                    }]
                }
            });

            // update edits with mfa passkey 
            onUpdateTarget({ 
                authentication: {
                    ...edits.authentication,
                    mfa_passkey: credential.id
                }
            });

        } catch(e) {
            if(e.message.includes('user denied')) {
                return;
            }
            utils.alert.error({
                error: e,
                message: `There was an issue generating your PassKey. ${e.message || 'An unknown error occurred'}`,
                origin: 'configure_triggers.on_generate_new_mfa_key'
            });
        }
    }

    const onMFAKeyFieldClick = async () => {
        utils.alert.show({
            title: 'Multi Factor Authentication Key',
            message: `Your multi factor authentication key is an encryption key used to verify your identity when ${category === 'life_checks' ? 'completing a LifeCheck' : 'initiating an Emergency Trigger'}. Would you like to generate a new key or use an existing key?`,
            buttons: [{
                key: 'new',
                title: 'Generate New Key',
                style: 'default'
            },{
                key: 'existing',
                title: 'Use Existing Key',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(key === 'new') {
                    onGenerateNewMFAKey();
                    return;
                }
                if(key === 'existing') {
                    onUseExistingMFAKey();
                    return;
                }
            }
        });
    }

    const onNewInstructionClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Instruction.new(),
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { 
                isNewTarget: true,
                onChange: instruction => {

                    // set flag indicating that at least one edit has been made
                    setHasMadeEdits(true);

                    // add instruction to list of available instructions
                    setInstructions(instructions => {
                        return update(instructions, {
                            $push: [instruction],
                            $apply: instructions => instructions.sort((a,b) => a.name.localeCompare(b.name))
                        });
                    });

                    // add new instruction to list of selected instructions
                    setEdits(edits => {
                        if(!edits.trigger_instructions) {
                            edits.trigger_instructions = [];
                        }
                        return update(edits, {
                            trigger_instructions: {
                                $push: [instruction.id]
                            }
                        });
                    });
                }
            }),
            id: 'new_trigger_instruction'
        });
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(prev => ({ ...prev, ...props }));
    }

    const onUseExistingMFAKey = async () => {
        try {

            // request credential creation from authentication api
            let credential = await navigator.credentials.get({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rpId: window.location.hostname,
                    userVerification: 'required'
                }
            });

            // update edits with mfa passkey 
            onUpdateTarget({ 
                authentication: {
                    ...edits.authentication,
                    mfa_passkey: credential.id
                }
            });

        } catch(e) {
            if(e.message.includes('user denied')) {
                return;
            }
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your PassKey. ${e.message || 'An unknown error occurred'}`,
                origin: 'configure_triggers.on_use_existing_mfa_key'
            });
        }
    }

    const onVerificationWindowChangeAsync = duration => {
        return new Promise((resolve, reject) => {

            // require at least one hour if no duration was provided
            if(duration === 0) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `To ensure integrity and avoid false alarms, LifeWipe requires at least a 1-hour window to verify and respond to LifeCheck alerts. This buffer allows us to confirm your status before triggering any irreversible actions`,
                    onClick: () => {

                        // set the verification window to the min allowed duration
                        onUpdateTarget({ verification_window_hours: 1 });

                        // throw and error for the parent
                        let error = new Error('invalid_selection');
                        reject(error);
                    }
                });
                return;
            }

            // determine if verification window exceeds the schedule for the life check
            let max = false;
            switch(edits.schedule) {
                case Subscription.schedules.get().daily:
                max = 24;
                break;

                case Subscription.schedules.get().weekly:
                max = 168;
                break;

                case Subscription.schedules.get().bi_weekly:
                max = 336;
                break;

                case Subscription.schedules.get().monthly:
                max = 720;
                break;

                case Subscription.schedules.get().quarterly:
                max = 2160;
                break;

                case Subscription.schedules.get().annually:
                max = 8760;
                break;
            }

            // determine if the configuration is valid
            if(duration <= max) {
                resolve();
                return;
            }

            // show an error message since the requested configuration is invalid
            utils.alert.show({
                title: 'Just a Second',
                message: `Your LifeCheck verification window can never exceed the frequency you've set for check-ins. If you've scheduled a LifeCheck every 7 days, your verification window must be 7 days or less. You can't delay your response beyond your expected check-in cycle — otherwise, the system can't trust the gap`,
                onClick: () => {

                    // set the verification window to the max allowed duration
                    onUpdateTarget({ verification_window_hours: max });

                    // throw and error for the parent
                    let error = new Error('invalid_selection');
                    reject(error);
                }
            });
        });
    }

    const getAuthenticationItems = () => {
        return [{
            id: 'mfa_passkey',
            title: 'Multi Factor Authentication PassKey'
        },{
            id: 'secret_phrase',
            title: 'Secret Passcode or Phrase'
        },{
            id: 'two_factor_sms',
            title: 'Two Factor Authentication SMS'
        }];
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            visible: hasMadeEdits || requiresRequiemReview(),
            text: requiresRequiemReview() ? 'Approve' : 'Save Changes'
        }];
    }

    const getContactBehaviorItems = () => {
        return [{
            id: 'proceed',
            title: 'Proceed with Instructions'
        },{
            id: 'hold',
            title: 'Hold and Wait for Review'
        },{
            id: 'cancel',
            title: 'Cancel Instructions'
        }];
    }

    const getDeliveryMethodItems = () => {
        return [{
            id: 'email',
            title: 'Email'
        },{
            id: 'push_notification',
            title: 'Push Notification'
        },{
            id: 'sms',
            title: 'Text Message'
        }];
    }
    
    const getFieldItems = filter => {

        // prepare list items
        let authenticationItems = getAuthenticationItems();
        let contactBehaviorItems = getContactBehaviorItems();
        let deliveryMethodItems = getDeliveryMethodItems();
        let instructionItems = getInstructionItems();
        let scheduleItems = getScheduelItems();

        // determine with fields to generate using the supplied filter
        switch(filter) {
            case 'what_is_it':
            return [{
                component: 'bool_list',
                description: category === 'emergency_triggers' ? 'When enabled, Emergency Trigger support for immediate instruction activation will be available.' : 'When enabled, LifeChecks will be sent to you at the frequency you specify to confirm you are alive and in control of your digital assets.',
                key: 'enabled',
                onChange: val => onUpdateTarget({ enabled: val }),
                requiemSmartFlow: true,
                title: category === 'emergency_triggers' ? 'Allow Emergency Triggers' : 'Send LifeChecks',
                value: edits.enabled
            }];

            case 'how_it_works':
            return [{
                component: 'list',
                description: 'This is how you will receive your LifeCheck notifications when they are sent.',
                key: 'delivery_method',
                items: deliveryMethodItems,
                onChange: item => onUpdateTarget({ delivery_method: item?.id }),
                props: {
                    disabled: canEditDeliveryMethod() ? false : true,
                    onAsyncChange: onDeliveryMethodChange
                },
                requiemSmartFlow: true,
                required: canEditDeliveryMethod(),
                title: 'Delivery Method',
                value: deliveryMethodItems.find(item => item.id === edits.delivery_method)?.title || 'Email'
            },{
                component: 'list',
                description: 'This is how often you will receive LifeChecks to confirm you are alive and in control.',
                key: 'schedule',
                items: scheduleItems,
                onChange: item => onUpdateTarget({ schedule: item?.id }),
                requiemSmartFlow: true,
                title: 'Frequency',
                value: scheduleItems.find(item => item.id === edits.schedule)?.title
            },{
                component: 'hour_picker',
                description: 'This is the time of day you will receive your LifeCheck notifications, based on your local time zone.',
                key: 'execution_hour_utc',
                onChange: onExecutionHourChange,
                props: {
                    disabled: canEditExecutionHoursUTC() ? false : true
                },
                requiemSmartFlow: true,
                required: canEditExecutionHoursUTC(),
                title: 'Time of Day',
                value: edits.execution_hour_utc && moment.utc(edits.execution_hour_utc, 'H').local().format('H')
            },{
                component: 'duration_picker',
                description: 'This is the amount of time you have to respond to a LifeCheck notification before your emergency protocols are activated.',
                key: 'verification_window_hours',
                onChange: duration => onUpdateTarget({ verification_window_hours: duration }),
                props: {
                    disabled: canEditVerificationWindow() ? false : true,
                    onAsyncChange: onVerificationWindowChangeAsync
                },
                requiemSmartFlow: true,
                required: canEditVerificationWindow(),
                title: 'Grace Period (Confirmation Window)',
                value: edits.verification_window_hours
            }];

            case 'why_security_matters':
            return [{
                component: 'list',
                description: category === 'emergency_triggers' ? 'This is how you will verify your identity when initiating an Emergency Trigger to ensure only you can activate your protocols.' : 'This is how you will verify your identity when completing a LifeCheck to ensure only you can confirm your status.',
                key: 'authentication_method',
                items: authenticationItems,
                onChange: item => {
                    onUpdateTarget({ 
                        authentication: {
                            method: item?.id,
                            ...item?.id === 'two_factor_sms' && {
                                two_factor_sms_phone_number: user.phone_number
                            }
                        }
                    });
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Authentication Method',
                value: authenticationItems.find(item => item.id === edits.authentication?.method)?.title
            },{
                component: 'textfield',
                description: category === 'emergency_triggers' ? 'This is the multi factor authentication PassKey you will use to verify your identity when initiating an Emergency Trigger.' : 'This is the multi factor authentication PassKey you will use to verify your identity when completing a LifeCheck.',
                key: 'mfa_passkey',
                onClick: onMFAKeyFieldClick,
                props: {
                    isSecure: true,
                    placeholder: 'Click to configure a PassKey'
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Multi Factor Authentication PassKey',
                value: edits.authentication?.mfa_passkey,
                visible: edits.authentication?.method === 'mfa_passkey'
            },{
                component: 'textfield',
                description: category === 'emergency_triggers' ? 'This is the secret passcode or phrase you will use to verify your identity when initiating an Emergency Trigger.' : 'This is the secret passcode or phrase you will use to verify your identity when completing a LifeCheck.',
                key: 'secret_phrase_value',
                onChange: text => {
                    onUpdateTarget({ 
                        authentication: {
                            ...edits.authentication,
                            secret_phrase_value: text
                        }
                    });
                },
                props: { 
                    isSecure: true
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Secret Passcode or Phrase',
                value: edits.authentication?.secret_phrase_value,
                visible: edits.authentication?.method === 'secret_phrase'
            },{
                component: 'textfield',
                description: category === 'emergency_triggers' ? 'This is the phone number where you will receive your two factor authentication codes when initiating an Emergency Trigger.' : 'This is the phone number where you will receive your two factor authentication codes when completing a LifeCheck.',
                key: 'two_factor_sms_phone_number',
                onChange: text => {
                    onUpdateTarget({ 
                        authentication: {
                            ...edits.authentication,
                            two_factor_sms_phone_number: text
                        }
                    });
                },
                props: {
                    format: 'phone_number'
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'Two Factor Authentication SMS Phone Number',
                value: edits.authentication?.two_factor_sms_phone_number,
                visible: edits.authentication?.method === 'two_factor_sms'
            }];

            case 'trusted_contacts':
            return [{
                component: 'trusted_contacts_list',
                description: 'Trusted Contacts are individuals you designate who can help manage your digital legacy in emergencies. They can verify your status and optionally initiate emergency protocols on your behalf.',
                key: 'trusted_contacts',
                onChange: items => onUpdateTarget({ trusted_contacts: items?.map(item => item.id) }),
                requiemSmartFlow: true,
                required: false,
                title: 'My Trusted Contacts',
                value: edits.trusted_contacts
            },{
                component: 'duration_picker',
                description: `When a trigger is activated, we alert your Trusted Contacts to verify your status. This setting defines how long we wait for them to respond before taking action. Think of it as your countdown clock. If no one replies in time, we fall back on your 'No Response Behavior' preference. We recommend 24-72 hours for most users.`,
                key: 'trusted_contacts_verification_window_hours',
                onChange: duration => onUpdateTarget({ trusted_contacts_verification_window_hours: duration }),
                requiemSmartFlow: true,
                required: edits.trusted_contacts?.length > 0,
                title: 'Response Window (Hours)',
                value: edits.trusted_contacts_verification_window_hours || 24
            },{
                component: 'list',
                description: `If your Trusted Contacts don't respond within the time window you’ve set, this determines what happens next. You can tell us to proceed with your trigger instructions, cancel everything, or hold and wait for further review. It’s your call.`,
                items: contactBehaviorItems,
                key: 'trusted_contacts_behavior',
                onChange: item => onUpdateTarget({ trusted_contacts_behavior: item?.id }),
                requiemSmartFlow: true,
                required: edits.trusted_contacts?.length > 0,
                title: 'No Response Behavior',
                value: contactBehaviorItems.find(item => item.id === edits.trusted_contacts_behavior)?.title
            }];

            case 'what_are_instructions':
            return [{
                component: 'multiple_list',
                description: category === 'emergency_triggers' ? 'These are the specific actions that will be executed when an Emergency Trigger is activated.' : 'These are the specific actions that will be executed if a LifeCheck goes unanswered.',
                key: 'trigger_instructions',
                items: instructionItems,
                onChange: items => onUpdateTarget({ trigger_instructions: items.map(item => item.id) }),
                props: {
                    onAddNew: onNewInstructionClick,
                    onRenderResult: (item, index, items, onRemoveItem) => {
                        return (
                            <div
                            className={`view-entry ${window.theme}`}
                            key={index}
                            onClick={onEditInstructionClick.bind(this, item.id)}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== items.length - 1 ? `1px solid ${Appearance.colors.divider()}` : null,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                padding: '8px 12px 8px 12px'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.key(),
                                    minWidth: null,
                                    whiteSpace: 'normal'
                                }}>{item.title}</span>
                                <img
                                className={'text-button'}
                                onClick={onRemoveItem.bind(this, index)}
                                src={`images/delete-red-icon.png`}
                                style={{
                                    height: 20,
                                    marginLeft: 8,
                                    objectFit: 'contain',
                                    width: 20
                                }} />
                            </div>
                        )
                    }
                },
                requiemSmartFlow: true,
                required: edits.enabled === true,
                title: 'My Instructions',
                value: instructionItems.filter(item => edits.trigger_instructions?.includes(item.id))
            }];
            
            default:
            return [];
        }
    }

    const getInstructionItems = () => {
        return instructions.map(instruction => ({
            id: instruction.id,
            title: instruction.name
        }));
    }

    const getLegalAgreements = () => {

        // prepare documents based on trigger type
        let documents = category === 'life_checks' ? ['indemnity_and_waiver_agreement'] : ['emergency_trigger_authorization_agreement','indemnity_and_waiver_agreement'];

        // determine if at least one of the required documents is not completed
        let editable = user && documents.find(id => user.legal_agreements[id] ? false : true);

        return edits.enabled === true && (
            <LegalAgreements
            documentIDs={documents}
            editable={editable ? true : false}
            ref={legalAgreementsRef}
            showDescription={true}
            utils={utils} 
            value={user.legal_agreements} />
        )
    }

    const getOverviewComponents = () => {

        // prepare sections for selected category
        let sections = getOverviewItems().map(section => ({
            ...section,
            collapsed: false,
            items: getFieldItems(section.key),
            lastItem: false
        }));

        return (
            <AltFieldMapper
            fields={sections}
            resourceID={resource.id}
            utils={utils} />
        )
    }

    const getOverviewItems = () => {
        if(category === 'emergency_triggers') {
            return [{
                description: `An Emergency Trigger is LifeWipe's digital panic button—built for moments when your safety, privacy, or identity is under threat. It lets you instantly execute a secure, pre-set plan to wipe devices, lock accounts, alert contacts, and more. Designed for real-world emergencies, not just end-of-life scenarios.`,
                key: 'what_is_it',
                title: 'What is an Emergency Trigger?'
            },{
                description: `Emergency Triggers are powerful by design—but dangerous if misused. That's why every trigger is protected by advanced verification layers to confirm it's really you. Multi-factor checks, biometric options, and two factor verification codes help prevent false alarms, unauthorized wipes, or identity abuse.`,
                key: 'why_security_matters',
                title: 'Why Security Matters',
                visible: edits.enabled === true
            },{
                description: `Trusted Contacts are your digital first responders. If you activate an Emergency Trigger, they're the ones we quietly alert. They can confirm you're okay, delay a trigger, or—in true emergencies—authorize your final instructions. You control how long we wait for them, and what happens if they stay silent. Think of them as your human backup plan. Handpicked. Prepped. Empowered to act—or let the system act—if you can’t.`,
                key: 'trusted_contacts',
                title: 'Trusted Contacts',
                visible: edits.enabled === true
            },{
                description: `Emergency Trigger Instructions are your custom command list for digital lockdown. You choose what happens—what gets wiped, who gets notified, and how your accounts respond—if danger strikes. Whether it's deleting files or transferring control, your instructions make sure your digital defenses launch instantly, no questions asked.`,
                key: 'what_are_instructions',
                title: 'What Are Instructions?',
                visible: edits.enabled === true
            }];
        }
        return [{
            description: `A LifeCheck is LifeWipe's quiet pulse-check on your status — a secure, periodic alert system designed to confirm that you're alive and in control of your digital legacy. Think of it like a dead man's switch, but smarter and more compassionate.`,
            key: 'what_is_it',
            title: 'What is a LifeCheck?'
        },{
            description: `At regular intervals (customizable with some plans), LifeWipe sends you a LifeCheck prompt — a secure notification via email, SMS, or push notification guarded using your preferred authentication method. You simply confirm with a tap or quick response. If no response is received after a grace period, LifeWipe begins executing your pre-set instructions.`,
            key: 'how_it_works',
            title: 'How It Works',
            visible: edits.enabled === true
        },{
            description: `Your LifeCheck is more than just a "check-in." It's the heartbeat of your digital legacy. Adding additional layers of security ensures that only you—not a hacker, impersonator, or AI clone—can confirm you're still in control. It's how we prevent false triggers, unauthorized wipes, and identity hijacks.`,
            key: 'why_security_matters',
            title: 'Why Security Matters',
            visible: edits.enabled === true
        },{
            description: `Trusted Contacts are your digital first responders. If you stop responding to LifeChecks, they're the ones we quietly alert. They can confirm you're okay, delay a trigger, or—in true emergencies—authorize your final instructions. You control how long we wait for them, and what happens if they stay silent. Think of them as your human backup plan. Handpicked. Prepped. Empowered to act—or let the system act—if you can’t.`,
            key: 'trusted_contacts',
            title: 'Trusted Contacts',
            visible: edits.enabled === true
        },{
            description: `LifeCheck instructions are your personal "if-this-then-that" rules for the end of your digital life. You decide what should happen—and to whom—if you stop responding to LifeChecks. Want your email accounts locked down? Social profiles erased? Or a final message sent to a loved one? LifeCheck instructions make sure your digital footprint follows your rules—even when you're not around to enforce them.`,
            key: 'what_are_instructions',
            title: 'What Are Instructions?',
            visible: edits.enabled === true
        }];
    }

    const getRequiemReviewBanner = () => {
        return requiresRequiemReview() === true && {
            description: `Your ${category === 'life_checks' ? 'LifeChecks were' : 'Emergency Trigger was'} setup with help from Requiem, your digital assistant. Before ${category === 'life_checks' ? 'they can be activated' : 'the trigger can be used'}, we ask that you review and confirm the setup below. Click here to approve the ${category === 'life_checks' ? 'LifeChecks' : 'Emergency Trigger'} setup.`,
            onClick: onDoneClick
        }
    }

    const getScheduelItems = () => {
        let schedules = Subscription.schedules.get();
        return Object.values(schedules).map(code => ({
            id: code,
            title: Subscription.schedules.toText(code)
        })).filter(item => {
            if(abstract.object.tier.preferences.benefits.life_checks.schedule) {
                return item.id >= abstract.object.tier.preferences.benefits.life_checks.schedule;
            }
            return true;
        });
    }

    const getSmartFlowFields = () => {
        return getOverviewItems().map(section => ({
            ...section,
            collapsed: false,
            items: getFieldItems(section.key),
            lastItem: false
        }));
    }

    const getTitle = () => {
        return category === 'life_checks' ? 'LifeCheck' : 'Emergency Trigger';
    }

    const canEditDeliveryMethod = () => {
        return abstract.object.tier.preferences.benefits.life_checks.delivery_method ? false : true;
    }

    const canEditVerificationWindow = () => {
        return abstract.object.tier.preferences.benefits.life_checks.verification_window ? false : true;
    }

    const canEditExecutionHoursUTC = () => {
        return abstract.object.tier.preferences.benefits.life_checks.execution_hours_utc ? false : true;
    }

    const fetchInstructions = async () => {
        try {
            setLoading(true);
            let { trigger_instructions } = await Request.get(utils, '/users/', {
                type: 'trigger_instructions'
            });

            setLoading(false);
            setInstructions(trigger_instructions);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of instructions. ${e.message || 'An unknown error occurred'}`,
                origin: 'complete_life_check.fetch_instructions'
            });
        }
    }

    const requiresRequiemReview = () => {
        return edits.metadata?.requiem_generated === true && edits.metadata?.requiem_confirmation_date === null ? true : false;
    }
    
    const setupEditing = () => {
        setEdits({
            authentication: {},
            ...abstract.object.preferences[category]
        });
    }

    useEffect(() => {
        setupEditing();
        fetchInstructions();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium',
            support: {
                description: `Need help? Watch our quick video tutorial or activate Requiem AI for real-time guidance as you setup your ${category === 'life_checks' ? 'LifeChecks' : 'Emergency Trigger'}. Use ${category === 'life_checks' ? 'LifeChecks' : 'the Emergency Trigger'} to configure fast-response actions that activate if you're in danger or unable to respond.`,
                requiem: {
                    enabled: true,
                    smart_flow: {
                        items: getSmartFlowFields(),
                        ...resource
                    } 
                },
                title: category === 'life_checks' ? 'LifeChecks' : 'Emergency Trigger',
                videoUrl: 'https://www.youtube.com/embed/hFrgLg0X_Yk?si=1aMLStJu8rnFzTUM'
            },
            warnings: {
                requiem_review_required: getRequiemReviewBanner()
            }
        }}
        utils={utils}>
            {getOverviewComponents()}
            {getLegalAgreements()}
        </Layer>
    )
}

export const DigitalLifeCheckup = ({ index, options, utils }) => {

    const layerID = 'digital_life_checkup';
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [outcome, setOutcome] = useState(null);
    const { referredByUserCode, user } = useDataStore();
    const [values, setValues] = useState({});

    const onCheckboxValueChange = (key, index) => {
        setValues(values => {
            return update(values, {
                [key]: {
                    $set: index
                }
            })
        });
    }

    const getScoredResult = score => {  
        if(score >= 24) {
            return {
                description: `Your digital life is wide open in a crisis. You're not alone—most people haven't thought this through. But LifeWipe exists to give you an airtight plan. Start your free trial and lock the doors in 5 minutes.`,
                image: 'images/checkup-quiz-unlocked-door.png',
                title: 'The Unlocked Door'
            }
        }
        if(score >= 16) {
            return {
                description: `You've got the right instincts—but your plan breaks down at access. In a real emergency, your loved ones might be locked out of your phone, passwords, cloud, and more. LifeWipe bridges the gap between good intentions and real-world access.`,
                image: 'images/checkup-quiz-half-prepared.png',
                title: 'The Half-Prepared'
            }
        }
        if(score >= 8) {
            return {
                description: `You've taken steps—but you might be trusting systems that only work while you're alive and logged in. LifeWipe ensures your digital life isn't a dead end when something goes wrong.`,
                image: 'images/checkup-quiz-overconfident.png',
                title: 'The Overconfident'
            }
        }
        return {
            description: `You’ve done the work — mapped your risks, faced the digital reality, and taken the first step. Whether you’re just getting organized or already managing your digital footprint, LifeWipe is here when you’re ready to automate, delegate, or level up.`,
            image: 'images/checkup-quiz-digital-monk.png',
            title: 'The Digital Monk'
        }
    }

    const onSubmitClick = async () => {
        try {

            // verify that user details have been provided if applicable
            if(!user) {
                if(!edits.first_name || !edits.last_name || !edits.email_address || !edits.phone_number) {
                    utils.alert.show({
                        title: 'Just a Second',
                        message: `To ensure your results are personalized and secure, please provide your first name, last name, email address, and phone number. This information is used to protect your privacy and ensure that your insights are tailored to you.`
                    });
                    return;
                }
            }

            // caculate the risk score using the selected values
            let questions = getQuestions();
            let score = Object.entries(values).reduce((total, [key, value]) => {
                let selection = questions.find(question => question.key === key)?.options[value];
                switch(selection?.value) {
                    case 'low':
                    return total + 1;

                    case 'med':
                    return total + 2;
                    
                    case 'high':
                    return total + 3;

                    default:
                    return total;
                }
            }, 0);

            // prepare values map for server 
            let map = Object.entries(values).reduce((object, [key, value]) => {
                object[key] = questions.find(question => question.key === key)?.options[value]?.label;
                return object;
            }, {});

            // submit request to server to log results of quiz
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                score: score,
                type: 'new_digital_checkup_quiz',
                values: map
            }); 

            // prepare content result using score
            let result = getScoredResult(score);
            setOutcome(result);
            setLoading(false);
            
        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your checkup quiz. ${e.message || 'An unknown error occurred'}`,
                origin: 'digital_checkup_quiz.on_submit_click'  
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const onViewAllPlans = () => {
        setLayerState('close');
        utils.navigation.set({ view: 'pricing'})
    }

    const getButtons = () => {
        if(outcome) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            },{
                color: 'primary',
                id: 'view_plans',
                onClick: onViewAllPlans,
                text: 'View All Plans',
                visible: user?.subscription ? false : true
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getContactFields = () => {

        // prepare list of origin items 
        let originItems = [{
            id: 'digital_management_learning_session',
            title: 'Digital Management Learning Session'
        },{
            id: 'friend_or_family',
            title: 'Friend or Family Member'
        },{
            id: 'news_article_or_ad',
            title: 'News Article or Ad'
        },{
            id: 'search_engine',
            title: 'Search Engine'
        },{
            id: 'social_media',
            title: 'Social Media'
        },{
            id: 'other',            
            title: 'Other'
        }];

        // prepare items for alt field mapper
        let fields = [{
            component: 'textfield',
            key: 'first_name',
            onChange: text => onUpdateTarget({ first_name: text }),
            title: 'First Name',
            value: edits.first_name
        },{
            component: 'textfield',
            key: 'last_name',
            onChange: text => onUpdateTarget({ last_name: text }),
            title: 'Last Name',
            value: edits.last_name
        },{
            component: 'textfield',
            key: 'email_address',
            onChange: text => onUpdateTarget({ email_address: text }),
            title: 'Email Address',
            value: edits.email_address
        },{
            component: 'textfield',
            key: 'phone_number',
            onChange: text => onUpdateTarget({ phone_number: text }),
            props: {
                format: 'phone_number'
            },
            title: 'Phone Number',
            value: edits.phone_number
        },{
            component: 'textfield',
            key: 'referral_code',
            onChange: text => onUpdateTarget({ referral_code: text }),
            required: false,
            title: 'Referred by Someone? (Enter Code)',
            value: edits.referral_code
        },{
            component: 'list',
            items: originItems,
            key: 'origin',
            onChange: item => onUpdateTarget({ origin: item?.id }),
            title: 'How Did You Hear About Us?',
            value: edits.origin && originItems.find(item => item.id === edits.origin)?.title,
        }];

        return !user && (
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Almost there'}>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    display: 'block',
                    marginBottom: 12
                }}>{` We generate personalized insights and risk triggers just for you. To lock in your results and protect your privacy, enter your details below.`}</span>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: 16,
                    width: '100%'
                }}>
                    {fields.map((field, index) => {
                        return (
                            <AltFieldItem
                            item={field}
                            key={index}
                            lastItem={index === fields.length - 1}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getContent = () => {
        if(outcome) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    width: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.divider(),
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        marginBottom: 24, 
                        padding: Utils.isMobile() ? 36 : 72,
                        width: '100%'
                    }}>
                        <img
                        className={'float'}
                        src={outcome.image}
                        style={{
                            height: 150,
                            objectFit: 'contain',
                            width: 150
                        }} />
                    </div>
                    <span style={{
                        ...Appearance.textStyles.heavyHeader(),
                        fontFamily: 'Zen Dots',
                        marginBottom: 12
                    }}>{outcome.title.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{outcome.description}</span>
                </div>
            )
        }
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                {getQuestions().map((question, index, questions) => { 
                    return (
                        <LayerItem
                        collapsed={false}
                        key={index}
                        lastItem={user ? index === questions.length - 1 : false}
                        title={`Question ${index + 1}`}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                display: 'block',
                                marginBottom: 12
                            }}>{question.title}</span>
                            <div style={{
                                ...Appearance.styles.unstyledPanel(),
                                padding: 16,
                                width: '100%'
                            }}>
                                {question.options.map((option, i) => {
                                    return (
                                        <div
                                        key={i}
                                        style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            marginBottom: i === question.options.length - 1 ? 0 : 12,
                                            width: '100%'
                                        }}>
                                            <Checkbox 
                                            checked={values[index] === i}
                                            onChange={onCheckboxValueChange.bind(this, question.key, i)} />
                                            <span 
                                            className={'cursor-pointer'}
                                            onClick={onCheckboxValueChange.bind(this, question.key, i)}
                                            style={{
                                                ...Appearance.textStyles.defaultText(),
                                                marginLeft: 12
                                            }}>{option.label}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </LayerItem>
                    )
                })}
                {getContactFields()}
                {getDisclaimer()}
            </div>
        )
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'flex',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.75,
                marginTop: 24,
                textAlign: 'left',
                width: '100%'
            }}>{`By taking this quiz, you agree to receive occasional follow-ups from LifeWipe. Our goal is to help you understand your digital footprint and offer guidance on how to protect it. No spam — just practical tips, reminders, and support if you’re ready to take the next step. If you were referred to this quiz by a LifeWipe Sales Operative or partner, they may also reach out to help you set up your protection plan and answer any questions you have.`}</span>
        )
    }

    const getQuestions = () => {
        return [{
            key: "personal_device_usage",
            title: "How many digital devices do you personally own?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "1" 
            },{ 
                value: "med", 
                label: "2–3" 
            },{ 
                value: "high", 
                label: "4–5" 
            },{ 
                value: "high", 
                label: "6+" 
            }]
        },{
            key: "personal_device_storage_content",
            title: "Which of these do you store most frequently on your personal devices?",
            type: "checkbox",
            options: [{ 
                value: "high", 
                label: "Passwords" 
            },{ 
                value: "high", 
                label: "Banking apps" 
            },{ 
                value: "high", 
                label: "Private photos or messages" 
            },{ 
                value: "high", 
                label: "Health or legal documents" 
            },{ 
                value: "high", 
                label: "Business or creative work" 
            },{ 
                value: "low", 
                label: "I'm not sure" 
            }]
        },{
            key: "digital_life_protection",
            title: "If something happened to you tonight, who could protect or access your digital life?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "I've prepared someone fully" 
            },{ 
                value: "med", 
                label: "Someone might figure it out" 
            },{ 
                value: "high", 
                label: "No one would know how" 
            },{ 
                value: "high", 
                label: "I've never thought about this" 
            }]
        },{
            key: "personal_items_deletion",
            title: "Are there personal items you'd want deleted in a crisis?",
            type: "radio",
            options: [{ 
                value: "high", 
                label: "Yes" 
            },{ 
                value: "low", 
                label: "No" 
            },{ 
                value: "med", 
                label: "I don't know, but probably" 
            }]
        },{
            key: "shared_digital_accounts",
            title: "If you and your spouse/partner were in a serious accident, who could manage your shared digital accounts?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "A designated person with full access" 
            },{ 
                value: "med", 
                label: "Maybe someone could piece it together" 
            },{ 
                value: "high", 
                label: "No one" 
            },{ 
                value: "med", 
                label: "I'm single" 
            }]
        },{
            key: "trusted_person_access",
            title: "Could your trusted person get past any of the following?",
            type: "checkbox",
            options: [{ 
                value: "high", 
                label: "Two-factor authentication" 
            },{ 
                value: "high", 
                label: "Biometric locks" 
            },{ 
                value: "high", 
                label: "Password managers" 
            },{ 
                value: "high", 
                label: "Encrypted files/messages" 
            },{ 
                value: "high", 
                label: "They would likely be locked out" 
            },{ 
                value: "med", 
                label: "I haven't thought about this" 
            }]
        },{
            key: "lost_phone_access",
            title: "If your phone was lost, could your contact access your accounts?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "Yes, they have alternative access" 
            },{ 
                value: "med", 
                label: "Possibly, but not everything" 
            },{ 
                value: "high", 
                label: "No, they'd be locked out" 
            },{ 
                value: "high", 
                label: "I'm not sure" 
            }]
        },{
            key: "blocked_two_factor_authentication",
            title: "Have you ever been blocked by your own 2FA or backup system?",
            type: "radio",
            options: [{ 
                value: "high", 
                label: "Yes" 
            },{ 
                value: "low", 
                label: "No" 
            },{ 
                value: "med", 
                label: "I don't use 2FA (yet)" 
            }]
        },{
            key: "stolen_digital_life_access",
            title: "If your devices were stolen today, what could be accessed?",
            type: "checkbox",
            options: [{ 
                value: "high", 
                label: "Emails" 
            },{ 
                value: "high", 
                label: "Social media" 
            },{ 
                value: "high", 
                label: "Cloud photos" 
            },{ 
                value: "high", 
                label: "Saved browser passwords" 
            },{ 
                value: "high", 
                label: "Banking or crypto apps" 
            },{ 
                value: "high", 
                label: "Smart home systems" 
            },{ 
                value: "med", 
                label: "Not sure how secure I am" 
            }]
        },{
            key: "home_device_security",
            title: "If someone broke into your home and took your devices?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "Probably nothing—I have safeguards" 
            },{ 
                value: "med", 
                label: "Some things—they'd have to try" 
            },{ 
                value: "high", 
                label: "A lot—they'd have access to my whole life" 
            },{ 
                value: "med", 
                label: "I've never considered that" 
            }]
        },{
            key: "digital_life_peace_of_mind",
            title: "What would give you peace of mind right now?",
            type: "checkbox",
            options: [{ 
                value: "high", 
                label: "A panic button to wipe data remotely" 
            },{ 
                value: "high", 
                label: "A secure vault for passwords/files" 
            },{ 
                value: "high", 
                label: "Messages for loved ones" 
            },{ 
                value: "high", 
                label: "Backup plan for 2FA/biometrics" 
            },{ 
                value: "low", 
                label: "All of the above" 
            }]
        },{
            key: "monthly_digital_subscriptions_cost",
            title: "How much do you pay per month for digital subscriptions?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "Under $20" 
            },{ 
                value: "med", 
                label: "$20–$50" 
            },{ 
                value: "high", 
                label: "$50–$100" 
            },{ 
                value: "high", 
                label: "Over $100" 
            },{ 
                value: "med", 
                label: "I'm not sure" 
            }]
        },{
            key: "digital_life_security_preparedness",
            title: "How secure or prepared do you feel?",
            type: "radio",
            options: [{ 
                value: "low", 
                label: "I feel well-prepared" 
            },{ 
                value: "med", 
                label: "I could use a better system" 
            },{ 
                value: "high", 
                label: "I realize I'm vulnerable" 
            },{ 
                value: "med", 
                label: "I'm not sure, but I want to be protected" 
            }]
        }];
    }

    useEffect(() => {
        setEdits({ referral_code: referredByUserCode });
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getContent()}
        </Layer>
    )
}

export const Devices = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);

    const onDeviceClick = device => {
        /*
        utils.layer.open({
            abstract: Abstract.create({
                object: device,
                type: 'trusted_devices'
            }),
            Component: AddEditTrustedDevice.bind(this, { isNewTarget: false }),
            id: `edit_trusted_device_${device.id}`
        });
        */
    }

    const onNewDeviceClick = () => {
        utils.alert.dev();
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'add_device',
            onClick: onNewDeviceClick,
            text: 'Add New Device'
        }];
    }

    const getDevices = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: 36,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return devices.length > 0 && (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 36,
                width: '100%'
            }}>
                {devices.map((device, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        key={index}
                        onClick={onDeviceClick.bind(this, device)}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== devices.length - 1 && Appearance.styles.bottomBorder(),
                            display: 'flex',
                            flexDirection: 'row',
                            marginBottom: index !== devices.length - 1 ? 24 : 0,
                            minWidth: 0,
                            padding: 16,
                            width: '100%'
                        }}>
                            <img 
                            src={device.image_url || window.theme === 'dark' ? 'images/logo-badge-dark.png' : 'images/logo-badge-light.png'}
                            style={{
                                height: 50,
                                marginRight: 16,
                                objectFit: 'contain',
                                width: 50
                            }} />
                            <div style={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots'
                                }}>{device.name.toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    color: Appearance.colors.subText()
                                }}>{`Created: ${Utils.format.date(device.created)}`}</span>
                            </div>
                            <img
                            src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                            style={{
                                height: 20,
                                marginLeft: 8,
                                objectFit: 'contain',
                                width: 15
                            }} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const fetchDevices = async () => {
        try {
            setLoading(true);
            let { devices, paging } = await Request.get(utils, '/devices/', {
                limit: limit,
                offset: offset.current,
                type: 'list'
            });

            setLoading(false);
            setDevices(devices.map(device => Device.create(device)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your devices list. ${e.message || 'An unknown error occurred'}`,
                origin: 'devices.fetch_devices'
            });
        }
    }

    useEffect(() => {
        //fetchDevices();
        utils.content.subscribe('devices_list', ['devices'], {
            onFetch: fetchDevices
        });
        return () => {
            utils.content.unsubscribe('devices_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={'A managed device refers to a supported device such as a mobile phone, tablet, smartwatch, or computer that has been specifically set up with an end-of-life trigger. When this end-of-life trigger is activated—whether due to time expiration, policy enforcement, or remote command—the device will automatically undergo certain modifications. These modifications could include actions such as wiping data, disabling functionalities, locking access, or even rendering the device completely inoperable, depending on the configuration.'}
                buttons={getContainerButtons()}
                title={'Managed Devices'}>
                    {getDevices()}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const EditSocialChannelPreferences = ({ channel, id }, { index, options, utils }) => {

    const layerID = `edit_social_channel_preferences_${id}`;
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user, setUser } = useDataStore();

    const onDeactivateAccount = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        channel: channel.id,
                        id: id,
                        type: 'deactivate_social_account'
                    });

                    // remove account from list of user social accounts
                    setUser(user => {
                        user.social_media[channel.id] = null;
                        return user;
                    });

                    // notify subscribers that a data fetch is required
                    utils.content.fetch('social_media_content');

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        message: `Your Nova integrations with this ${channel.title} account have been deactivated. You can reactivate this integration by re-authenticating your ${channel.title} account.`,
                        onClick: setLayerState.bind(this, 'close')
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue deactivating your account. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Deactivate Account',
                message: `Are you sure that you want to deactivate Nova integrations for this ${channel.title} account? This will prevent any future posts from being made to this account.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSubmitClick = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('submit');
            let { preferences } = await Request.post(utils, '/users/', {
                channel: channel.id,
                id: id,
                preferences: edits,
                type: 'update_social_account_preferences'
            });

            // end loading and update user target with new preferences
            setLoading(false);
            setUser(user => {
                user.social_media[channel.id].metadata.preferences = preferences;
                return user;
            });

            // notify subscribers that data has changed
            utils.content.fetch('social_media_users');

            // show confirmation alert
            utils.banner.show({
                title: `${channel.title} Account Updated`,
                message: `The changes to your ${channel.title} account preferences have been saved.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue saving your changes. ${e.message || 'An unknown error occurred'}`,
                origin: 'edit_social_channel_preferences.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits({ ...edits, ...props });
    }

    const getApproachItems = () => {
        return [{
            id: 'automated',
            title: 'Automatically Post Content'
        },{
            id: 'require_approval',
            title: 'Require Approval Before Posting'
        }].filter(item => {
            return item.visible !== false;
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            description: `Control how LifeWipe shares content through your ${channel.title} account. Choose between auto-posting or manual approval, set posting limits, and define when posts are allowed to go live.`,
            id: 'permissions',
            title: 'Posting Behavior',
            items: [{
                component: 'list',
                id: 'approach',
                items: getApproachItems(),
                onChange: item => onUpdateTarget({ approach: item?.id }),
                title: 'Approach',
                value: getApproachItems().find(item => item.id === edits.approach)?.title
            },{
                component: 'hour_picker',
                id: 'time_of_day',
                onChange: hour => onUpdateTarget({ time_of_day_utc: hour && parseInt(moment(hour, 'H').utc().format('H')) }),
                required: edits.approach === 'automated',
                title: 'Time of Day',
                value: edits.time_of_day_utc && moment.utc(edits.time_of_day_utc, 'H').local().format('H'),
                visible: edits.approach === 'automated'
            }]
        },{
            collapsed: false,
            description: `Choose when and how you'd like to be alerted about your content. Get notified when new posts are ready, when something's been published, or if there's an issue with your account.`,
            id: 'notifications',
            lastItem: false,
            title: 'Notification Settings',
            items: [{
                component: 'bool_list',
                id: 'notify_new_content_available',
                onChange: val => onUpdateTarget({ notify_new_content_available: val }),
                title: 'New Content Available To Post',
                value: edits.notify_new_content_available
            },{
                component: 'bool_list',
                id: 'notify_content_posted',
                onChange: val => onUpdateTarget({ notify_content_posted: val }),
                title: `Content Posted to ${channel.title} Feed`,
                value: edits.notify_content_posted
            }]
        }];
    }

    const getResourceComponents = () => {
        return (
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={`This deactivates the Nova integrations for your ${channel.title} account and disables future posts.`}
                    image={'images/social-account-deactivate.png'}
                    lastItem={true}
                    onAsyncClick={onDeactivateAccount}
                    title={'Deactivate Account'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const setupEditing = () => {
        setEdits({
            approach: 'require_approval',
            notify_content_posted: true,
            notify_new_content_available: true,
            time_of_day_utc: parseInt(moment(9, 'H').utc().format('H')),
            ...user.social_media[channel.id].metadata.preferences
        });
    }
    
    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}
        utils={utils}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            {getResourceComponents()}
        </Layer>
    )
}

export const EmergencyTrigger = ({ utils }) => {

    const [loading, setLoading] = useState(false);
    const { requiem, user, setRequiem, setUser } = useDataStore();

    const onConfigureEmergencyTrigger = async () => {
        try {
                
            // verify that user has access to this feature
            await utils.user.capabilities.verify('emergency_triggers', 'create');

            // show layer with options to configure triggers
            utils.layer.open({
                abstract: Abstract.create({
                    object: user.subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, {
                    category: 'emergency_triggers',
                    onChange: preferences => {
                        setUser(user => {
                            user.subscription.preferences = preferences;
                            return user;
                        });
                    }
                }),
                id: `emergency_triggers.${user.subscription.id}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onEmergencyTriggerClick = () => {
        user ? onRequestEmergencyTriggerConfirmation() : utils.user.request.login({ shouldRedirectUser: false }, onRequestEmergencyTriggerConfirmation);
    }

    const onRequestBackupKey = (resolve, reject) => {
        let value = null;
        utils.alert.show({
            title: 'Backup Access Key',
            message: 'Please provide your backup access key below and click "Done" to continue.',
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    isSecure={true}
                    onChange={text => value = text}
                    placeholder={'Access Key'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(value && key === 'confirm') {
                    setTimeout(resolve.bind(this, { mfa_passkey: value }), 500);
                    return;
                }
                let error = new Error('user_cancelled');
                reject(error);
            }
        });
    }

    const onRequestEmergencyTriggerConfirmation = () => {
        utils.alert.show({
            title: 'Emergency Trigger',
            message: `This will activate your full emergency protocol—wiping data, locking accounts, and alerting contacts as configured. The action is permanent and cannot be undone. Are you sure you want to continue?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setLoading('emergency_trigger');
                    setTimeout(onSubmitEmergencyTriggerRequest, 500);
                    return;
                }
            }
        });
    }

    const onRequestMFAPassKey = async (resolve, reject) => {
        try {

            // request credentials from web authenticaton api
            let credential = await navigator.credentials.get({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rpId: window.location.hostname,
                    userVerification: 'required'
                }
            });

            // submit verification to server
            setTimeout(resolve.bind(this, { mfa_passkey: credential.id }), 500);

        } catch(e) {
            if(e.message.includes('user denied') === false) {
                reject(e);
                return;
            }
            let error = new Error('user_cancelled');
            reject(error);
        }
    }

    const onStartMFAVerification = (resolve, reject) => {
        utils.alert.show({
            title: 'Authentication Required',
            message: `You've configured a multi factor authentication key as your Emergency Trigger authentication method. Please select a PassKey or provide a backup key to continue.`,
            buttons: [{
                key: 'mfa_passkey',
                title: 'Select a PassKey',
                style: 'default'
            },{
                key: 'backup_key',
                title: 'Provide a Backup Key',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'backup_key') {
                    return setTimeout(onRequestBackupKey.bind(this, resolve, reject), 500);
                }
                if(key === 'mfa_passkey') {
                    return setTimeout(onRequestMFAPassKey.bind(this, resolve, reject), 500);
                }
                let error = new Error('user_cancelled');
                reject(error);
            }
        });
    }

    const onStartPasscodeVerification = (resolve, reject) => {
        
        let result = null;
        utils.alert.show({
            title: 'Authentication Required',
            message: `You've configured a secret passcode or phrase as your Emergency Trigger authentication method. Please input your passcode or phrase below to verify your request.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    isSecure={true}
                    onChange={text => result = text}
                    placeholder={'Secret Passcode or Phrase'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Submit',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(result && key === 'confirm') {
                    setTimeout(resolve.bind(this, { secret_phrase_value: result }), 500);
                    return;
                }
                let error = new Error('user_cancelled');
                reject(error);
            }
        });
    }

    const onStartTwoFactorVerification = async (resolve, reject) => {
        try {

            // set loading flag and submit request to server
            let { phone_number_last_4 } = await Request.get(utils, '/subscriptions/', {
                type: 'new_emergency_trigger_2fa_code'
            });

            // show alert requesting sms code from user
            let result = null;
            utils.alert.show({
                title: 'Authentication Required',
                message: `You've configured your Emergency Trigger to use two-factor authentication for verification. We've sent a one-time code to your phone ending in ${phone_number_last_4}. Please enter the code below to confirm your identity and complete your request.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        isSecure={true}
                        onChange={text => result = text}
                        placeholder={'Two Factor Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(result && key === 'confirm') {
                        setTimeout(resolve.bind(this, { two_factor_sms_code: result }), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
            
        } catch(e) {
            reject(e);
        }
    }

    const onSubmitEmergencyTriggerRequest = async () => {
        try {
            
            // request authentication credentials from user
            let result = await getAuthenticationCredentials();
            
            // submit request to server
            let { status } = await Request.post(utils, '/users/', {
                ...result,
                type: 'new_emergency_trigger'
            });

            // update user status to indicate that account is in emergency trigger mode
            setUser(user => {
                user.status = status;
                return user;
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `Your Emergency Trigger has been initiated and your configured instructions are being executed. Some actions—like data deletion, account lockdowns, and alerts to Trusted Contacts—may already be in progress.`
            });

        } catch(e) {
            setLoading(false);
            if(e.code === Request.status.get().draft_mode_failure) {
                // TODO => show layer indicating that emergency trigger cannot be activated in draft mode
                /*
                header: Activate your plan to enable this emergency trigger
                body: You’ve set up what should happen in an emergency. Right now, your plan is in draft mode. Draft plans let you prepare and configure instructions, but emergency triggers do not run automatically. Activating your plan allows LifeWipe to monitor and execute this trigger based on the rules you’ve defined. Plans remain inactive until activated.
                buttons: Activate plan, Activate plan
                */
            }
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue submitting your emergency trigger request. ${e.message || 'An unknown error occurred'}`,
                    origin: 'emergency_trigger.on_submit_emergency_trigger_request',
                });
            }
        }
    }

    const getAuthenticationCredentials = () => {
        return new Promise((resolve, reject) => {

            // determine which authentication logic to run based on the configured method
            switch(user.subscription?.preferences.emergency_triggers.authentication?.method) {
                case 'secret_phrase':
                return onStartPasscodeVerification(resolve, reject);

                case 'two_factor_sms':
                return onStartTwoFactorVerification(resolve, reject);

                case 'mfa_passkey':
                return onStartMFAVerification(resolve, reject);

                default:
                let error = new Error('Unknown or invalid authentication method found with emergency triggers configuration');
                reject(error);
            }
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'dark',
            id: 'configure',
            onClick: onConfigureEmergencyTrigger,
            text: 'Configure Trigger',
            visible: user?.subscription ? true : false
        },{
            color: 'danger',
            id: 'emergency_trigger',
            loading: loading === 'emergency_trigger',
            onClick: onEmergencyTriggerClick,
            text: 'Trigger Full Lockdown'
        }];
    }

    const getLineItems = platform => {

        let ios = [
            'Tap the Share icon (bottom of screen - square with an arrow).',
            'Scroll down and select "Add to Home Screen".',
            'Tap "Add" in the top-right corner.',
            'The Emergency Trigger icon will now appear on your Home Screen.'
        ];

        let android = [
            'Tap the three-dot menu (top-right corner).',
            'Tap "Add to Home screen" or "Install App".',
            'Confirm by tapping "Add".',
            'You\'ll now have a shortcut to trigger emergency actions instantly.'
        ];

        return (
            <div className={'col-12'}>
                <LayerItem
                collapsed={null} 
                title={platform === 'ios' ? '📱 iPhone (iOS - Safari)' : '🤖 Android (Chrome)'}>
                    {(platform === 'ios' ? ios : android).map((item, index) => {
                        return (
                            <span 
                            key={index}
                            style={{
                                ...Appearance.textStyles.defaultText(),
                                display: 'block',
                                padding: 8,
                                textAlign: 'left', 
                                width: '100%'
                            }}>{`• ${item}`}</span>
                        )
                    })}
                </LayerItem>
            </div>
        ); 
    }

    const onProcessRequiemNavigationRequest = () => {
    
        // no further action is needed if there is no target match
        if(requiem.action?.target !== 'emergency_trigger') {
            return;
        }

        // determine next steps for the action request
        switch(requiem.action?.key) {
            case 'configure_emergency_trigger':
            onConfigureEmergencyTrigger();
            break;
        }

        // reset requiem object for next request
        setRequiem({ ...requiem, action: null });
    }

    const onProcessRequiemReviewRequest = async () => {
        try {

            // no further action is needed if there is no target match
            if(requiem.review?.category !== 'emergency_trigger') {
                return;
            }

            // fetch details for subscription from server and update user object with changes
            let subscription = await Subscription.get(utils, requiem.review.id);
            setUser(user => {
                user.onboarding.has_subscription = true;
                user.subscription = subscription;
                return user;
            });

            // open configuration for emergency trigger after a brief pause while the state updates
            setTimeout(onConfigureEmergencyTrigger, 500);

            // reset requiem object for next request
            setRequiem({ ...requiem, review: null });

        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        onProcessRequiemReviewRequest();
        onProcessRequiemNavigationRequest();
    }, [requiem]);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            useContentDivider={true}
            visible={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`This is your final line of defense. Activating the Emergency Trigger will execute your pre-set instructions—this may include wiping devices, deleting cloud data, notifying contacts, or locking accounts. This action is irreversible. If you're not in immediate danger or certain this is what you want, do not proceed. Take a deep breath. If you're ready, we'll handle the rest.`}
                buttons={getContainerButtons()}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/emergency-trigger.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'Emergency Trigger'}/>
            </AnimatedElement>
        </div>
    )
}

export const getAuthCredential = ({ abstract, utils }) => {

    const getExistingPassKey = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // request web authentication for access key
                let credential = await navigator.credentials.get({
                    publicKey: {
                        challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                        rpId: window.location.hostname,
                        userVerification: 'required'
                    }
                });
                resolve(credential.id);
                
            } catch(e) {
                reject(e);
            }
        });
    }

    const requestBackupPassKey = () => {
        return new Promise(async (resolve, reject) => {
            
            let value = null;
            utils.alert.show({
                title: 'Backup Access Key',
                message: 'Please provide your backup access key below.',
                content: (
                    <div style={{
                        padding: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        isSecure={true}
                        onChange={text => value = text}
                        placeholder={'Access Key'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Verify',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: async key => {
                    if(value && key === 'confirm') {
                        resolve(value);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const requestPassKeyValidation = async () => {
        return new Promise(async (resolve, reject) => {

            // show alert requesting key submission
            utils.alert.show({
                title: 'Authentication Required',
                message: `Multi Factor Authentication is required to access sensitive data or make changes to "${abstract.getTitle()}". What type of key would you like to use?`,
                buttons: [{
                    key: 'passkey',
                    title: 'PassKey',
                    style: 'default'
                },{
                    key: 'backup_key',
                    title: 'Backup Access Key',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    setTimeout(async () => {
                        try {
                            if(key === 'backup_key') {
                                let response = await requestBackupPassKey();
                                resolve(response);
                                return;
                            }
                            if(key === 'passkey') {
                                let response = await getExistingPassKey();
                                resolve(response);
                                return;
                            }
                            throw new Error('user_cancelled');

                        } catch(e) {
                            reject(e);
                        }
                    }, 500);
                }
            });
        });
    }

    const requestPassPhraseValidation = async () => {
        return new Promise(async (resolve, reject) => {

            // show alert requesting user to input secret passphrase 
            let code = null;
            utils.alert.show({
                title: 'Authentication Required',
                message: `A secret passphrase is required to access sensitive data or make changes to "${abstract.getTitle()}". Please enter your passphrase below to continue.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        isSecure={true}
                        onChange={text => code = text}
                        placeholder={'Secret Passphrase'} />
                    </div>
                ),
                buttons: [{
                    key: 'done',
                    title: 'Done',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    setTimeout(() => {
                        if(key === 'done' && code) {
                            resolve(code);
                            return;
                        }
                        let error = new Error('user denied');
                        reject(error);
                    }, 500);
                }
            });
        });
    }

    const requestTwoFactorVerification = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // set loading flag and submit request to server
                let { phone_number_last_4 } = await Request.get(utils, '/accounts/', {
                    id: abstract.getID(),
                    type: 'new_two_factor_code'
                });

                // show alert requesting sms code from user
                let result = null;
                utils.alert.show({
                    title: 'Authentication Required',
                    message: `Two factor authentication is required to access sensitive data or make changes to "${abstract.getTitle()}". We've sent a one-time code to your phone ending in ${phone_number_last_4}. Please enter the code below to continue.`,
                    content: (
                        <div style={{
                            paddingBottom: 12,
                            paddingLeft: 12,
                            paddingRight: 12,
                            width: '100%'
                        }}>
                            <TextField
                            fieldStyle={{
                                textAlign: 'center'
                            }}
                            isSecure={true}
                            onChange={text => result = text}
                            placeholder={'Two Factor Code'} />
                        </div>
                    ),
                    buttons: [{
                        key: 'confirm',
                        title: 'Done',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        if(result && key === 'confirm') {
                            resolve(result);
                            return;
                        }
                        let error = new Error('user_cancelled');
                        reject(error);
                    }
                });
                
            } catch(e) {
                reject(e);
            }
        });
    }

    return new Promise(async (resolve, reject) => {
        try {

            // determine if an account was provided as the auth target
            if(abstract.type === 'accounts') {

                // request a multi-factor authentication passkey if applciable
                if(abstract.object.authentication_method === 'mfa_passkey') {
                    let result = await requestPassKeyValidation();
                    resolve({ mfa_passkey: result, requires_validation: false });
                    return;
                }

                // request a secret passphrase if applicable
                if(abstract.object.authentication_method === 'secret_phrase') {
                    let result = await requestPassPhraseValidation();
                    resolve({ secret_phrase_value: result, requires_validation: false });
                    return;
                }

                // request a two factor authentication sms code
                if(abstract.object.authentication_method === 'two_factor_sms') {
                    let result = await requestTwoFactorVerification();
                    resolve({ two_factor_sms_code: result, requires_validation: true });
                    return;
                }

                // throw an error for unsupported or missing authentication method
                throw new Error('Unsupported authentication method');
            }
            
            // fallback to resolving for resources that dont require auth
            resolve(null);

        } catch(e) {
            if(e.message.includes('user denied') === true) {
                let error = new Error('user_cancelled');
                reject(error);
                return;
            }
            reject(e);
        }
    });
}

const getPopupWindowOptions = (options = {}) => {

    // prepare height and width values for window popup size
    let height = options.height || 500;
    let width = options.width || 500;

    // prepare x and y values for window popup position
    let y = window.top.outerHeight / 2 + window.top.screenY - ( height / 2);
    let x = window.top.outerWidth / 2 + window.top.screenX - ( width / 2);

    return `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`;
}

export const IdentityVerification = ({ onComplete }, { index, options, utils }) => {

    const layerID = 'identity_verification';
    const routeRef = useRef(null);
    
    const [dob, setDob] = useState(null);
    const [expirationDate, setExpirationDate] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [phoneNumberLast4, setPhoneNumberLast4] = useState(null);
    const [route, setRoute] = useState(null);
    const { sockets, user, setUser } = useDataStore();
    const [verificationAnswers, setVerificationAnswers] = useState({});
    const [verificationQuestions, setVerificationQuestions] = useState(null);
    const [verificationQuizSessionId, setVerificationQuizSessionId] = useState(null);

    const onCheckboxValueChange = (key, value) => {
        setVerificationAnswers(values => {
            return update(values, {
                [key]: {
                    $set: value
                }
            })
        });
    }

    const onEditProfile = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: false
            }),
            id: `edit_user_${user.user_id}`
        });
    }

    const onSubmitDobClick = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('dob');
            await Request.post(utils, '/users/', {
                dob: dob.format('YYYY-MM-DD'),
                type: 'set_dob'
            });

            // end loading and update user object with dob
            setLoading(false);
            setUser(user => {
                user.dob = dob.format('YYYY-MM-DD');
                return user;
            });

            // advance route to the verification preference requestor
            setRoute('request_verification_preference');

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your date of birth. ${e.message || 'An unknown error occurred'}`,
                origin: 'identity_verification.on_submit_dob_click'
            });
        }
    }

    const onSubmitVerificationQuizClick = async () => {
        try {

            // prepare answers for request
            let answers = Object.entries(verificationAnswers).map(([key, value]) => {
                return {
                    question_id: key,
                    answer_id: value
                }
            });

            // set loading flag and submit request to server
            setLoading('verification_quiz');
            let { verified } = await Request.post(utils, '/users/', {
                answers,
                session_id: verificationQuizSessionId,
                type: 'process_identity_verification_quiz'
            });

            // end loading and determine if verification failed
            setLoading(false);
            if(verified === false) {
                throw new Error('The answers provided did not match our records. Please try again.');
            }

            // update user object with verification flag
            setUser(user => {
                user.verified = true;
                return user;
            });

            // show confirmation alert
            utils.alert.show({
                title: 'Identity Verified',
                message: 'Your identity has been successfully verified. No further action is required.',
                onClick: () => {
                    setLayerState('close');
                    if(typeof(onComplete) === 'function') {
                        onComplete();
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue processing the verification quiz. ${e.message || 'An unknown error occurred'}`,
                onClick: setRoute.bind(this, 'request_verification_preference'),
                origin: 'identity_verification.on_submit_verification_quiz_click'
            });
        }
    }

    const onVerificationStatusChange = evt => {

        // no additional logic is required if the route is not set to government_id_verification
        if(routeRef.current !== 'government_id_verification') {
            return;
        }
        
        // update local state and user object with verified flag
        setUser(user => {
            user.verified = evt.verified;
            return user;
        });
        
        // determine if verification suceeded
        if(evt.verified === false) {
            utils.alert.show({
                title: 'Verification Failed',
                message: 'Your identity verification has failed for this account. Please try again.',
                onClick: setRoute.bind(this, 'request_verification_preference')
            });
            return;
        }

        // notify subscribers that verification has completed
        if(typeof(onComplete) == 'function') {
            onComplete();
        }

        // show confirmation alert
        utils.alert.show({
            title: 'Verification Complete',
            message: 'Your identity verification has been successfully completed for this account. No further action is required.',
            onClick: setLayerState.bind(this, 'close')
        });
    }

    const getButtons = () => {
        if(route === 'request_dob') {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            },{
                color: 'primary',
                id: 'continue',
                loading: loading === 'dob',
                onClick: onSubmitDobClick,
                text: 'Continue',
                visible: dob ? true : false
            }];
        }
        if(route === 'verification_quiz') {
            return [{
                color: 'dark',
                id: 'back',
                onClick: setRoute.bind(this, 'request_verification_preference'),
                text: 'Back'
            },{
                color: 'primary',
                id: 'continue',
                loading: loading === 'verification_quiz',
                onClick: onSubmitVerificationQuizClick,
                text: 'Submit',
                visible: Object.keys(verificationAnswers).length === verificationQuestions.length
            }];
        }
        if(route === 'government_id_verification') {
            return [{
                color: 'dark',
                id: 'back',
                onClick: setRoute.bind(this, 'request_verification_preference'),
                text: 'Back'
            }];
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getContent = () => {
        if(route === 'request_dob') {
            return (
                <LayerItem 
                collapsed={false}
                lastItem={true}
                title={'Required Information Needed'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginBottom: 16
                    }}>{`To begin verifying your identity, we just need one final detail: your date of birth. We've already received the rest of your information — once your birthdate is confirmed, we'll proceed with verification to protect your account and enable secure access to LifeWipe services.`}</span>
                    <DatePickerDropdownField
                    onChange={setDob}
                    removable={false}
                    utils={utils}
                    value={dob} />
                </LayerItem>
            )
        }
        if(route === 'request_verification_preference') {
            return (
                <LayerItem 
                collapsed={false}
                lastItem={true}
                title={'Identity Verification'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginBottom: 16
                    }}>{`How would you like to complete your identity verification? You can finish it right here in your browser, or we can send a secure link to your mobile device so you can complete it there. Both options are fast and secure — just choose the one that’s most convenient for you.`}</span>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        marginTop: 24,
                        width: '100%'
                    }}>
                        <AsyncInteractionItem 
                        description={'Answer a short quiz consisting of a few multiple-choice questions based on publicly available records — such as past addresses, vehicles, or known family members.'}
                        image={'images/identity-verification-quiz.png'}
                        onAsyncClick={fetchVerificationQuestions}
                        title={'Start Verification Quiz'}
                        utils={utils} />
                        <AsyncInteractionItem 
                        description={`Receive a secure link to upload a photo of your government-issued ID. A mobile device is required to take a clear photo of the document and complete verification securely.`}
                        image={'images/identity-verification-sms.png'}
                        lastItem={true}
                        onAsyncClick={generateVerificationLink}
                        title={'Upload a Photo of Your ID'}
                        utils={utils} />
                    </div>
                </LayerItem>
            )
        }
        if(route === 'verification_quiz') {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    {verificationQuestions.map((question, index, questions) => { 
                        return (
                            <LayerItem
                            collapsed={false}
                            key={index}
                            lastItem={user ? index === questions.length - 1 : false}
                            title={`Question ${index + 1}`}>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    display: 'block',
                                    marginBottom: 12
                                }}>{question.description}</span>
                                <div style={{
                                    ...Appearance.styles.unstyledPanel(),
                                    padding: 16,
                                    width: '100%'
                                }}>
                                    {question.options.map((option, i) => {
                                        return (
                                            <div
                                            key={i}
                                            style={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                marginBottom: i === question.options.length - 1 ? 0 : 12,
                                                width: '100%'
                                            }}>
                                                <Checkbox 
                                                checked={verificationAnswers[question.id] === option.id}
                                                onChange={onCheckboxValueChange.bind(this, question.id, option.id)} />
                                                <span 
                                                className={'cursor-pointer'}
                                                onClick={onCheckboxValueChange.bind(this, question.id, option.id)}
                                                style={{
                                                    ...Appearance.textStyles.defaultText(),
                                                    marginLeft: 12
                                                }}>{option.value}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </LayerItem>
                        )
                    })}
                </div>
            )
        }
        if(route === 'government_id_verification') {
            return (
                <LayerItem 
                collapsed={false}
                lastItem={true}
                title={'Identity Verification'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`We’ve sent a secure verification text message to the phone number ending in ${phoneNumberLast4}. Please check your text messages, tap the link to begin the identity verification, and return here when finished with the verification. Make sure to use it by ${Utils.format.date(expirationDate)}—the link will expire after that time.`}</span>
                </LayerItem>
            )
        }
    }

    const fetchVerificationQuestions = () => {
        return new Promise(async (resolve, reject) => {
            try {

                // set loading flag and submit request to server
                let { questions, session_id, verified } = await Request.get(utils, '/users/', {
                    type: 'identity_verification_questions'
                });

                // determine if account has already been verified
                if(verified === true) {

                    // update user object with verification flag
                    setUser(user => {
                        user.verified = true;
                        return user;
                    });

                    // show confirmation alert
                    utils.alert.show({
                        title: 'Identity Verified',
                        message: 'Your identity has already been verified for this account. No further action is required.',
                        onClick: () => {
                            setLayerState('close');
                            if(typeof(onComplete) === 'function') {
                                onComplete();
                            }
                        }
                    });
                    return;
                }

                // update local state with verification questions and resolve without alert
                setVerificationQuestions(questions);
                setVerificationQuizSessionId(session_id);
                setRoute('verification_quiz');
                resolve();

            } catch(e) {

                // prepare error alert data for handler if applicable
                if(e.code === Request.status.get().incomplete_user_profile) {
                    e.props = {
                        title: 'Verification Error',
                        buttons: [{
                            key: 'edit',
                            title: 'Edit Profile',
                            style: 'default'
                        },{
                            key: 'cancel',
                            title: 'Close',
                            style: 'cancel'
                        }],
                        onClick: key => {
                            if(key === 'edit') {
                                onEditProfile();
                                return;
                            }
                        }
                    };  
                }
                reject(e);
            }
        });
    }

    const generateVerificationLink = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            let { expiration_date, phone_number_last_4 } = await Request.post(utils, '/users/', {
                delivery_method: 'sms',
                type: 'new_identity_verification'
            });

            // set expiration date for content description
            setExpirationDate(moment.utc(expiration_date).local());
            setPhoneNumberLast4(phone_number_last_4);
            setRoute('government_id_verification');
            setLoading(false);

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue preparing the identity verification process. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'identity_verification.generate_verification_link'
            });
        }
    }

     const setupWebsocketListeners = () => {
        sockets.on('users', 'identity_verification_status_change', onVerificationStatusChange);
        return () => {
            sockets.off('users', 'identity_verification_status_change', onVerificationStatusChange);
        }
    }

    useEffect(() => {
        routeRef.current = route;
    }, [route]);

    useEffect(() => {
        setRoute(user?.onboarding.has_configured_dob ? 'request_verification_preference' : 'request_dob');
        let unsubscribe = setupWebsocketListeners();
        return unsubscribe;
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}
        utils={utils}>
            {getContent()}
        </Layer>
    )
}

export const ImportAccounts = ({ index, options, utils }) => {
    
    const layerID = 'import_accounts';
    const limit = 5;

    const [accounts, setAccounts] = useState([]);
    const [category, setCategory] = useState(null);
    const [layerState, setLayerState] = useState(false);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset]= useState(0);
    const [paging, setPaging] = useState(null);
    const [purchaseProps, setPurchaseProps] = useState(false);
    const [searchText, setSearchText] = useState(null);

    const onAccountClick = account => {

        // prepare target for account editing
        let target = Account.new();
        target.authentication = account.authentication;
        target.authentication_method = account.authentication?.method;
        target.category = account.category && { id: account.category };
        target.config = account.config;
        target.credentials = account.credentials;
        target.id = account.id;
        target.name = account.name;
        target.provider = { id: account.provider };
        
        // open layer with editing options
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'accounts'
            }),
            Component: AddEditAccount.bind(this, { 
                configuring: true,
                isNewTarget: true,
                onChange: result => {
                    setAccounts(accounts => {
                        return accounts.map(account => {
                            if(account.id === target.id) {
                                account = {
                                    ...account,
                                    authentication: result.authentication,
                                    category: result.category,
                                    config: result.config,
                                    credentials: result.credentials,
                                    name: result.name,
                                    provider: result.provider
                                }
                            }
                            return account
                        })
                    })
                }
            }),
            id: 'new_linked_account'
        });
    }

    const onBrowserFileChange = async (category, file) => {
        try {

            // set loading flag and submit request to server
            setLoading(`${category}_file_picker`);
            let { accounts, purchase_props } = await Request.upload(utils, '/accounts/', {
                type: `new_${category}_import_document`
            },{
                files: [{
                    data: file.raw,
                    key: 'file'
                }]
            });

            setLoading(false);
            setPaging({
                current_page: 1,
                number_of_pages: accounts.length > limit ? Math.ceil(accounts.length / limit) : 1
            });
            setAccounts(accounts);
            setPurchaseProps(purchase_props);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue processing your ${Utils.ucFirst(category)} passwords document. ${e.message || 'An unknown error occurred'}`,
                origin: 'import_accounts.on_browser_file_change'
            });
        }
    }

    const onCheckboxChange = (account, val, evt) => {
        evt.stopPropagation();
        setAccounts(accounts => {
            return update(accounts, {
                $apply: accounts => {
                    return accounts.map(act => {
                        if(act.id === account.id) {
                            act.enabled = val;
                        }
                        return act;
                    });
                }
            });
        });
    }

    const onSearchTextChange = text => {
        setOffset(0);
        setSearchText(text);
    }

    const onSelectAllClick = val => {
        setAccounts(accounts => {
            return accounts.map(act => {
                act.enabled = val;
                return act;
            });
        });
    }

    const onSpreadsheetFileChange = async file => {
        try {

            // set loading flag and submit request to server
            setLoading('spreadsheet_file_picker');
            let { accounts, purchase_props } = await Request.upload(utils, '/accounts/', {
                type: 'new_spreadsheet_import_document'
            },{
                files: [{
                    data: file.raw,
                    key: 'file'
                }]
            });

            setLoading(false);
            setAccounts(accounts);
            setPurchaseProps(purchase_props);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue processing your spreadsheet. ${e.message || 'An unknown error occurred'}`,
                origin: 'import_accounts.on_spreadsheet_file_change'    
            });
        }
    }

    const onSubmitClick = async () => {
        try {

            // declare all eligible accounts for import
            let [targets] = getTargetAccounts();

            // set loading flag and submit request to server, response will be a spreadsheet download
            setLoading('submit');
            let { payment_id } = await Request.post(utils, '/accounts/', {
                accounts: targets,
                type: 'new_import'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: payment_id ? `Your payment has been processed, and your accounts are now being securely added to your LifeWipe profile. This may take up to 30 minutes, depending on the number of accounts submitted. You’ll receive a notification as soon as the process is complete.` : `Your accounts have been submitted and are being securely added to your profile. This process can take up to 30 minutes depending on how many accounts you've submitted. We'll notify you as soon as everything is complete.`,
                buttons: [{
                    key: 'cancel',
                    title: 'Okay',
                    style: 'cancel'
                }],
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            if(e.message === 'user_cancelled') {
                return;
            }
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your accounts for processing. ${e.message || 'An unknown error occurred'}`,
                origin: 'import_accounts.on_submit_click'
            });
        }
    }

    const onTemplateDownloadClick = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('download');
            await Request.download(utils, '/accounts/', {
                type: 'batch_import_template'
            });
            setLoading(false);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue preparing your import template. ${e.message || 'An unknown error occurred'}`,
                origin: 'import_accounts.on_template_download_click'
            });
        }
    }

    const getButtons = () => {

        // no other buttons are needed if no accounts have been generated
        if(accounts.length === 0) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }

        // determine which buttons to show using the total number of paid accounts
        let [targets, total] = getTargetAccounts();
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: targets?.length > 0 && total > 0 ? 'Submit Order' : 'Submit',
            visible: targets?.length > 0 ? true : false
        }];
    }

    const getCategoryItems = () => {
        return [{
            id: '1password',
            title: '1Password'
        },{
            id: 'google_chrome',
            title: 'Google Chrome'
        },{
            id: 'safari',
            title: 'Safari'
        },{
            id: 'spreadsheet',
            title: 'Spreadsheet'
        }];
    }

    const getContent = () => {
        
        // determine if no accounts have been imported yet
        if(accounts.length === 0) {
            let categories = getCategoryItems();
            return (
                <>
                <LayerItem 
                collapsed={null}
                lastItem={category ? false : true}
                title={'Bulk Import Your Accounts'}
                useStyle={true}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginBottom: 24
                    }}>{`If you've got a long list of accounts to secure, we've got you covered. LifeWipe lets you import accounts in bulk using a spreadsheet and you can even bring in credentials exported from browsers like Safari and Chrome. Just upload your file and we'll handle the rest, securely and efficiently.`}</span>
                    <AltFieldItem 
                    item={{
                        component: 'list',
                        items: categories,
                        onChange: item => setCategory(item?.id),
                        title: 'Provider',
                        value: categories.find(item => item.id === category)?.title
                    }} 
                    lastItem={true}
                    utils={utils} />
                </LayerItem>
                {getSteps().map((step, index) => {
                    return (
                        <LayerItem
                        collapsed={false}
                        key={index}
                        title={`${index + 1}. ${step.title}`}
                        useStyle={true}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                marginBottom: step.content ? 24 : 0
                            }}>{step.description}</span>
                            {step.content}
                        </LayerItem>
                    )
                })}
                </>
            )
        }

        // prepare list of eligible targets using search text and paging props
        let targets = accounts.filter(account => {
            return searchText ? account.name.toLowerCase().includes(searchText.toLowerCase()) : true
        }).filter((_, index) => {
            return !paging || (index >= ((paging.current_page - 1) * limit) && index < (paging.current_page * limit));
        });

        return (
            <>
            <LayerItem 
            collapsed={null}
            title={'Bulk Import Your Accounts'}>
                <span style={{
                    ...Appearance.textStyles.defaultText()
                }}>
                    {`Your export has been successfully processed — ephemerally. This means the data was handled in-memory only and was not stored or saved beyond the scope of this session. Each account from the file is listed below, where you can review and configure your preferences before finalizing your import.`}
                    {purchaseProps !== null && (
                        <>
                        <b>{` Important: `}</b>
                        {`Your LifeWipe plan includes a limited number of linked accounts. Based on your current usage and the number of accounts in this import, some entries may require an add-on purchase to complete the import.`}
                        </>
                    )}
                </span>
            </LayerItem>
            <LayerItem
            collapsed={false}
            lastItem={false}
            title={'New Linked Accounts'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        padding: '12px 16px 12px 16px',
                    }}>{`Below is a list of the accounts detected in your import file. You can choose which ones to proceed with by selecting or deselecting them from the list. Only selected accounts will be linked to your LifeWipe profile and count toward your plan or billing total. Changes here will update the summary above in real time.`}</span>
                    <div style={{
                        alignItems: 'center',
                        borderBottom: Appearance.styles.bottomBorder(),
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: 8,
                        padding: 14,
                        width: '100%'
                    }}>
                        <Checkbox 
                        checked={true}
                        onChange={onSelectAllClick} />
                        <TextField 
                        containerStyle={{
                            flexGrow: 1,
                            marginLeft: 12
                        }}
                        onChange={onSearchTextChange}
                        placeholder={'Search by account name...'} 
                        value={searchText} />
                    </div>
                    {targets.map((account, index) => {
                        return (
                            <div 
                            className={`view-entry ${window.theme}`}
                            key={index}
                            onClick={onAccountClick.bind(this, account)}
                            style={{
                                alignItems: 'center',
                                borderBottom: Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: '10px 14px 10px 14px',
                                width: '100%'
                            }}>
                                <Checkbox 
                                checked={account.enabled !== false}
                                onChange={onCheckboxChange.bind(this, account)} />
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    flexGrow: 1,
                                    marginLeft: 12,
                                    opacity: account.enabled === false ? 0.5 : 1
                                }}>{account.name}</span>
                                {account.config.two_factor_required && (
                                    <AltBadge 
                                    content={{
                                        color: Appearance.colors.primary(),
                                        text: '2FA'
                                    }} 
                                    style={{
                                        marginLeft: 8, 
                                        marginRight: 0 
                                    }} />
                                )}
                                {account.config.mfa_required && (
                                    <AltBadge 
                                    content={{
                                        color: Appearance.colors.secondary(),
                                        text: 'MFA'
                                    }} 
                                    style={{
                                        marginLeft: 8, 
                                        marginRight: 0 
                                    }} />
                                )}
                                <img
                                src={'images/next-arrow-grey-small.png'}
                                style={{
                                    height: 12,
                                    marginLeft: 8,
                                    objectFit: 'contain',
                                    width: 12
                                }} />
                            </div>
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset}
                        onClick={next => {
                            setOffset(next);
                            setPaging({
                                current_page: (next / limit) + 1,
                                number_of_pages: accounts.length > limit ? Math.ceil(accounts.length / limit) : 1
                            });
                        }}
                        style={{
                            borderTop: null 
                        }} />
                    )}
                </div>
            </LayerItem>
            {getOrderOverview()}
            </>
        )
    }

    const getOrderOverview = () => {
        
        // prepare array of targets enabled for import
        let [targets, total] = getTargetAccounts();
            
        return purchaseProps?.cost_per_account && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Order Overview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getOrderOverviewProps({ targets, total })}
                </div>
                {total > 0 && (
                    <span style={{
                        color: Appearance.colors.subText(),
                        display: 'flex',
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.75,
                        marginTop: 24,
                        textAlign: 'left',
                        width: '100%'
                    }}>{`By completing this import, you agree to be billed ${Utils.toCurrency(total * purchaseProps.cost_per_account)} per month for the additional linked accounts selected above. This charge will be processed alongside your existing LifeWipe subscription and will renew monthly until removed or modified in your account settings.`}</span>
                )}
            </LayerItem>
        )
    }

    const getOrderOverviewProps = ({ targets, total }) => {

        // prepare a prorated discount that reflects how close or far away the renewal date is
        let cost = total > 0 ? total * purchaseProps.cost_per_account : 0;
        let discount = cost - (cost * purchaseProps.pro_rate_percentage);

        // prepare billing items with dynamic costs
        let items = [{
            key: 'subscription_tier_limit',
            title: `Locket Passwords Included in ${purchaseProps?.tier_title} Plan`,
            value: purchaseProps.subscription_tier_limit
        },{
            key: 'current_count',
            title: 'Currently Used',
            value: purchaseProps.current_count,
        },{
            key: 'new_count',
            title: 'New Locket Passwords to Import',
            value: targets.length,
        },{
            key: 'billing_cycle',
            title: 'Billing Cycle',
            value: 'Monthly'
        },{
            key: 'renewal_date',
            title: 'Renewal Date',
            value: Utils.format.date(purchaseProps.renewal_date, { exclude_time: true })
        },{
            color: total > 0 && Appearance.colors.green,
            key: 'total',
            title: 'Sub Total',
            value: Utils.toCurrency(cost),
        },{
            color: Appearance.colors.yellow,
            key: 'pro_rate_percentage',
            title: 'First Month Discount',
            value: `-${Utils.toCurrency(discount)}`,
            visible: discount > 0
        },{
            color: total > 0 && Appearance.colors.green,
            key: 'total',
            title: 'Due Today',
            value: Utils.toCurrency(cost - discount),
        }];

        return items.filter(item => {
            return item.visible !== false;
        }).map((item, index, items) => {
            return (
                <div 
                key={index}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 12,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                        ...item.color && {
                            color: item.color
                        }
                    }}>{item.value}</span>
                </div>
            )
        })
    }

    const getSteps = () => {
        switch(category) {
            case 'spreadsheet':
            return [{
                content: (
                    <Button 
                    color={'primary'}
                    containerStyle={{
                        marginTop: 12
                    }} 
                    label={'Download Template'}
                    loading={loading === 'download'}
                    onClick={onTemplateDownloadClick}
                    type={'large'} />
                ),
                description: `We provide a pre-formatted spreadsheet to keep things organized and secure.`,
                title: 'Download the template'
            },{
                description: `Just enter your login details, recovery notes, and any custom instructions required for accessing your accounts.`,
                title: 'Fill it out with your accounts '
            },{
                content: (
                    <FilePickerField
                    fileTypes={['xlsx']}
                    loading={loading === 'spreadsheet_file_picker'}
                    onChange={onSpreadsheetFileChange}
                    style={{
                        marginTop: 12
                    }} 
                    utils={utils} />
                ),
                description: `Once uploaded, LifeWipe will process the data securely and prepare it for your review.`,
                title: 'Upload the completed file'
            },{
                description: `You'll be able to review and fine-tune settings for each account before final submission.`,
                title: 'Configure each account'
            }];

            case '1password':
            return [{
                description: `Open the 1Password app (Mac/Windows) → Unlock your vault → Go to File > Export > All Items (or selected items) and click Export to export your saved credentials as a .csv file. (You'll be prompted to authenticate with your account login.)`,
                title: 'Export from 1Password'
            },{
                content: (
                    <FilePickerField
                    fileTypes={['csv']}
                    loading={loading === `${category}_file_picker`}
                    onChange={onBrowserFileChange.bind(this, category)}
                    persist={false}
                    style={{
                        marginTop: 12
                    }} 
                    utils={utils} />
                ),
                description: `Just select the CSV file from your computer using the file upload browser field below.`,
                title: 'Upload the CSV to LifeWipe'
            },{
                description: `You'll be able to review and fine-tune settings for each account before final submission.`,
                title: 'Configure each account'
            }];
            

            case 'google_chrome':
            return [{
                description: `Go to Chrome Settings → Autofill & Passwords → Google Password Manager → Settings → Click "Export passwords" to export your saved credentials as a .csv file. (You'll be prompted to authenticate with your computer login.)`,
                title: 'Export from Google Chrome'
            },{
                content: (
                    <FilePickerField
                    fileTypes={['csv']}
                    loading={loading === `${category}_file_picker`}
                    onChange={onBrowserFileChange.bind(this, category)}
                    persist={false}
                    style={{
                        marginTop: 12
                    }} 
                    utils={utils} />
                ),
                description: `Just select the CSV file from your computer using the file upload browser field below.`,
                title: 'Upload the CSV to LifeWipe'
            },{
                description: `You'll be able to review and fine-tune settings for each account before final submission.`,
                title: 'Configure each account'
            }];

            case 'safari':
            return [{
                description: `Go to Safari > Settings > Passwords, then export your saved credentials as a .csv file. (You'll be prompted to authenticate with your Mac login.)`,
                title: 'Export from Safari'
            },{
                content: (
                    <FilePickerField
                    fileTypes={['csv']}
                    loading={loading === `${category}_file_picker`}
                    onChange={onBrowserFileChange.bind(this, category)}
                    persist={false}
                    style={{
                        marginTop: 12
                    }} 
                    utils={utils} />
                ),
                description: `Just select the CSV file from your computer using the file upload browser field below.`,
                title: 'Upload the CSV to LifeWipe'
            },{
                description: `You'll be able to review and fine-tune settings for each account before final submission.`,
                title: 'Configure each account'
            }];

            default:
            return [];
        }
    }

    const getTargetAccounts = () => {

        // prepare list of eligible targets based on on current selections
        let targets = accounts.filter(account => account.enabled !== false);
        if(!purchaseProps) {
            return [targets, 0];
        }

        // prepare total for current accounts + newly selected accounts
        let count = purchaseProps.current_count + targets.length;

        // determine if targets length falls within subscription tier limit
        if(count <= purchaseProps.subscription_tier_limit) {
            return [targets, 0];
        }

        // determine if current count falls within subscription tier limit
        if(purchaseProps.current_count <= purchaseProps.subscription_tier_limit) {
            return [
                targets, 
                targets.length - (purchaseProps.subscription_tier_limit - purchaseProps.current_count)
            ];
        }

        // prepare a count for the total number of paid accounts 
        return [targets, targets.length];
    }

    useEffect(() => {
        let targets = accounts.filter(account => searchText ? account.name.toLowerCase().includes(searchText.toLowerCase()) : true);
        setPaging({
            current_page: 1,
            number_of_pages: targets.length > limit ? Math.ceil(targets.length / limit) : 1
        });
    }, [searchText]);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}
        utils={utils}>
            {getContent()}
        </Layer>
    )
}

export const Instructions = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [instructions, setInstructions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const { requiem, setRequiem } = useDataStore();

    const onInactiveIconClick = evt => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Instruction Not Active',
            message: `This trigger instruction is not currently active and can not be used with LifeChecks or Emergency Trigger.`
        });
    }

    const onInstructionClick = instruction => {
        utils.layer.open({
            abstract: Abstract.create({
                object: instruction,
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { 
                isNewTarget: false,
                onChange: () => {
                    offset.current = 0;
                    fetchInstructions();
                }
            }),
            id: `trigger_instructions.${instruction.id}`
        });
    }

    const onInUseIconClick = evt => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Instruction In-Use',
            message: `This trigger instruction is in-use by either LifeChecks or Emergency Trigger. This means the instruction will execute when a LifeCheck is failed and/or the Emergency Trigger is activated.`
        });
    }

    const onNewInstructionClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Instruction.new(),
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { 
                isNewTarget: true,
                onChange: () => {
                    offset.current = 0;
                    fetchInstructions();
                }
            }),
            id: 'new_trigger_instruction'
        });
    }

     const onNewInstructionTemplateClick = () => {
        utils.layer.open({
            Component: NewInstructionFromTemplate.bind(this, { 
                onChange: target => {
                    
                    // open layer to configure new instruction based on template
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: target,
                            type: 'trigger_instructions'
                        }),
                        Component: AddEditInstruction.bind(this, { 
                            isNewTarget: true,
                            isTemplate: true,
                            onChange: () => {
                                offset.current = 0;
                                fetchInstructions();
                            }
                        }),
                        id: 'new_trigger_instruction'
                    });
                }
            }),
            id: 'new_instruction_from_template'
        });
    }

    const onProcessRequiemReviewRequest = async () => {
        try {

            // no further action is needed if there is no target match
            if(requiem.review?.category !== 'trigger_instructions') {
                return;
            }

            // fetch details for instruction from server and update user object with changes
            let instruction = await Instruction.get(utils, requiem.review.id);

            // open configuration for instruction after a brief pause while the state updates
            setTimeout(onInstructionClick.bind(this, instruction), 500);

            // reset requiem object for next request
            setRequiem({ ...requiem, review: null });

        } catch(e) {
            console.error(e.message);
        }
    }

    const getBadges = instruction => {
        if(instruction.metadata?.requiem_generated === true && instruction.metadata?.requiem_confirmation_date === null) {
            return (
                <img 
                title={'Requiem Review Required'}
                src={'images/warning-icon-yellow.png'}
                style={{
                    height: 25,
                    marginLeft: 4,
                    width: 25
                }} />
            )
        }
        return (
            <>
            {instruction.in_use === true && (
                <img 
                className={'text-button'}
                onClick={onInUseIconClick}
                title={'Trigger Instruction In-Use'}
                src={'images/trigger-instruction-in-use-green.png'}
                style={{
                    height: 25,
                    marginLeft: 4,
                    width: 25
                }} />
            )}
            {instruction.active === false && (
                <img 
                className={'text-button'}
                onClick={onInactiveIconClick}
                title={'Trigger Instruction Not Active'}
                src={'images/trigger-instruction-inactive-grey.png'}
                style={{
                    height: 25,
                    marginLeft: 4,
                    width: 25
                }} />
            )}
            </>
        )
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'new',
            onClick: onNewInstructionClick,
            text: 'Create Instruction'
        },{
            color: 'secondary',
            id: 'new_from_template',
            onClick: onNewInstructionTemplateClick,
            text: 'Start With A Template'
        }];
    }

    const getIcon = instruction => {

        switch(instruction.category) {
            case Instruction.categories.get().system_triggers:
            if(instruction.in_use === true) {
                return window.theme === 'dark' ? 'images/system-triggers-in-use-icon-dark.png' : 'images/system-triggers-in-use-icon-light.png';
            }
            return window.theme === 'dark' ? 'images/system-triggers-icon-dark.png' : 'images/system-triggers-icon-light.png';

            default:
            if(instruction.in_use === true) {
                return window.theme === 'dark' ? 'images/instructions-in-use-icon-dark.png' : 'images/instructions-in-use-icon-light.png';
            }
            return window.theme === 'dark' ? 'images/instructions-icon-dark.png' : 'images/instructions-icon-light.png'
        }
    }

    const getInstructions = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(instructions.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payments-icon-dark.png' : 'images/payments-icon-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Instructions Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any instructions for your account.'}</span>
                    </div>
                </div>
            )
        }
        return instructions.map((instruction, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onInstructionClick.bind(this, instruction)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== instructions.length - 1 ? `1px solid ${Appearance.colors.divider()}` : 0,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-lg-flex'}
                    src={getIcon(instruction)}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{instruction.name.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{instruction.description || 'Description not available'}</span>
                    </div>
                    {getBadges(instruction)}
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchInstructions = async () => {
        try {
            setLoading(true);
            let { paging, trigger_instructions } = await Request.get(utils, '/users/', {
                include_inactive_trigger_instructions: true,
                limit: limit,
                offset: offset.current,
                type: 'trigger_instructions'
            });

            setLoading(false);
            setInstructions(trigger_instructions);
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of instructions. ${e.message || 'An unknown error occurred'}`,
                origin: 'instructions.fetch_instructions'
            });
        }
    }

    useEffect(() => {
        onProcessRequiemReviewRequest();
    }, [requiem]);

    useEffect(() => {
        fetchInstructions();
        utils.content.subscribe('instructions_list', ['trigger_instructions'], {
            onFetch: fetchInstructions,
            onUpdate: abstract => {
                setInstructions(instructions => {
                    return instructions.map(instruction => {
                        return instruction.id === abstract.getID() ? abstract.object : instruction;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('instructions_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer 
                body={`Instructions are essentially pre-set directives—rules that tell the system what to do in specific situations, such as emergencies, device inactivity, or posthumous data handling. They define how your devices and digital assets should be managed under various scenarios, including when to wipe data, alert trusted contacts, or execute legacy transfers. This ensures your digital privacy plan stays aligned with your evolving preferences.`}
                buttons={getContainerButtons()}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/trigger-instructions.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'Trigger Instructions'} />
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer 
                body={`Below you'll find all instructions configured for your LifeWipe account. You have full control over these directives. To customize, review, or update any instruction, simply click on it. While instructions are created and stored here, they are not ran automatically. To make them functional, you must link the ones you want to your LifeCheck system. This allows you to decide which actions should be triggered if a LifeCheck is missed or if an emergency signal is activated.`}
                title={'My Instructions'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        maxWidth: '100%',
                        width: '100%'
                    }}>
                        {getInstructions()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchInstructions();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const JitsiMeeting = ({ meeting_id }, { index, options, utils }) => {

    const layerID = `jitsi_meeting_${meeting_id}`;
    const [api, setApi] = useState(null);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [screenShareEnabled, setScreenShareEnabled] = useState(false);
    const { user } = useDataStore();
    const [videoEnabled, setVideoEnabled] = useState(false);

    const onAudioStateChange = ({ muted }) => {
        setAudioEnabled(muted ? false : true);
    }

    const onReadyToClose = () => {
        setLayerState('close');
    }

    const onScreenShareChange = ({ on }) => {
        setScreenShareEnabled(on);
    }

    const onVideoStateChange = ({ muted }) => {
        setVideoEnabled(muted ? false : true);
    }

    const getContent = () => {
        return (
            <div style={{
                backgroundColor: 'rgba(20,20,20,1)',
                height: '100%',
                width: '100%'
            }}>
                <div style={{
                    height: '100%',
                    paddingBottom: 90,
                    width: '100%'
                }}>
                    <JitsiMeetingComponent
                    configOverwrite={{
                        defaultLanguage: 'en',
                        disableDeepLinking: true,
                        enableLobby: true,
                        prejoinConfig: {
                            enabled: true,
                            hideDisplayName: false
                        },
                        startWithAudioMuted: true,
                        startWithVideoMuted: true,
                        userPermissionsPolicy: {
                            camera: true,
                            microphone: true
                        }
                    }}
                    domain={'jitsi.life-wipe.com'}
                    getIFrameRef={iframeRef => { 
                        iframeRef.style.height = '100%';
                        iframeRef.style.width = '100%';
                    }}
                    interfaceConfigOverwrite={{
                        APP_NAME: 'LifeWipe',
                        DEFAULT_LOCAL_DISPLAY_NAME: user?.full_name,
                        TOOLBAR_BUTTONS: [],
                    }}
                    onApiReady={setApi}
                    roomName={meeting_id}
                    style={{
                        height: '100%',
                        width: '100%'
                    }}
                    userInfo={{
                        displayName: user?.full_name
                    }} />
                </div>
                {getControls()}
            </div>
        )
    }

    const getControls = () => {
        return (
            <div style={{
                bottom: 20,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                gap: 12,
                position: 'absolute',
                right: 0
            }}>
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('hangup')}
                src={'images/jitsi-leave-meeting-red.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleShareScreen')}
                src={screenShareEnabled ? 'images/jitsi-screen-share-enabled.png' : 'images/jitsi-screen-share-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleAudio')}
                src={audioEnabled ? 'images/jitsi-audio-enabled.png' : 'images/jitsi-audio-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleVideo')}
                src={videoEnabled ? 'images/jitsi-video-enabled.png' : 'images/jitsi-video-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
            </div>
        );
    }

    useEffect(() => {
        if(api) {
            api.addListener('audioMuteStatusChanged', onAudioStateChange);
            api.addListener('readyToClose', onReadyToClose);
            api.addListener('screenSharingStatusChanged', onScreenShareChange);
            api.addListener('videoMuteStatusChanged', onVideoStateChange);
            return () => {
                api.removeListener('audioMuteStatusChanged', onAudioStateChange);
                api.removeListener('readyToClose', onReadyToClose);
                api.removeListener('screenSharingStatusChanged', onScreenShareChange);
                api.removeListener('videoMuteStatusChanged', onVideoStateChange);
            }
        }
    }, [api]);

    return (
        <Layer 
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState,
            removePadding: true,
            sizing: 'fullscreen'
        }}
        utils={utils}>
            {getContent()}
        </Layer>
    );
}

export const LegalAgreementsLayer = ({ documentIDs, legalAgreements = {}, onAsyncComplete, onCancel, onComplete }, { index, options, utils }) => {

    const layerID = 'legal_agreements';
    const [edits, setEdits] = useState(legalAgreements);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onCancelClick = () => {
        setLayerState('close');
        if(typeof(onCancel) === 'function') {
            onCancel(edits);
        }
    }

    const onSubmitClick = async () => {
        try {

            // no additional logic is required if a standard callback was provided
            if(typeof(onComplete) === 'function') {
                onComplete(edits);
                setLayerState('close');
                return;
            }
            
            // set loading flag and call completion function
            setLoading(true);
            let result = await onAsyncComplete(edits);

            // end loading and show completion message
            setLoading(false);
            utils.banner.show({
                title: result.title || 'Agreements Completed',
                message: result.message || 'You have successfully completed the legal agreements.',
                onShow: () => {
                    setLayerState('close');
                    if(typeof(result.onClick) === 'function') {
                        result.onClick();
                    }
                    if(typeof(result.onShow) === 'function') {
                        result.onShow();
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your responses. ${e.message || 'An unknown error occurred'}`,
                origin: 'legal_agreements_layer.on_submit_click'
            });
        }
    }
    
    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCancelClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading,
            onClick: onSubmitClick,
            text: 'Submit',
            visible: edits ? true : false
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        index={index}
        id={layerID}
        options={{
            ...options,
            layerState: layerState
        }}>
            <LegalAgreements
            documentIDs={documentIDs}
            editable={true}
            lastItem={true}
            onComplete={setEdits}
            showDescription={true}
            utils={utils} 
            value={edits} />
        </Layer>
    )
}

export const Login = ({ onLogin, secureAuth, shouldRedirectUser = true }, { index, options, utils }) => {

    const layerID = 'login';

    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [route, setRoute] = useState(null);
    const [sessionEnabled, setSessionEnabled] = useState(false);

    const onContinueWithFacebookClick = async props => {
        try {

            // set loading state
            setLoading('facebook');

            // initialize facebook sdk if not already loaded
            await initializeFacebookSDK();

            // request facebook login
            let response = await new Promise((resolve, reject) => {
                window.FB.login(response => {
                    if(response.authResponse) {
                        resolve(response.authResponse);
                    } else {
                        let error = new Error('user_cancelled');
                        reject(error);
                    }
                }, { 
                    scope: 'public_profile,email' 
                });
            });

            // submit request to processor function
            await processSocialSignin({ channel: 'facebook', token: response.accessToken });

        } catch(e) {

            setLoading(false);
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue signing in with Facebook. ${e.message || 'An unknown error occurred'}`,
                    origin: 'login.on_continue_with_facebook_click'
                });
            }
        }
    }

    const onContinueWithGoogleClick = async () => {
        try {

            // set loading state
            setLoading('google');

            // ensure google sdk is loaded (should already be preloaded from useEffect)
            if(!window.google?.accounts || !window.gapi) {
                await initializeGoogleSDK();
            }

            // request google sign-in with oauth popup using token client
            const tokenResponse = await new Promise((resolve, reject) => {

                // init token client using google client id
                const client = window.google.accounts.oauth2.initTokenClient({
                    client_id: API.google_client_id,
                    scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
                    callback: response => {
                        if(response.access_token) {
                            resolve(response);
                        } else {
                            reject(new Error('user_cancelled'));
                        }
                    },
                    error_callback: e => {
                        let error = new Error('user_cancelled');
                        reject(error);
                    }
                });
                
                // request access token with user gesture hint
                client.requestAccessToken({ prompt: 'consent' });
            });

            // submit request to processor function
            await processSocialSignin({ channel: 'google', token: tokenResponse.access_token });

        } catch(e) {
            setLoading(false);
            if(e.message !== 'user_cancelled') {
                utils.alert.error({
                    error: e,
                    message: `There was an issue signing in with Google. ${e.message || 'An unknown error occurred'}`,
                    origin: 'add_edit_user.on_continue_with_google_click'
                });
            }
        }
    }

    const onForgotPasswordClick = () => {
        
        const onConfirm = async props => {
            try {

                // submit request to server
                setLoading('reset_password');
                await Request.post(utils, '/users/', {
                    ...props,
                    type: 'new_reset_password_request'
                });

                // return success message to sender
                setLoading(false);
                utils.banner.show({
                    message: `Your password verification ${API.use_email_2fa ? 'email' : 'text message'} has been sent. The reset link will expire after 15 minutes.`,
                    title: 'Password Reset Sent',
                });

            } catch(e) {
                setLoading(false);
                utils.error.show({
                    error: e,
                    message: `We were unable to send your password reset verification message. ${e.message || 'An unknown error occurred'}`,
                    origin: 'on_forgot_password_click.on_confirm'
                })
            }
        }
        
        // request phone number input from user
        let result = null;
        utils.alert.show({
            title: 'Forgot Password?',
            message: `No worries — it happens to the best of us. Just enter your ${API.use_email_2fa ? 'email address' : 'phone number'} below, and we'll send you ${API.use_email_2fa ? 'an email' : 'a text message'} with a secure link to reset your password. You'll be back in your account in no time.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    format={API.use_email_2fa ? 'email_address' : 'phone_number'}
                    onChange={text => result = text}
                    placeholder={API.use_email_2fa ? 'Email Address' : 'Phone Number'} />
                </div>
            ),
            buttons: [{
                key: 'continue',
                title: 'Continue',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(result && key === 'continue') {
                    setTimeout(onConfirm.bind(this, {
                        email_address: API.use_email_2fa ? result : null,
                        phone_number: API.use_email_2fa ? null : result
                    }));
                    return;
                }
            }
        });
    }

    const onLoginWithPhoneNumber = async props => {
        try {

            // set loading flag and submit request to server
            setLoading('phone_number');
            let { user } = await Request.post(utils, '/users/', {
                ...props,
                type: 'login_with_phone_number'
            });

            // request confirmation from the user to stay signed in for 30 days
            let session = await requestSessionEnabled();

            // submit request result to processor
            processLoginResult(user, { use_session: session });

        } catch(e) {

            setLoading(false);

            // require that user submit a mfa passkey along with their username and password if applicable
            if(e.code === Request.status.get().mfa_required) {
                onRequestRequiredPasskey(passkey => onLoginWithPhoneNumber({ ...props, passkey: passkey }));
                return;
            }
            utils.alert.error({ 
                error: e,
                message: `There was an issue logging into your account. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_login_with_phone_number'
            });
        }
    }

    const onPassKeyClick = async () => {
        try {

            // request web authentication for access key
            let credential = await navigator.credentials.get({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rpId: window.location.hostname,
                    userVerification: 'required'
                }
            });
            
            // set loading flag and submit request to server
            setLoading('passkey');
            let { user } = await Request.post(utils, '/users/', {
                passkey: credential.id,
                secure_auth: secureAuth,
                type: 'login'
            });

            // request confirmation from the user to stay signed in for 30 days
            let session = await requestSessionEnabled();

            // submit request result to processor
            processLoginResult(user, { use_session: session });

        } catch(e) {

            // remove trusted device token from local storage if error indicates that device is no longer trusted
            if(e.code === Request.status.get().invalid_trusted_device_token) {
                utils.user.device.remove_current_token();
            }

            // no further action is required if user denied the request
            setLoading(false);
            if(e.message.includes('user denied') === true) {
                return;
            }

            // show error alert explaining login failure
            utils.alert.error({
                error: e,
                message: `There was an issue logging into your account. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_passkey_click'
            });
        }
    }

    const onPhoneNumberClick = async () => {
        let phoneNumber = null;
        utils.alert.show({
            title: 'Sign-In With Phone Number',
            message: `Enter your phone number to sign in. We will text you a quick verification code so we know it is really you. Just enter the code to finish logging in.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    autoComplete={'tel'}
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    onChange={text => phoneNumber = text}
                    placeholder={'Phone Number'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Send Code',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && phoneNumber) {
                    onSendPhoneNumberLoginCode(phoneNumber);
                    return;
                }
            }
        });
    }

    const onRequestRequiredPasskey = async callback => {
        try {

            // update route to show customer some context around this request
            setRoute('username_password_mfa');

            // request web authentication for access key
            await Utils.sleep(0.5);
            let credential = await navigator.credentials.get({
                publicKey: {
                    challenge: new Uint8Array([117, 61, 252, 231, 191, 241]),
                    rpId: window.location.hostname,
                    userVerification: 'required'
                }
            });

            // submit request to login again
            callback(credential.id);
            
        } catch(e) {
            
            setRoute(null);
            if(e.message.includes('user denied') === true) {
                return;
            }

            // show error alert explaining failure
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your passkey. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_request_required_passkey'
            });
        }
    }

    const onSendPhoneNumberLoginCode = async phoneNumber => {
        try {

            let code = null;

            // set loading flag and submit request to server
            setLoading('phone_number');
            await Request.post(utils, '/users/', {
                phone_number: phoneNumber,
                type: 'new_phone_number_login_request'
            });

            // end loading and show alert to enter verification code
            setLoading(false);
            utils.alert.show({
                title: 'Verification Code',
                message: `A verification code has been sent to ${Utils.format.phoneNumber(phoneNumber)}. Please enter the code below to complete your login.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        autoComplete={'one-time-code'}
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'Verification Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && code) {
                        setTimeout(onLoginWithPhoneNumber.bind(this, { code, phone_number: phoneNumber }), 500);
                        return;
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({ 
                error: e,
                message: `There was an issue validating your verification code. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_send_phone_number_login_code'
            });
        }
    }

    const onSetPhoneNumber = (user, options = {}) => {

        let phoneNumber = null;
        utils.alert.show({
            title: 'Phone Number Required',
            message: `For added security, all accounts must have a verified phone number. Please enter your mobile phone number below to continue.`,
            content: (
                <div style={{
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextField
                    autoComplete={'tel'}
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    format={'phone_number'} 
                    onChange={text => phoneNumber = text}
                    placeholder={'Phone Number'} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Submit',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && phoneNumber) {
                    setTimeout(() => {
                        onSetPhoneNumberConfirm(user, phoneNumber, options);
                    }, 500);
                    return;
                }

                // fallack to end loading from upstream request 
                setLoading(false);
            }
        });
    }

    const onSetPhoneNumberConfirm = async (user, phoneNumber, options = {}) => {
        try {

            // submit request to server
            await Request.post(utils, '/users/', {
                phone_number: phoneNumber,
                token: user.refresh_token,
                type: 'set_phone_number'
            });

            // process login result
            processLoginResult(user, options);

        } catch(e) {
            setLoading(false);
            utils.alert.error({ 
                error: e,
                message: `There was an issue setting your phone number. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_set_phone_number_confirm'
            });
        }
    }

    const onSignupClick = () => {
        setLayerState('close');
        utils.user.request.signup({ 
            onComplete: user => onLogin(user)
        });
    }

    const onSubmitClick = async props => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('login');
            let { user } = await Request.post(utils, '/users/', {
                ...edits,
                passkey: props?.passkey,
                secure_auth: secureAuth,
                type: 'login'
            });

            //submit request result to processor
            processLoginResult(user, { use_session: sessionEnabled });

        } catch(e) {

            // update loading state
            setLoading(false);

            // remove trusted device token from local storage if error indicates that device is no longer trusted
            if(e.code === Request.status.get().invalid_trusted_device_token) {
                utils.user.device.remove_current_token();
            }

            // require that user submit a mfa passkey along with their username and password if applicable
            if(e.code === Request.status.get().mfa_required) {
                onRequestRequiredPasskey(passkey => onSubmitClick({ ...props, passkey }));
                return;
            }

            // show error alert with customer facing description and reset route
            setRoute(null);
            utils.alert.error({
                error: e,
                message: `There was an issue logging into your account. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(prev => ({ ...prev, ...props }));
    }

    const getContent = () => {

        // return mfa passkey details for account that require mfa to login
        if(route === 'username_password_mfa') {
            return getRequiredPasskeyPlaceholder();
        }
        
        return (
            <>
            <LayerItem 
            collapsed={null}
            lastItem={true}
            title={'Login'}>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    textAlign: 'left'
                }}>
                    {`Sign in using the method that works best for you — Facebook, Google, Passkey, or your username and password. New Here? `}
                    <span 
                    className={'cursor-pointer'}
                    onClick={onSignupClick}
                    style={{
                        color: Appearance.colors.primary(),
                        textDecoration: 'underline'
                    }}>{'Signup for free.'}</span>
                </span>
            </LayerItem>
            {getExpressLoginComponents()}
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 24,
                marginTop: 24,
                width: '100%'
            }}>
                <div style={{
                    backgroundColor: Appearance.colors.divider(),
                    borderRadius: 1,
                    height: 2,
                    marginRight: 12,
                    width: '100%'
                }} />
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    flex: 'none'
                }}>{'OR'}</span>
                <div style={{
                    backgroundColor: Appearance.colors.divider(),
                    borderRadius: 1,
                    height: 2,
                    marginLeft: 12,
                    width: '100%'
                }} />
            </div>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                padding: 16,
                width: '100%'
            }}>
                {getLoginFields()}
                <div 
                className={'row mx-0'}
                style={{
                    marginBottom: 24,
                    marginTop: 16,
                    width: '100%'
                }}>
                    <div className={'col-12 col-md p-0 pb-2 p-md-0 d-flex align-items-center'}>
                        <Checkbox 
                        checked={sessionEnabled}
                        onChange={setSessionEnabled} />
                        <span 
                        className={'cursor-pointer'}
                        onClick={setSessionEnabled.bind(this, !sessionEnabled)}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            marginLeft: 12,
                            whiteSpace: 'nowrap'
                        }}>{'Remember Me For 30 Days'}</span>
                    </div>
                    <div className={'col-12 col-md pb-2 p-0 p-md-0 d-flex justify-content-md-end'}>
                        <span 
                        className={'cursor-pointer'}
                        onClick={onForgotPasswordClick}
                        style={{
                            ...Appearance.textStyles.defaultText(),
                            textDecoration: 'underline',
                            whiteSpace: 'nowrap'
                        }}>{'Forgot Password?'}</span>
                    </div>  
                </div>
                <Button 
                color={'primary'}
                label={'Login'}
                loading={loading === 'login'}
                onClick={onSubmitClick}
                type={'large'} />
            </div>
            </>
        )
    }

    const getExpressLoginComponents = () => {
        
        let services = [{ 
            id: 'facebook',
            image: 'images/sign-in-with-facebook.png',
            name: 'Continue with Facebook',
            onClick: onContinueWithFacebookClick
        },{
            id: 'google',
            image: 'images/sign-in-with-google.png',
            name: 'Continue with Google',
            onClick: onContinueWithGoogleClick
        },{
            id: 'passkey',
            image: window.theme === 'dark' ? 'images/sign-in-with-passkey-dark.png' : 'images/sign-in-with-passkey-light.png',
            name: 'Continue with PassKey',
            onClick: onPassKeyClick
        },{
            id: 'phone_number',
            image: window.theme === 'dark' ? 'images/sign-in-with-phone-number-dark.png' : 'images/sign-in-with-phone-number-light.png',
            name: 'Continue with Phone Number',
            onClick: onPhoneNumberClick
        }];
        
        return route === null && (
            <div style={{
                marginTop: 24,
                width: '100%'
            }}>
                 {services.map((service, index) => {
                    return (
                        <div 
                        className={`view-entry ${window.theme}`}
                        onClick={service.onClick}
                        key={index}
                        style={{
                            alignItems: 'center',
                            borderColor: Appearance.colors.divider(),
                            borderRadius: 20,
                            borderStyle: 'solid',
                            borderWidth: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            height: 40,
                            justifyContent: 'center',
                            marginBottom: index === services.length - 1 ? 0 : 8,
                            padding: 12,
                            textAlign: 'center',
                            width: '100%'
                        }}>
                            {loading === service.id && (
                                <LottieView
                                autoPlay={true}
                                loop={true}
                                source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                                style={{
                                    height: 25,
                                    width: 25
                                }}/>
                            )}
                            {loading !== service.id && (
                                <>
                                <img 
                                src={service.image}
                                style={{
                                    height: 20,
                                    marginRight: 8,
                                    objectFit: 'contain',
                                    width: 20
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    flexGrow: 1,
                                    fontWeight: 600
                                }}>{service.name}</span>
                                </>
                            )}
                        </div>
                    )
                })}   
            </div>
        )
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'contact',
            lastItem: true,
            style: {
                border: 'none'
            },
            items: [{
                component: 'textfield',
                key: 'username',
                onChange: text => onUpdateTarget({ username: text }),
                props: {
                    autoCapitalize: false,
                    autoComplete: 'username'
                },
                title: 'Username',
                value: edits.username
            },{
                component: 'textfield',
                key: 'password',
                onChange: text => onUpdateTarget({ password: text }),
                props: {
                    autoComplete: 'current-password',
                    isSecure: true
                },
                title: 'Password',
                value: edits.password
            }]
        }];
    }

    const getLoginFields = () => {
        
        let fields = [{
            component: 'textfield',
            key: 'username',
            onChange: text => onUpdateTarget({ username: text }),
            props: {
                autoCapitalize: false,
                autoComplete: 'username'
            },
            title: 'Username',
            value: edits.username
        },{
            component: 'textfield',
            key: 'password',
            onChange: text => onUpdateTarget({ password: text }),
            props: {
                autoComplete: 'current-password',
                isSecure: true
            },
            title: 'Password',
            value: edits.password
        }];

        return fields.map((field, index) => {
            return (
                <AltFieldItem 
                item={field}
                lastItem={index === fields.length - 1}
                key={index}
                utils={utils} />
            )
        });
    }

    const getRequiredPasskeyPlaceholder = () => {
        return (
            <div style={{
                padding: 18,
                width: '100%'
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 24,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <img 
                    src={'images/login-with-passkey.png'}
                    style={{
                        height: 50,
                        marginBottom: 16,
                        width: 50
                    }} />
                    <span style={{
                        ...Appearance.textStyles.title(),
                        fontFamily: 'Zen Dots',
                        marginBottom: 4,
                        whiteSpace: 'normal'
                    }}>{'Additional Authentication Required'.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        lineHeight: 1.5,
                        whiteSpace: 'normal'
                    }}>{`Your account is protected with a passkey or security key for multi-factor authentication. To finish signing in, follow the instructions from your browser.`}</span>
                    <Button 
                    color={'dark'}
                    containerStyle={{
                        marginTop: 12,
                        width: '100%'
                    }}
                    label={'Back'}
                    onClick={setRoute.bind(this, null)}
                    type={'large'} />
                </div>
            </div>
        )
    }

     const initializeFacebookSDK = () => {
        return new Promise((resolve, reject) => {

            // check if sdk is already loaded
            if(window.FB) {
                resolve();
                return;
            }
            
            // load facebook sdk
            window.fbAsyncInit = () => {
                window.FB.init({
                    appId: API.facebook_app_id_public,
                    cookie: true,
                    xfbml: true,
                    version: 'v18.0'
                });
                resolve();
            };

            // inject sdk script
            const script = document.createElement('script');
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
            document.body.appendChild(script);
        });
    }

    const initializeGoogleSDK = () => {
        return new Promise((resolve, reject) => {
            
            // check if sdk is already loaded
            if(window.google?.accounts && window.gapi) {
                resolve();
                return;
            }

            // load google api client library first
            const gapiScript = document.createElement('script');
            gapiScript.src = 'https://apis.google.com/js/api.js';
            gapiScript.async = true;
            gapiScript.defer = true;
            gapiScript.onload = () => {
                window.gapi.load('client', () => {

                    // load google identity services sdk
                    const script = document.createElement('script');
                    script.src = 'https://accounts.google.com/gsi/client';
                    script.async = true;
                    script.defer = true;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error('Failed to load Google SDK'));
                    document.head.appendChild(script);
                });
            };
            gapiScript.onerror = () => reject(new Error('Failed to load Google API'));
            document.head.appendChild(gapiScript);
        });
    }

    const processLoginResult = async (user, options) => {
        try {

            // log firebase login event
            utils.analytics.log('login', { method: route });

            // save refresh token for future logins if applicable
            if(options.use_session === true) {
                localStorage.setItem('user.refresh_token', user.refresh_token);
            }

            // retrieve trusted device token from storage
            let token = utils.user.device.get(user.user_id);

            // validate token if a token was found or start a new device registration otherwise
            if(token) {
                await validateTrustedDeviceToken(utils, { token, user_id: user.user_id }); 
            } else {
                token = await runTrustedDeviceRegistration(utils, user.user_id);
            }

            // end loading and close layer
            setLoading(false);
            setLayerState('close');

            // notify subscribers that login has completed
            if(typeof(onLogin) === 'function') {
                onLogin(user, { 
                    redirect: shouldRedirectUser && { view: 'accounts' },
                    trusted_device_token: token
                });
            }

        } catch(e) {
            if(e.code === Request.status.get().incomplete_user_profile) {
                onSetPhoneNumber(user, options);
                return;
            }
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue completing your login. ${e.message || 'An unknown error occurred'}`,
                origin: 'login.process_login_result'
            });
        }
    }

    const processSocialSignin = props => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit access token to server for social login
                let { user } = await Request.post(utils, '/users/', {
                    ...props,
                    type: 'login_with_social'
                });

                // request confirmation from the user to stay signed in for 30 days
                let session = await requestSessionEnabled();
    
                // submit request result to processor
                processLoginResult(user, { use_session: session });

            } catch(e) {

                // require that user submit a mfa passkey along with their username and password if applicable
                if(e.code === Request.status.get().mfa_required) {
                    onRequestRequiredPasskey(async passkey => {
                        try {
                            await processSocialSignin({ ...props, passkey });
                            resolve();
                        } catch(e) {
                            setRoute(null);
                            reject(e);
                        }
                    });
                    return;
                }
                reject(e);
            }
        });
    }

    const requestSessionEnabled = () => {
        return new Promise(resolve => {
            utils.alert.show({
                title: 'Remember Me',
                message: `Would you like to stay signed in for 30 days on this device? This is not recommended for shared or public computers.`,
                buttons: [{
                    key: 'yes',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'no',
                    title: 'No',
                    style: 'cancel'
                }],
                onClick: key => {
                    resolve(key === 'yes');
                }
            });
        });
    }

    useEffect(() => {
        initializeGoogleSDK().catch(e => console.warn('Failed to preload Google SDK:', e));
        initializeFacebookSDK().catch(e => console.warn('Failed to preload Facebook SDK:', e));
    }, []);
    
    return (
        <Layer
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            onCloseButtonClick: setLayerState.bind(this, 'close')
        }}>
            {getContent()}
        </Layer>
    )
}

export const ManageSocialMediaChannelContentPreferences = ({ onChange, source, preferences }, { index, options, utils }) => {
    
    const layerID = 'manage_social_media_channel_content_preferences';
    const [edits, setEdits] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [layerState, setLayerState] = useState(null);

    const onCloseClick = () => {
        if(hasMadeEdits === false) {
            setLayerState('close');
            return;
        }
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitClick, 500);
                    return;
                }
                setLayerState('close');
            }
        });
    }

    const onSubmitClick = async () => {
        try {

            // verify that all required fields are completed
            await validateRequiredFields(getFields);

            // prepare payload for subscribers
            let payload = { 
                ...edits,
                date: moment(edits.date).utc()
            }

            // verify that post date is in the future
            if(edits.enabled && moment().utc().isAfter(payload.date, 'day') === true) {
                throw new Error('Please choose a scheduled post date that is in the future.');
            }

            // notify subscribers of data change and close layer
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(payload);
            }

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue saving your preferences. ${e.message || 'An unknown error occurred'}`,
                origin: 'manage_social_media_channel_content_preferences.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setHasMadeEdits(true);
        setEdits(({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            onClick: onSubmitClick,
            text: 'Save Changes'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            id: 'preferences',
            lastItem: false,
            title: 'Preferences',
            items: [{
                component: 'bool_list',
                id: 'enabled',
                onChange: val => onUpdateTarget({ enabled: val }),
                title: 'Automatically Post Content',
                value: edits.enabled
            },{
                component: 'textview',
                id: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'Caption',
                value: edits.description || source.description
            },{
                component: 'textfield',
                id: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                title: 'Post Name',
                value: edits.title || source.title
            },{
                component: 'date_picker',
                id: 'date',
                onChange: date => onUpdateTarget({ date: date?.format('YYYY-MM-DD') }),
                required: edits.enabled,
                title: 'Scheduled Post Date',
                value: edits.date,
                visible: edits.enabled
            }]
        }];
    }

    const setupEditing = () => {
        setEdits({ ...preferences, date: moment(preferences.date) });
    }
    
    useEffect(() => {
        setupEditing();
    }, []);
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}
        utils={utils}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const ManageSocialMediaContentPreferences = ({ onChange, source }, { index, options, utils }) => {
    
    const layerID = 'manage_social_media_content_preferences';
    const [edits, setEdits] = useState(source);
    const [hasMadeEdits, setHasMadeEdits] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onCloseClick = () => {
        if(hasMadeEdits.length === 0) {
            setLayerState('close');
            return;
        }
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitClick, 500);
                    return;
                }
                setLayerState('close');
            }
        });
    }

    const onManuallyPostContentClick = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async (destination, options) => {
                try {

                    let { shouldSaveContent } = options || {};

                    // prevent submission from taking place if unsaved changes are present
                    if(shouldSaveContent !== false && hasMadeEdits.includes(destination.key) === true) {
                        utils.alert.show({
                            title: 'Unsaved Changes',
                            message: 'It looks like you have some unsaved changes. Would you like to save your changes before posting this content?',
                            buttons: [{
                                key: 'confirm',
                                title: 'Save Changes',
                                style: 'default'
                            },{
                                key: 'continue',
                                title: 'Continue Without Saving',
                                style: 'cancel'
                            }],
                            onClick: key => {
                                if(key === 'confirm') {
                                    setTimeout(onSubmitClick.bind(this, onConfirm.bind(this, destination, { shouldSaveContent: false })), 500);
                                    return;
                                }
                                if(key === 'continue') {
                                    setTimeout(onConfirm.bind(this, destination, { shouldSaveContent: false }), 500);
                                }
                                let error = new Error('user_cancelled');
                                reject(error);
                            }
                        });
                        return;
                    }

                    // submit request to server
                    await Request.post(utils, '/users/', {
                        channel: destination.channel,
                        target_id: destination.id,
                        source_id: source.id,
                        type: 'manually_post_social_media_content'
                    });

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        message: `Your content has been added to the queue and will be posted to your feed shortly. If you opted in for notifications, we'll let you know as soon as it goes live.`,
                        onClick: setLayerState.bind(this, 'close') 
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue posting your content. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            let destination = null;
            utils.alert.show({
                title: 'Post Content',
                message: 'Where would you like to post this content? Select a social media destination from the list below.',
                content: (
                    <div style={{
                        padding: '0px 12px 12px 12px',
                        width: '100%'
                    }}>
                        <ListField
                        items={getDestinationItems()}
                        onChange={item => destination = getDestinationItems().find(i => i.id === item.id)} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Submit',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && destination) {
                        setTimeout(onConfirm.bind(this, destination), 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onPreferencesClick = (channel, target, preferences) => {
        utils.layer.open({
            Component: ManageSocialMediaChannelContentPreferences.bind(this, {
                onChange: result => {

                    // update local state with new preferences
                    setEdits(edits => {
                        return update(edits, {
                            preferences: {
                                [channel]: {
                                    $apply: preferences => {

                                        // update edits object with identifier
                                        setHasMadeEdits(edits => {
                                            edits.push(`${channel}.${target.id}`);
                                            return edits;
                                        });

                                        // determine if edits were made to a facebook channel
                                        if(channel === 'facebook') {
                                            preferences.pages[target.id] = result;
                                            return preferences;
                                        }

                                        // fallback to working with general channels
                                        return result;
                                    }
                                }
                            }
                        });
                    });
                },
                preferences,
                source
            }),
            id: 'manage_social_media_channel_content_preferences'
        });
    }

    const onSubmitClick = async callback => {
        try {

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/users/', {
                source_id: source.id,
                preferences: edits.preferences,
                type: 'update_social_account_source_preferences'
            });

            // end loading and reset edits flag
            setLoading(false);
            setHasMadeEdits([]);

            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange(edits);
            }

            // call optional callback if provided
            if(typeof(callback) === 'function') {
                callback();
                return;
            }

            // show confirmation alert
            utils.banner.show({
                title: 'Content Preferences Updated',
                message: `Your social media content preferences have been updated.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue updating your social media content preferences. ${e.message || 'An unknown error occurred'}`,
                origin: 'manage_social_media_channel_content_preferences.on_submit_click'
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Save Changes',
            visible: hasMadeEdits.length > 0 === true
        }];
    }

    const getContentPreview = () => {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 8,
                width: '100%'
            }}>
                {source.type === 'image' && (
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.divider(),
                        display: 'flex',
                        flexDirection: 'column',
                        height: 275,
                        justifyContent: 'center',
                        padding: 12,
                        width: '100%'
                    }}>
                        <img 
                        src={source.url}
                        style={{
                            borderRadius: 8,
                            height: '100%',
                            objectFit: 'contain',
                            width: 'auto'
                        }} />
                    </div>
                )}
                {source.type === 'video' && (
                    <div style={{
                        alignItems: 'center',
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 275,
                        justifyContent: 'center',
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        <video 
                        autoPlay={false}
                        controls={true}
                        muted={false}
                        poster={source.poster_image_url}
                        src={source.url}
                        style={{
                            height: '100%',
                            objectFit: 'cover',
                            width: '100%'
                        }} />
                    </div>
                )}
            </div>
        )
    }

    const getDestinationItems = () => {
        return source.channels.reduce((array, channel) => {
            if(source.preferences[channel]) {
                let targets = channel === 'facebook' ? Object.values(edits.preferences[channel].pages) : [edits.preferences[channel].account];
                targets.forEach(target => {
                    array.push({
                        channel: channel,
                        id: target.id,
                        key: `${channel}.${target.id}`,
                        title: `${Utils.ucFirst(channel)}: ${target.name}`
                    });
                });
            }
            return array;
        }, [{
            id: 'all',
            title: 'All Accounts'
        }]);
    }

    const getFields = () => {
        return [{
            collapsed: null,
            id: 'metadata',
            items: [{
                id: 'type',
                title: 'Content Type',
                value: Utils.ucFirst(source.type)
            },{
                id: 'title',
                title: 'Title',
                value: source.title
            },{
                id: 'description',
                style: {
                    container: {
                        alignItems: 'flex-start'
                    }
                },
                title: 'Description',
                value: source.description
            }]
        },{
            collapsed: false,
            id: 'destinations',
            lastItem: false,
            title: 'Destinations',
            items: source.channels.reduce((array, channel) => {
                if(source.preferences[channel]) {

                    // prepare list of targets for field
                    let targets = channel === 'facebook' ? Object.values(edits.preferences[channel].pages) : [edits.preferences[channel].account];

                    // loop through targets and prepare entries
                    targets.forEach(target => {

                        // prepare preferences for target 
                        let preferences = channel === 'facebook' ? target : edits.preferences[channel];

                        // determine if content has already been posted
                        if(preferences.post) {
                            array.push({
                                color: Appearance.colors.blue,
                                id: `${channel}.${target.id}`,
                                onClick: window.open.bind(this, preferences.post.url),
                                prepend: (
                                    <img 
                                    src={target.avatar_url}
                                    style={{
                                        borderRadius: 10,
                                        flex: 'none',
                                        height: 20,
                                        objectFit: 'cover',
                                        marginRight: 8,
                                        width: 20
                                    }} />
                                ),
                                title: targets.length === 1 ? Utils.ucFirst(channel) : `${Utils.ucFirst(channel)}: ${target.name}`,
                                value: 'Posted'
                            });
                            return;
                        }

                        // create entry for upcoming content
                        array.push({
                            color: preferences.enabled ? Appearance.colors.green : null,
                            id: `${channel}.${target.id}`,
                            onClick: onPreferencesClick.bind(this, channel, target, preferences),
                            prepend: (
                                <div style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row'
                                }}>
                                    {hasMadeEdits.includes(`${channel}.${target.id}`) === true && (
                                        <div style={{
                                            backgroundColor: Appearance.colors.primary(),
                                            borderRadius: 2.5,
                                            height: 5,
                                            marginRight: 8,
                                            width: 5
                                        }} />
                                    )}
                                    <img 
                                    src={target.avatar_url}
                                    style={{
                                        borderRadius: 10,
                                        flex: 'none',
                                        height: 20,
                                        objectFit: 'cover',
                                        marginRight: 8,
                                        width: 20
                                    }} />
                                </div>
                            ),
                            title: targets.length === 1 ? Utils.ucFirst(channel) : `${Utils.ucFirst(channel)}: ${target.name}`,
                            value: preferences.enabled ? 'Scheduled' : 'Not Setup'
                        });
                    });
                }
                return array;
            }, [])
        }];
    }

    const getResourcesComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'This allows you to post content directly to your feed instead of waiting for the scheduled post date.'}
                    image={'images/send-icon-small.png'}
                    lastItem={true}
                    onAsyncClick={onManuallyPostContentClick}
                    title={'Manually Post Content'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const setupEditing = () => {
        setEdits(source);
    }

    useEffect(() => {
        setupEditing();
    }, []);
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}
        utils={utils}>
            {getContentPreview()}
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getResourcesComponents()}
        </Layer>
    )
}

export const MyPartnerships = ({ utils }) => {
   
    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [partnerships, setPartnerships] = useState([]);

    const onPartnershipClick = partnership => {
        utils.layer.open({
            abstract: Abstract.create({
                object: partnership,
                type: 'partnerships'
            }),
            Component: PartnershipDetails,
            id: `partnerships.${partnership.id}`
        })
    }

    const getPartnerships = () => {
        return partnerships.map((partnership, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onPartnershipClick.bind(this, partnership)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== partnerships.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/partnership-dark.png' : 'images/partnership-light.png'}
                    style={{
                        borderRadius: 8,
                        height: Utils.isMobile() ? 40 : 55,
                        flex: 'none',
                        marginRight: 12,
                        objectFit: 'cover',
                        width: Utils.isMobile() ? 40 : 55
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        textAlign: 'left'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{partnership.name.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{partnership.url}</span>
                    </div>
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        flex: 'none',
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchPartnerships = async () => {
        try {
            let { paging, partnerships } = await Request.get(utils, '/users/', {
                limit: limit,
                offset: offset.current,
                type: 'partnerships'
            });

            setLoading(false);
            setPaging(paging);
            setPartnerships(partnerships.map(partnership => ({
                ...partnership,
                created: moment.utc(partnership.created).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your partnerships list. ${e.message || 'An unknown error occurred'}`,
                origin: 'partnerships.fetch_partnerships'
            });
        }
    }

    useEffect(() => {
        fetchPartnerships();
        utils.content.subscribe('partnerships_list', ['partnerships'], {
            onFetch: fetchPartnerships
        });
        return () => {
           utils.content.unsubscribe('partnerships_list'); 
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Below is a list of all partnerships and affiliate groups you're currently associated with through the LifeWipe partnership program. Each partnership represents a distinct onboarding or referral channel tied to your account. From here, you can manage your access to these partnerships and adjust your preferences.`}
                title={'My Partnerships'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getPartnerships()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            fetchPartnerships();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const NewChildPlanInvitation = ({ parent_plan, token }, { index, options, utils }) => {

    const layerID = 'new_child_plan_invitation';
    const [layerState, setLayerState] = useState(null);
    const { user } = useDataStore();

    const onShowSubscriptionSignup = () => {
        utils.layer.open({
            Component: NewSubscription.bind(this, { 
                familyPlan: true, 
                freeTrial: true,
                onComplete: setLayerState.bind(this, 'close'),
                token: token
            }),
            id: 'new_subscription'
        });
    }

    const onSignupClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: User.new(),
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: true,
                onComplete: onShowSubscriptionSignup
            }),
            id: 'new_user'
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'signup',
            onClick: user ? onShowSubscriptionSignup : onSignupClick,
            text: user ? `Continue as ${user.first_name}` : 'Signup'
        }]
    }

    const getContent = () => {

        // prepare list of line items for render
        let items = [{
            description: 'Your information stays yours. The person who invited you won’t have access to your messages, devices, or data.',
            title: 'Create your own private LifeWipe account'
        },{
            description: 'From emergency triggers to secure message delivery after death, you’ll choose how LifeWipe protects your devices and legacy.',
            title: 'Gain access to powerful digital protection tools'
        },{
            description: 'Pick the level of protection that fits your needs. There’s no auto-enrollment or hidden costs.',
            title: 'Choose your own plan during signup'
        }];

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <span style={{
                    ...Appearance.textStyles.heavyHeader(),
                    fontFamily: 'Zen Dots',
                    marginBottom: 16,
                    whiteSpace: 'normal'
                }}>{'You’ve Been Invited to Join a LifeWipe Family Plan'.toUpperCase()}</span>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    marginBottom: Utils.isMobile() ? 24 : 36
                }}>{`${parent_plan.user?.full_name || 'Someone'} has invited you to join their LifeWipe plan. This is your chance to create your own secure account and get covered under a plan designed to give peace of mind when it matters most.`}</span>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {items.map((item, index) => {
                        return (
                            <div 
                            key={index}
                            style={{
                                alignItems: 'center',
                                borderTop: index !== 0 && Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 16,
                                minWidth: 0,
                                width: '100%'
                            }}>
                                <img
                                className={'d-none d-xl-flex'} 
                                src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                style={{
                                    height: 45,
                                    marginRight: 18,
                                    objectFit: 'contain',
                                    width: 45
                                }} />
                                <div  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        fontWeight: 'bold',
                                        marginBottom: 2
                                    }}>
                                        {item.title}
                                    </span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        flexGrow: 1
                                    }}>
                                        {item.description}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getContent()}
        </Layer>
    )
}

export const NewInstructionFromTemplate = ({ onChange }, { index, options, utils }) => {

    const layerID = 'new_instruction_from_template';
    const limit = 5;

    const [completion, setCompletion] = useState(null);
    const [edits, setEdits] = useState({});
    const [instructions, setInstructions] = useState([]);
    const [instructionsOffset, setInstructionsOffset] = useState(0);
    const [instructionsPaging, setInstructionsPaging] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [progressBarAnimations, progressBarAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        width: '0%',
    }));
    const [providersOffset, setProvidersOffset] = useState(0);
    const [providersPaging, setProvidersPaging] = useState(null);
    const [selection, setSelection] = useState(null);
    const [selectedInstructionIndex, setSelectedInstructionIndex] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [templatesOffset, setTemplatesOffset] = useState(0);
    const [templatesPaging, setTemplatesPaging] = useState(null);
    const { user, setUser } = useDataStore();

    const onAttachToTrigger = action => {
        return new Promise(async (resolve, reject) => {
            try {

                let isLifeChecks = action.includes('life_checks') === true;

                // submit request to server to send new sms opt in message
                await Request.post(utils, '/subscriptions/', {
                    id: completion.id,
                    type: action
                });

                // update user subscription with new config
                setUser(user => {
                    user.subscription.preferences[isLifeChecks ? 'life_checks' : 'emergency_triggers'].trigger_instructions.push(completion.id);
                    return user;
                });

                resolve({
                    banner: true,
                    message: `Your new instruction has been attached to your ${isLifeChecks ? 'LifeChecks' : 'Emergency Trigger'} and is ready to use.`,
                    title: isLifeChecks ? `LifeChecks Updated` : `Emergency Trigger Updated` 
                });

            } catch(e) {
                reject( e);
            }
        }); 
    }

    const onContinueClick = () => {

        let categories = Instruction.categories.get();
        
        // verify that a linked account was provided if applicable
        let target = selection.trigger_instructions[selectedInstructionIndex];
        if(requiresLinkedAccount() === true && !edits.linked_account) {
            utils.alert.show({
                title: 'Linked Account Required',
                message: `It looks like this instruction requires a linked account to function properly. Please select a linked account before continuing.`
            });
            return;
        }

        // prepare a name for the instruction
        let name = selection.trigger_instructions.length === 1 ? selection.name : `${selection.name} (${selectedInstructionIndex + 1}/${selection.trigger_instructions.length})`;

        // create instruction target
        let instruction = Instruction.new();
        instruction.category = target.category;
        instruction.name = name;
        instruction.preferences = {
            actions: target.actions.map(action => action.id),
            template_id: selection.id
        };
        instruction.user_id = user.user_id;


        // set target id using the instruction category
        switch(target.category) {
            case categories.accounts:
            instruction.preferences.target_id = edits.linked_account.id;
            break;

            case categories.blueprints:
            instruction.preferences.target_id = target.blueprint_id;
            break;
        }

        // open editing layer for new instruction
        utils.layer.open({
            abstract: Abstract.create({
                object: instruction,
                type: 'trigger_instructions'
            }),
            Component: AddEditInstruction.bind(this, { 
                isNewTarget: true,
                isTemplate: true,
                onChange: onRequestNextInstruction,
            }),
            id: 'new_trigger_instruction'
        });
    }

    const onInstructionClick = target => {

        // create instruction from target
        let instruction = Instruction.new();
        instruction.category = target.category;
        instruction.name = `${target.name} (Copy)`;
        instruction.preferences = target.preferences;
        instruction.user_id = user.user_id;

        // close layer and notify subscribers of data change
        setLayerState('close');
        if(typeof(onChange) === 'function') {
            onChange(instruction); 
        }
    }

    const onNoInstructionFoundClick = () => {
        utils.alert.show({
            title: 'No Instructions Found',
            message: `It looks like there are no instructions associated with your account. Instructions will show up here as template options once you've created as least one instruction.`,
            buttons: [{
                key: 'create_instruction',
                title: 'Create Instruction',
                style: 'default'
            },{
                key: 'close',
                title: 'Close',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'create_instruction') {
                    setLayerState('close');
                    if(typeof(onChange) === 'function') {
                        onChange({}); 
                    }
                    return;
                }
            }
        });
    }

    const onProviderClick = provider => {

        let target = selection.trigger_instructions[selectedInstructionIndex];

        // select provider account if exactly one linked account exists
        let accounts = target.supported_linked_accounts.filter(act => act.provider === provider.id);
        if(accounts.length === 1) {
            onUpdateTarget({ linked_account: accounts[0] });
            return;
        }

        // ask user which account to use if more than one exists
        if(accounts.length > 1) {
            let selected = null;
            utils.alert.show({
                title: 'Select Account',
                message: `Multiple linked accounts are available for ${provider.title}. Please select the account you'd like to use for this instruction.`,
                content: (
                    <div style={{
                        padding: '0px 12px 12px 12px',
                        width: '100%'
                    }}>
                        <ListField
                        items={accounts.map(account => ({
                            id: account.id,
                            title: account.name
                        }))}
                        onChange={item => selected = item?.id} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(selected && key === 'confirm') {
                        let account = accounts.find(act => act.id === selected);
                        onUpdateTarget({ linked_account: account });
                        return;
                    }
                }
            });
            return;
        }

        // reset selection if no linked accounts exist
        onUpdateTarget({ linked_account: null });

        // show alert explaing that a new account is going to be linked
        utils.alert.show({
            title: 'Link New Account',
            message: `To use this instruction, you'll need to link your ${provider.title} account now. Would you like to proceed?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    
                    // prepare target for account editing
                    let account = Account.new();
                    account.category = provider.category && { id: provider.category };
                    account.name = `${provider.title} Account`;
                    account.provider = { id: provider.id };
                    
                    // open layer with editing options
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: account,
                            type: 'accounts'
                        }),
                        Component: AddEditAccount.bind(this, { 
                            isNewTarget: true,
                            onAccountAdded: result => {

                                // update state with selected account
                                onUpdateTarget({ 
                                    linked_account: {
                                        id: result.id,
                                        name: result.name,
                                        provider: result.provider
                                    } 
                                });
                                
                                // update selection with new linked account
                                setSelection(selection => {
                                    selection.trigger_instructions = selection.trigger_instructions.map((instruction, index) => {
                                        if(index === selectedInstructionIndex) {
                                            instruction.supported_linked_accounts.push({
                                                id: result.id,
                                                name: result.name,
                                                provider: result.provider
                                            });
                                            instruction.supported_linked_accounts = instruction.supported_linked_accounts.sort((a,b) => {
                                                return a.name.localeCompare(b.name);
                                            });
                                        }
                                        return instruction;
                                    });
                                    return selection;
                                });

                                // fetch resources to update linked accounts list
                                fetchResources();
                            }
                        }),
                        id: 'new_linked_account'
                    });
                }
            }
        })
    }

    const onRequestNextInstruction = instruction => {

        // notify subscribers that new instruction has been created
        utils.content.getch('trigger_instructions');

        // show completion if no more instructions are required
        let next = selection.trigger_instructions[selectedInstructionIndex + 1];
        if(!next) {
            setCompletion(instruction);
            return;
        }

        // show alert indicating that another instruction is ready to be configured
        utils.alert.show({
            title: `Instruction ${selectedInstructionIndex + 1}/${selection.trigger_instructions.length} Added`,
            message: `The instruction has been added successfully. Let's set up the next instruction in the template.`,
            buttons: [{
                key: 'confirm',
                title: 'Okay',
                style: 'default'
            }],
            onClick: () => {
                setSelectedInstructionIndex(selectedInstructionIndex + 1);
            }
        });
    }

    const onUpdateTarget = props => {
        setEdits(prev => ({ ...prev, ...props }));
    }

    const getButtons = () => {
        if(completion?.id) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }
        if(selection) {
            return [{
                color: 'dark',
                id: 'back',
                onClick: () => {
                    setEdits({});
                    setSelection(null);
                },
                text: 'Back'
            },{
                color: 'primary',
                id: 'continue',
                onClick: onContinueClick,
                text: 'Continue',
                visible: requiresLinkedAccount() && !edits.linked_account ? false : true
            }]
        }
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getCompletion = () => {
        return (
            <>
            <LayerItem 
            collapsed={false}
            title={'Setup Complete'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 16px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                    }}>{`Your ${selection.name} trigger instruction are now fully configured and ready. You can return at any time to review, edit, or disable individual instructions. To finalize setup, you may want to take a few follow-up actions — like attaching this instruction to an emergency trigger or linking additional contacts. These options are available below.`}</span>
                </div>
            </LayerItem>
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Next Steps'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {selection.next_actions?.map((action, index, actions) => {

                        let description = '';
                        let image = '';
                        let title = '';

                        // prepare description for action
                        switch(action) {
                            case 'attach_trigger_instruction_to_emergency_trigger':
                            description = 'Setup this instruction to automatically run if an Emergency Trigger is activated and Trusted Contacts have confirmed your status.';
                            image = 'images/attach-to-emergency-trigger-red.png';
                            title = 'Attach To Emergency Trigger';
                            break;

                            case 'attach_trigger_instruction_to_life_checks':
                            description = 'Setup this instruction to automatically run if you fail a LifeCheck and Trusted Contacts have confirmed your status.';
                            image = 'images/attach-to-life-checks-green.png';
                            title = 'Attach To LifeChecks';
                            break;
                        }

                        return (
                            <AsyncInteractionItem 
                            description={description}
                            image={image}
                            key={index}
                            lastItem={index === actions.length - 1}
                            onAsyncClick={onAttachToTrigger.bind(this, action)}
                            title={title}
                            utils={utils} />
                        )
                    })}
                </div>
            </LayerItem>
            </>
        )
    }

    const getComplexityColor = val => {
        switch(val) {
            case 'basic':
            return Appearance.colors.green;

            case 'intermediate':
            return Appearance.colors.orange;

            case 'advanced':
            return Appearance.colors.red;

            default:
            return Appearance.colors.grey();
        }
    }
    
    const getContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(completion?.id) {
            return getCompletion();
        }
        if(selection) {
            return getInstructionsSetup();
        }
        return (
            <>
            {getTemplates()}
            {getInstructions()}
            </>
        )
    }

    const getInstructions = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'My Instructions'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`Below are trigger instructions you’ve previously created. You can select any one as a template to jumpstart a new instruction — its structure will carry over, but the new version will be saved separately. Use your past thinking as a launchpad. Adjust timing, triggers, or actions as needed to match a new scenario — without starting from scratch.`}</span>
                    {instructions.length === 0 && (
                        <AsyncInteractionItem 
                        description={'There are no instructions associated with your account.'}
                        image={'images/instruction-template-grey.png'}
                        lastItem={true}
                        onClick={onNoInstructionFoundClick}
                        title={'No Instructions Found'}
                        utils={utils} />
                    )}
                    {instructions.filter((instruction, index) => {
                        return index >= instructionsOffset && index < (instructionsOffset + limit);
                    }).map((instruction, index) => {
                        return (
                            <AsyncInteractionItem 
                            description={instruction.description}
                            key={index}
                            lastItem={index === instructions.length - 1}
                            onClick={onInstructionClick.bind(this, instruction)}
                            title={instruction.name}
                            utils={utils} />
                        )
                    })}
                    {instructionsPaging && (
                        <PageControl
                        data={instructionsPaging}
                        limit={limit}
                        offset={instructionsOffset}
                        onClick={setInstructionsOffset} />
                    )}
                </div>
            </LayerItem>
        )
    }

    const getInstructionsSetup = () => {

        let target = selection.trigger_instructions[selectedInstructionIndex];
        let name = selection.trigger_instructions.length === 1 ? selection.name : `${selection.name} (${selectedInstructionIndex + 1}/${selection.trigger_instructions.length})`;

        return (
            <>
            {selection.trigger_instructions.length > 1 && getProgressBar()}
            <LayerItem 
            collapsed={false}
            title={name}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 16px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`Start building your instruction using the ${selection.name} template. Each template includes one or more actions that will be triggered if you're unresponsive, such as after a failed life-check or an emergency trigger. We'll show you exactly which actions are included, whether any of your connected accounts are supported, and give you full control to adjust or remove steps before finalizing.`}</span>
                </div>
            </LayerItem>
            <LayerItem 
            collapsed={false}
            lastItem={requiresLinkedAccount() === false}
            title={`Review The Planned Actions`}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`Here’s what this instruction is set to do. Each action listed below will be triggered if this instruction activates. You’ll have a chance to configure the details of each step next — including what gets deleted, who gets notified, and what gets shared.`}</span>
                    {target.actions.map((action, index, actions) => {
                        return (
                             <div 
                             key={index}
                             style={{
                                borderBottom: index !== actions.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '12px 16px 16px 16px',
                                textAlign: 'left',
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    fontFamily: 'Zen Dots',
                                    marginBottom: 2,
                                    whiteSpace: 'normal'
                                }}>{action.title.toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.subTitle(),
                                    lineHeight: 1.5,
                                    whiteSpace: 'normal'
                                }}>{action.description}</span>
                            </div>
                        )
                    })}
                </div>
            </LayerItem>
            {getLinkedAccountSelector()}
            </>
        )
    }

    const getLinkedAccountSelector = () => {

        let target = selection.trigger_instructions[selectedInstructionIndex];
        return requiresLinkedAccount() === true && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={`Choose a Linked Account`}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`Below is a list of supported providers for this instruction, based on the actions included in the template. From your connected accounts, select the ones you want this instruction to apply to. Only accounts that meet all action requirements will appear here. If none are listed, you may need to link a new account or choose a different instruction template.`}</span>
                    {target.supported_providers.filter((provider, index) => {
                        return index >= providersOffset && index < (providersOffset + limit);
                    }).map((provider, index, providers) => {
                        return (
                             <div 
                             className={`view-entry ${window.theme}`}
                             onClick={onProviderClick.bind(this, provider)}
                             key={index}
                             style={{
                                alignItems: 'center',
                                borderBottom: index !== providers.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                flexDirection: 'row',
                                padding: '12px 16px 12px 16px',
                                textAlign: 'left',
                                width: '100%'
                            }}>
                                <img 
                                src={provider.icon}
                                style={{
                                    borderRadius: 5,
                                    height: 25,
                                    width: 25
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    flexGrow: 1,
                                    fontFamily: 'Zen Dots',
                                    marginLeft: 8,
                                    whiteSpace: 'normal'
                                }}>{provider.title.toUpperCase()}</span>
                                {getProviderAccessory(provider)}
                            </div>
                        )
                    })}
                    {providersPaging && (
                        <PageControl
                        data={providersPaging}
                        limit={limit}
                        offset={providersOffset}
                        onClick={setProvidersOffset} />
                    )}
                </div>
            </LayerItem>
        )
    }

    const getProgressBar = () => {
        if(!selection) {
            return null;
        }
        return (
            <div style={{
                backgroundColor: Appearance.colors.divider(),
                borderRadius: 2.5,
                height: 5,
                marginBottom: 16,
                position: 'relative'
            }}>
                <animated.div style={{
                    backgroundColor: Appearance.colors.primary(),
                    borderRadius: 2.5,
                    height: 5,
                    ...progressBarAnimations
                }} />
            </div>
        )
    }

    const getProviderAccessory = provider => {

        let target = selection.trigger_instructions[selectedInstructionIndex];

        // determine if provider is selected
        if(edits.linked_account?.provider === provider.id) {
            return (
                <AltBadge 
                content={{
                    color: Appearance.colors.primary(),
                    text: 'Selected'
                }} 
                style={{
                    marginLeft: 8,
                    marginRight: 0
                }} />
            )
        }

        // determine if at least one account has been linked for the provider
        let linked = target.supported_linked_accounts.find(act => act.provider === provider.id);
        if(linked) {
            return (
                <img
                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                style={{
                    height: 15,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 15
                }} />
            )
        }

        // fallback to showing an unlinked provider badge
        return (
            <AltBadge 
            content={{
                color: Appearance.colors.grey(),
                text: 'Not Linked'
            }} 
            style={{
                marginLeft: 8,
                marginRight: 0
            }} />
        )
    }

    const getTemplates = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={false}
            title={'Not sure where to start?'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: '12px 16px 16px 16px',
                    }}>{`These pre-built instruction templates are designed by LifeWipe to cover the most common digital exit scenarios — from wiping cloud accounts to handing off financial tools or protecting your family’s access. Select any template to use it as a foundation. You can modify the actions, add linked accounts, and assign trusted contacts as needed.`}</span>
                    {templates.filter((template, index) => {
                        return index >= templatesOffset && index < (templatesOffset + limit);
                    }).map((template, index) => {
                        return (
                            <AsyncInteractionItem 
                            description={template.description}
                            key={index}
                            lastItem={index === templates.length - 1}
                            onClick={setSelection.bind(this, template)}
                            rightContent={(
                                <AltBadge 
                                content={{
                                    color: getComplexityColor(template.complexity),
                                    text: template.complexity
                                }} 
                                style={{
                                    marginRight: 0,
                                    marginLeft: 8
                                }} />
                            )}
                            title={template.name}
                            utils={utils} />
                        )
                    })}
                    {templatesPaging && (
                        <PageControl
                        data={templatesPaging}
                        limit={limit}
                        offset={templatesOffset}
                        onClick={setTemplatesOffset} />
                    )}
                </div>
            </LayerItem>
        )
    }

    const fetchResources = async () => {
        try {

            // submit request to server
            setLoading(true);
            let { templates, trigger_instructions } = await Request.get(utils, '/users/', {
                type: 'trigger_instruction_templates'
            });

            // end loading and update local state
            setLoading(false);
            setInstructions(trigger_instructions.map(instruction => Instruction.create(instruction)));
            setTemplates(templates);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your instructions list. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_instruction_from_template.fetch_resources'
            });
        }
    } 

    const requiresLinkedAccount = () => {
        return selection?.trigger_instructions[selectedInstructionIndex]?.category === Instruction.categories.get().accounts;
    }

    useEffect(() => {
        setInstructionsPaging({
            current_page: Math.floor(instructionsOffset / limit) + 1,
            number_of_pages: instructions.length > limit ? Math.ceil(instructions.length / limit) : 1,
            total: instructions.length
        });
    }, [instructions, instructionsOffset]);

    useEffect(() => {
        if(requiresLinkedAccount() === true) {
            let target = selection.trigger_instructions[selectedInstructionIndex];
            setProvidersPaging({
                current_page: Math.floor(providersOffset / limit) + 1,
                number_of_pages: target.supported_providers.length > limit ? Math.ceil(target.supported_providers.length / limit) : 1,
                total: target.supported_providers.length
            });
        }
    }, [selection, providersOffset]);

    useEffect(() => {
        if(selection) {
            progressBarAnimationsAPI.start({ width: `${(selectedInstructionIndex / selection.trigger_instructions.length) * 100}%` });
        }
    }, [selection, selectedInstructionIndex]);

    useEffect(() => {
        setTemplatesPaging({
            current_page: Math.floor(templatesOffset / limit) + 1,
            number_of_pages: templates.length > limit ? Math.ceil(templates.length / limit) : 1,
            total: templates.length
        });
    }, [templates, templatesOffset]);

    useEffect(() => {
        fetchResources();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getContent()}
        </Layer>
    )
}

export const NewPartnershipEnrollment = ({ alias }, { index, options, utils }) => {

    const layerID = 'new_partnership_enrollment';
    const legalAgreementsRef = useRef(null);
    
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [partnership, setPartnership] = useState(null);
    const [signupCode, setSignupCode] = useState(null);
    const { user, setUser } = useDataStore();

    const onSubmitClick = async () => {
        try {

            // throw an error if no signup code was provided
            if(!signupCode) {
                throw new Error('Please provide a valid signup code to continue.');
            }

            // verify that all legal agreements have been reviewed and completed
            let legalAgreements = await legalAgreementsRef.current?.getCompletedAgreements() || {};

            // set loading flag and submit request to server
            setLoading('submit');
            let { legal_agreements, partnership_id } = await Request.post(utils, '/partnerships/', {
                alias: alias,
                legal_agreements: legalAgreements,
                signup_code: signupCode,
                type: 'join_partnership'
            });

            // end loading and update user object with id for partnership
            setLoading(false);
            setUser(user => {
                user.legal_agreements = legal_agreements;
                user.partnership_ids = user.partnership_ids || [];
                user.partnership_ids.push(partnership_id);
                return user; 
            });

            // show confirmation alert and refresh subscribed lists
            utils.alert.show({
                title: 'Welcome Aboard!',
                message: `You have successfully joined the ${partnership.name} partnership program. You can now access all the benefits and features associated with this partnership.`,
                onClick: () => {
                    setLayerState('close');
                    utils.content.fetch('partnerships');
                }
            }); 

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue joining the partnership program. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_partnership_enrollment.on_submit_click'
            }); 
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getContent = () => {
        if(!partnership) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return (
            <>
            <LayerItem 
            collapsed={false}
            title={`Join ${partnership.name}`}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 16px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginBottom: 16
                    }}>{`You're about to join the partnership program for ${partnership.name}. To continue, please enter your authorized signup code to verify your access and complete your registration. If you don’t have a code, contact your team lead or LifeWipe partner manager.`}</span>
                    <TextField 
                    isSecure={true}
                    onChange={setSignupCode}
                    placeholder={'Signup Code'}
                    value={signupCode} />
                </div>
            </LayerItem>
            <LegalAgreements
            documentIDs={['partner_code_of_conduct_agreement']}
            editable={user.legal_agreements?.partner_code_of_conduct_agreement ? false : true}
            lastItem={true}
            ref={legalAgreementsRef}
            showDescription={true}
            utils={utils} 
            value={user.legal_agreements} />
            </>
        )
    }

    const fetchPartnershipData = async () => {
        try {

            // fetch details for partner from server
            setLoading(true);
            let { partnership } = await Request.get(utils , '/partnerships/', {
                alias: alias,
                type: 'public_details'
            });

            // update local data store with partnership data
            setLoading(false);
            setPartnership(partnership);
            
        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the partnership details. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close'),
                origin: 'new_partnership_enrollment.fetch_partnership_data'
            });
        }
    }

    useEffect(() => {
        fetchPartnershipData();
    }, []);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
           {getContent()} 
        </Layer>
    )
}

export const NewPartnershipUserSession = ({ index, options, utils }) => {

    const layerID = 'new_partnership_user_session';
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { partnership, sockets, setPartnership } = useDataStore();
    const [route, setRoute] = useState(null);

    const onCloseClick = () => {
        if(!partnership.user) {
            setLayerState('close');
            return;
        }
        utils.alert.show({
            title: 'End Session',
            message: `Are you sure you want to end this session? Any unsaved changes will be lost and the user will need to continue onboarding on their own.`,
            buttons: [{
                key: 'confirm',
                title: 'End Session',   
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Continue Session',
                style: 'default'
            }], 
            onClick: key => {
                if(key === 'confirm') {
                    setLayerState('close');
                    return;
                }
            }
        });
    }

    const onLegalAgreementsClick = () => {
        return new Promise((resolve, reject) => {

            // register function for final confirmation request to send agreement responses
            const onConfirm = async documents => {
                try {

                    // submit request to server to send new sms opt in message
                    await Request.post(utils, '/partnerships/', {
                        document_ids: userAccountLegalAgreementIds,
                        legal_agreements: documents,
                        type: 'update_legal_agreements'
                    });

                    resolve({ message: `The legal agreement results for ${partnership.user.first_name} ${partnership.user.last_name} have been recorded. You may proceed with the rest of the onboarding process.`})

                } catch(e) {
                    reject( e);
                }
            }

            // register function to send email to user with link to complete legal agreements
            const sendEmail = async () => {
                try {

                    // submit request to server to send agreemnts via email
                    await Request.post(utils, '/partnerships/', {
                        document_ids: userAccountLegalAgreementIds,
                        type: 'send_legal_agreements'
                    });

                    resolve({ message: `The legal agreement have been sent via email for ${partnership.user.first_name} ${partnership.user.last_name} to review. You may proceed with the rest of the onboarding process after they have confirmed the documents.`})

                } catch(e) {
                    reject( e);
                }
            }

            // register function to show legal agreements in browser
            const showAgreements = () => {
                utils.layer.open({
                    Component: LegalAgreementsLayer.bind(this, {
                        documentIDs: userAccountLegalAgreementIds,
                        onCancel: () => {
                            let error = new Error('user_cancelled');
                            reject(error);
                        },
                        onComplete: onConfirm
                    }),
                    id: 'legal_agreements'
                });
            }

            // request method of legal agreement review from user
            utils.alert.show({
                title: 'Legal Agreements',
                message: 'The customer must review and accept LifeWipe’s legal agreements before activation. You can either send them a link to complete this privately by email, or review the agreements with them now in this browser. Please choose the option that best fits the customer’s preference.',
                buttons: [{
                    key: 'browser',
                    title: 'Review Now',
                    style: 'default'
                },{
                    key: 'email',
                    title: 'Send via Email',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'browser') {
                        showAgreements();
                        return;
                    }
                    if(key === 'email') {
                        sendEmail();
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onNewLegalAgreementsCompleted = evt => {
        try {
            if(evt.user_id === partnership.user.user_id) {
                setPartnership(target => {
                    target.user.legal_agreements = evt.legal_agreements;
                    return target;
                });
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const onNewSubscriptionEvent = evt => {
        try {
            if(evt.user_id === partnership.user.user_id) {
                setPartnership(target => {
                    target.user.has_subscription = true;
                    return target;
                });
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const onPartnershipDetailsClick = () => {
        utils.alert.show({
            title: 'About This Partnership',
            message: `${partnership.name} is an official LifeWipe affiliate. They work with us to offer digital legacy protection to their customers, helping spread awareness while earning a commission on each subscription. Customer service and data remain fully managed by LifeWipe.`
        });
    }

    const onSetPartnershipUser = user => {

        // leave from for existing user if applicable
        if(partnership.user) {
            sockets.send('users', 'leave', {
                partnership_id: partnership.id,
                user_id: partnership.user.user_id
            });
        }

        // update partnership object with new user
        setPartnership(target => {
            target.user = user;
            return target;
        });
    }

    const onSmsOptInClick = () => {
        return new Promise(async (resolve, reject) => {
            
            const onConfirm = async () => {
                try {

                    // submit request to server to send new sms opt in message
                    await Request.post(utils, '/partnerships/', {
                        partnership_id: partnership.id,
                        type: 'new_sms_opt_in',
                        user_id: partnership.user.user_id
                    });

                    resolve({ message: `A text message has been sent to ${partnership.user.phone_number}. ${partnership.user.first_name} needs to respond START to begin receiving text messages from LifeWipe.`})

                } catch(e) {
                    reject( e);
                }
            }

            // request confirmation from the user
            utils.alert.show({
                title: 'Send SMS',
                message: `Are you sure you want to send a new SMS opt-in message to ${Utils.format.phoneNumber(partnership.user.phone_number)}? Messaging and data rates may apply.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        }); 
    }

    const onSmsOptInResult = evt => {
        try {
            if(evt.user_id === partnership.user.user_id) {
                setPartnership(target => {
                    target.user.sms_compliance = evt.result;
                    return target;
                });
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const onSubmitNewUserClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            let { user } = await Request.post(utils, '/partnerships/', {
                ...edits,
                partner_signup_code: partnership.signup_code,
                type: 'new_user'
            });
            
            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'Signup Complete',
                message: `${edits.first_name} ${edits.last_name} has been registered and will receive an email shortly with their username and a link to create their password`,
                onClick: onSetPartnershipUser.bind(this, user)
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue creating the account. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_partnership_user_session.on_submit_new_user_click'
            });
        }
    }

    const onSubscriptionPlanSelectionClick = () => {
         utils.layer.open({
            Component: NewSubscription.bind(this, {
                freeTrial: true,
                isPartnershipRequest: true
            }),
            id: 'new_subscription'
        });
    }

    const onUpdateTarget = props => {
        setEdits(({ ...edits, ...props }));
    }

    const getButtons = () => {
        if(!route) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: onCloseClick,
                text: 'Close'
            }];
        }
        return [{
            color: 'dark',
            id: 'back',
            onClick: () => {
                onSetPartnershipUser(null);
                setRoute(null);
            },
            text: 'Back'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitNewUserClick,
            text: 'Submit',
            visible: partnership.user ? false : true
        }];
    }

    const getVariableProps = key => {

        // determine if the key is for subscription status
        if(key === 'has_subscription') {
            return {
                color: partnership.user?.has_subscription ? Appearance.colors.green : Appearance.colors.orange,
                value: partnership.user?.has_subscription ? 'Completed' : 'Incomplete'
            }
        }

        // determine if the key is for legal agreements
        if(key === 'legal_agreements') {
            let keys = userAccountLegalAgreementIds;
            let results = Object.keys(partnership.user?.legal_agreements || {}).filter(key => keys.includes(key));
            return {
                color: keys.length === results.length ? Appearance.colors.green : Appearance.colors.orange,
                value: keys.length === results.length ? 'Completed' : 'Incomplete'
            }
        }

        // determine if the key is for sms compliance
        if(key === 'sms_compliance') {
            let val = typeof(partnership.user?.sms_compliance) === 'boolean' ? partnership.user?.sms_compliance : null;
            return {
                color: val === null ? Appearance.colors.orange : (val ? Appearance.colors.green : Appearance.colors.red),
                value: val === null ? 'Incomplete' : (val ? 'Completed' : 'Rejected')
            }
        }
    }

    const getContent = () => {
        if(route === null) {
            return (
                <>
                {getHeader()}
                <div style={{
                    padding: 16,
                    width: '100%'
                }}> 
                    {getSessionComponents()}
                    {getDisclaimer()}
                </div>
                </>
            )
        }
        if(partnership.user) {
            return (
                <>
                {getHeader()}
                <div style={{
                    padding: 16,
                    width: '100%'
                }}> 
                    {getUserComponent()}
                    {getOnboardingSteps()}
                    {getDisclaimer()}
                </div>
                </>
            )
        }
        if(route === 'existing_user') {
            return (
                <>
                {getHeader()}
                <div style={{
                    padding: 16,
                    width: '100%'
                }}> 
                    <LayerItem 
                    collapsed={false}
                    title={'User Lookup'}>
                         <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: '12px 16px 16px 16px',
                            width: '100%'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                marginBottom: 16
                            }}>{`Search for existing LifeWipe users associated with the ${partnership.name} user base. You can look them up by first and last name, email address, or phone number to check their account status or continue onboarding.`}</span>
                            <UserLookup
                            isPartnershipRequest={true}
                            metadata={['has_subscription', 'legal_agreements', 'sms_compliance']}
                            onChange={onSetPartnershipUser} 
                            utils={utils} />
                        </div>
                    </LayerItem>
                    {getDisclaimer()}
                </div>
                </>
            )
        }
        if(partnership) {
            return (
                <>
                {getHeader()}
                <div style={{
                    padding: 16,
                    width: '100%'
                }}> 
                    <AltFieldMapper
                    fields={getFields()}
                    utils={utils} />
                    {getDisclaimer()}
                </div>
                </>
            )
        }
    }

    const getDisclaimer = () => {
        return (
            <span style={{
                color: Appearance.colors.subText(),
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
                marginTop: 24,
                width: '100%'
            }}>{`By using this system to register customers for LifeWipe, you agree to follow all applicable partner guidelines and represent that each customer has consented to be signed up. Your use of this platform is subject to LifeWipe’s Partner Terms and Privacy Policy, and may be monitored for compliance. LifeWipe is committed to protecting user privacy and ensuring all personal information is handled securely`}</span>
        )
    }

    const getFields = () => {
        return [{
            key: 'about',
            title: 'Contact Information',
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text?.trim() }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text?.trim() }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            }]
        }];
    }

    const getHeader = () => {
        return null;
        return (
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 16,
                paddingTop: 12,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                leftContent={typeof(partnership.logo_url) === 'string' && (
                    <img 
                    src={partnership.logo_url}
                    style={{
                        borderRadius: 8,
                        height: 45,
                        marginRight: 12,
                        width: 'auto'
                    }} />
                )}
                rightContent={(
                    <img 
                    className={'text-button'}
                    onClick={onPartnershipDetailsClick}
                    src={window.theme === 'dark' ? 'images/partnership-signup-details-white.png' : 'images/partnership-signup-details-black.png'}
                    style={{
                        height: 25,
                        width: 25
                    }} />
                )}
                subTitle={`In Limited Partnership With ${partnership.name}`}
                title={`LifeWipe Inc`}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginTop: 16
                    }}>{`As an official LifeWipe partner, ${partnership.name} customers now have access to LifeWipe’s powerful digital legacy system — built to protect your data, your wishes, and your digital afterlife. This exclusive partnership ensures customer information is handled securely, exactly the way you want.`}</span>
                </LayerItem>
            </div>
        )
    }

    const getOnboardingSteps = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Onboarding Steps'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={`The customer must review and accept LifeWipe’s legal agreements to complete their account setup. This includes terms of service, data policies, and consent documents. Click here to begin.`}
                    image={'images/legal-agreements-clear.png'}
                    imageStyle={{
                        backgroundColor: getVariableProps('legal_agreements').color
                    }}
                    onAsyncClick={onLegalAgreementsClick}
                    title={'Legal Agreements'}  
                    utils={utils} />
                    <AsyncInteractionItem
                    description={`Before LifeWipe can send the customer any text messages, they must first give their explicit consent. Click here to send an opt-in text message to ${Utils.format.phoneNumber(partnership.user.phone_number)}.`}
                    image={'images/sms-opt-in-clear.png'}
                    imageStyle={{
                        backgroundColor: getVariableProps('sms_compliance').color
                    }}
                    onAsyncClick={onSmsOptInClick}
                    title={'SMS Opt-In'} 
                    utils={utils} />
                    <AsyncInteractionItem
                    description={`The customer must select a subscription plan to begin using LifeWipe services. This step is required to unlock full access. Click here to view the available plans and complete their subscription setup.`}
                    image={'images/subscription-plan-selection-clear.png'}
                    lastItem={true}
                    onClick={onSubscriptionPlanSelectionClick}
                    imageStyle={{
                        backgroundColor: getVariableProps('has_subscription').color
                    }}
                    title={'Subscription Plan Selection'}  
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getSessionComponents = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Onboarding'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={`If the customer is new to LifeWipe, you can register them here to create their account. Click here to begin the registration process.`}
                    image={'images/register-new-user-green.png'}
                    onClick={setRoute.bind(this, 'new_user')}
                    title={'Register New User'}  
                    utils={utils} />
                    <AsyncInteractionItem
                    description={`If the customer already has a LifeWipe account, you can look them up here to continue the onboarding process. Click here to search for an existing user.`}
                    image={'images/lookup-existing-user-blue.png'}
                    lastItem={true}
                    onClick={setRoute.bind(this, 'existing_user')}
                    title={'Lookup Existing User'}  
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getUserComponent = () => {

        let hasSubscription = getVariableProps('has_subscription');
        let legalAgreements = getVariableProps('legal_agreements');
        let smsCompliance = getVariableProps('sms_compliance');

        let sections = [{
            id: 'details',
            items: [{
                key: 'first_name',
                title: 'First Name',
                value: partnership.user.first_name
            },{
                key: 'last_name',
                title: 'Last Name',
                value: partnership.user.last_name
            },{
                key: 'email_address',
                title: 'Email Address',
                value: partnership.user.email_address
            },{
                key: 'phone_number',
                title: 'Phone Number',
                value: partnership.user.phone_number
            },{
                key: 'legal_agreements_completed',
                title: 'Policy and Consent Agreements',
                ...legalAgreements
            },{
                key: 'sms_compliance',
                title: 'SMS Compliance',
                ...smsCompliance
            },{
                key: 'subscription',
                title: 'Subscription',
                ...hasSubscription
            }]
        }];

        return (
            <LayerItem 
            collapsed={false}
            title={'Account Overview'}>
                <FieldMapper
                sections={sections}
                utils={utils} />
            </LayerItem>
        )
    }

    const getValueForVariable = target => {
        if(typeof(target) === 'boolean') {
            return target ? 'Completed' : 'Rejected'; 
        }
        return 'Incomplete';
    }

    const connectToSockets = () => {

        let userID = partnership.user.user_id;

        // send request to join room for user
        sockets.send('users', 'join', {
            partnership_id: partnership.id,
            user_id: userID
        });

        // listen to relevant account changes
        sockets.on('users', 'new_legal_agreements_completed', onNewLegalAgreementsCompleted);
        sockets.on('users', 'new_subscription_signup', onNewSubscriptionEvent);
        sockets.on('users', 'sms_opt_in_result', onSmsOptInResult);

        // return function to cleanup subscriptions on unmount
        let disconnect = userID => {
            sockets.send('users', 'leave_user', {
                partnership_id: partnership.id,
                user_id: userID
            });
            sockets.off('users', 'new_legal_agreements_completed', onNewLegalAgreementsCompleted);
            sockets.off('users', 'new_subscription_signup', onNewSubscriptionEvent);
            sockets.off('users', 'sms_opt_in_result', onSmsOptInResult);
        }
        return disconnect.bind(this, userID);
    }

    useEffect(() => {
        if(partnership.user) {
            let disconnect = connectToSockets();
            return disconnect;
        }
    }, [partnership?.user]);
    
    useEffect(() => {
        /*
        setPartnership(target => {
            target.user = {
                email_address: 'mrcvideo@gmail.com',
                first_name: 'John',
                last_name: 'Smith',
                phone_number: '+12144582802',
                user_id: 159
            };
            return target;
        });
        */
        return () => {
            setPartnership(target => {
                target.user = null;
                return target;
            });
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            removePadding: true
        }}>
            {getContent()}
        </Layer>
    )
}

export const PartnershipDetails = ({ abstract, index, options, utils }) => {

    const layerID = `partnerships.${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [registration, setRegistration] = useState(null);
    const { setPartnership, setUser } = useDataStore();

    const onCopyOnboardingUrlClick = () => {

        // copy text to clipboard
        Utils.copyText(`https://enroll.life-wipe.com/${abstract.object.signup_code}`);

        // show banner indicating that copy took place
        utils.banner.show({
            title: 'Copied!',
            message: `The onboarding link for ${abstract.object.name} has been copied to your clipboard.`
        });
    }

    const onLeavePartnershipClick = () => {
        return new Promise((resolve, reject) => {

            const onConfirm = async () => {
                try {

                    // submit request to server to leave partnership
                    await Request.post(utils, '/partnerships/', {
                        partnership_id: abstract.getID(),
                        type: 'leave_partnership'
                    });

                    // remove partnership id from user ids in local storage
                    setUser(user => {
                        user.partnership_ids = user.partnership_ids.filter(id => id !== abstract.getID()) || [];
                        return user;
                    })

                    // remove partnership object from local state if applicable
                    setPartnership(target => {
                        if(target?.id === abstract.getID()) {
                            return null;
                        }
                        return target;
                    });

                    // notify subscribers that data new data is available
                    utils.content.fetch('partnerships');

                    // show confirmation alert to user
                    resolve({ 
                        message: `You have successfully left the partnership with ${abstract.object.name}`,
                        onClick: setLayerState.bind(this, 'close') 
                    });
                } catch(e) {
                    reject(e);
                }
            }

            // request confirmation from user to leave partnership
            utils.alert.show({
                title: 'Leave Partnership',
                message: `Are you sure you want to leave the ${abstract.object.name} partnership? You will lose access to any partnership benefits and offers associated with this affiliation.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',   
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 250);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            })
        });
    }
    
    const onStartSessionClick = () => {

        // close layer and set partnership object in local store
        setLayerState('close');
        setPartnership(abstract.object);

        // open layer to start new partnership user session
        utils.layer.open({
            Component: NewPartnershipUserSession,
            id: 'new_partnership_user_session'
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            id: 'details',
            lastItem: false,
            title: 'Details',
            items: [{
                id: 'id',
                title: 'ID',
                value: `PAR${abstract.getID()}`
            },{
                id: 'name',
                title: 'Name',
                value: abstract.object.name
            },{
                id: 'onboarding_url',
                onClick: onCopyOnboardingUrlClick,
                title: 'Onboarding URL',
                value: 'Click to Copy'
            },{
                id: 'website',
                onClick: window.open.bind(this, abstract.object.url),
                title: 'Website',
                value: 'Click to Open'
            }]
        },{
            collapsed: false,
            id: 'registration',
            lastItem: false,
            title: 'Team Assignment',
            visible: registration ? true : false,
            items: [{
                id: 'date',
                title: 'Join Date',
                value: Utils.format.date(registration?.date)
            },{
                color: registration?.active ? Appearance.colors.green : Appearance.colors.red,
                id: 'status',
                title: 'Status',
                value: registration?.active ? 'Active' : 'Inactive'
            }]
        }];
    }

    const getResources = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem
                    description={`Begin a new onboarding session for a customer associated with this partnership. This will guide you through the necessary steps to set up their account, review legal agreements, and ensure compliance.`}
                    image={'images/partnership-start-session-green.png'}
                    onClick={onStartSessionClick}
                    title={'Start Onboarding Session'}  
                    utils={utils} />
                    <AsyncInteractionItem
                    description={`If you no longer wish to be affiliated with this partnership, you can choose to leave it here. This action will revoke any partnership benefits and offers associated with your account.`}
                    image={'images/leave-partnership-red.png'}
                    lastItem={true}
                    onAsyncClick={onLeavePartnershipClick}
                    title={'Leave Partnership'}  
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const fetchRegistration = async () => {
        try {
            
            setLoading(true);
            let { registration } = await Request.get(utils, '/partnerships/', {
                partnership_id: abstract.getID(),
                type: 'registration_details'
            });

            setLoading(false);
            setRegistration(registration && {
                ...registration,
                date: moment.utc(registration.date).local()
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your partnership registration details. ${e.message || 'An unknown error occurred'}`,
                origin: 'partnership_details.fetch_registration'
            });
        }
    }

    useEffect(() => {
        fetchRegistration();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading
        }}>
            <FieldMapper 
            sections={getFields()}
            utils={utils} />
            {getResources()}
        </Layer>
    )
}

export const Referrals = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [users, setUsers] = useState([]);
    const { system, user } = useDataStore();

    const onCopyLinkClick = () => {

        // copy text to clipboard
        Utils.copyText(`https://lifewipe.pro/${user.referral_code}`);

        // show banner indicating that copy took place
        utils.banner.show({
            title: 'Copied!',
            message: `Your referral code has been copied to your clipboard`
        });
    }

    const onRenderUser = (user, index, users) => {
        return (
            <div 
            className={`view-entry ${window.theme}`}
            key={index}
            onClick={onUserClick.bind(this, user)}
            style={{
                alignItems: 'center',
                backgroundColor: null,
                borderBottom: index !== users.length - 1 && Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                padding: 16,
                width: '100%'
            }}>
                <img
                className={'d-none d-lg-flex'} 
                src={'images/avatar-placeholder.png'}
                style={{
                    borderRadius: 27.5,
                    height: 50,
                    marginRight: 16,
                    objectFit: 'contain',
                    width: 50
                }} />
                <div style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1
                }}>
                    <span style={{
                        ...Appearance.textStyles.header(),
                        fontFamily: 'Zen Dots'
                    }}>{`${user.first_name} ${user.last_initial}`.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: Appearance.colors.subText()
                    }}>{`${user.address.locality || 'Unknown City'}, ${user.address.administrative_area_level_1 || 'Unknown State'}. ${user.address.country || ''}`}</span>
                </div>
                {system.window.isMobile === false && user.has_valid_subscription && (
                    <AltBadge content={{
                        color: Appearance.colors.primary(),
                        text: 'Subscribed'
                    }} />
                )}
                <img
                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                style={{
                    height: 20,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 15
                }} />
            </div>
        )
    }

    const onShareClick = () => {

        // show alert with qr code element
        utils.alert.show({
            title: 'Referral QR Code',
            message: `Your QR code has been generated and is tied to your referral code. Share it directly from here to bring friends and clients into the LifeWipe fold—it's the easiest way to grow your network!`,
            content: (
                <div style={{
                    height: 250,
                    paddingBottom: 12,
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <img 
                    src={`https://lifewipe.pro/${user.referral_code}/qr-code`}
                    style={{
                        height: '100%',
                        objectFit: 'contain',
                        width: '100%'
                    }} />
                </div>
            )
        });
    }

    const onUserClick = user => {
        utils.alert.dev();
    }

    const getContainerButtons = () => {
        return [{
            color: 'secondary',
            id: 'share',
            onClick: onCopyLinkClick,
            text: 'Copy Referral Link'
        },{
            color: 'primary',
            id: 'share',
            onClick: onShareClick,
            text: 'Share My QR-Code'
        }];
    }

    const getSearchField = () => {
        return (
            <div style={{
                marginBottom: system.window.isMobile ? 16 : 24,
                width: '100%'
            }}>
                <TextField
                icon={'search'}
                loading={loading === 'search'}
                onChange={text => {
                    offset.current = 0;
                    setLoading('search');
                    setSearchText(text);
                }}
                placeholder={'Search by name, city, or state...'}
                useDelay={true}
                value={searchText} />
            </div>
        )
    }
    
    const getUsers = () => {
        if(loading === true) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(users.length === 0) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: 16,
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1
                        }}>
                            <span style={{
                                ...Appearance.textStyles.header(),
                                fontFamily: 'Zen Dots'
                            }}>{'No Referrals Found'.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: Appearance.colors.subText(),
                                whiteSpace: 'normal'
                            }}>{'We were unable to find any users who have signed up using your referral code.'}</span>
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div 
            className={'text-left text-xl-center'}
            style={{
                ...Appearance.styles.unstyledPanel(),
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxWidth: 1250,
                width: '100%'
            }}>
                {users.map(onRenderUser)}
            </div>
        )
    }

    const fetchUsers = async () => {
        try {
            let { paging, users } = await Request.get(utils, '/users/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                type: 'referrals'
            });

            setLoading(false);
            setUsers(users);
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your referrals list. ${e.message || 'An unknown error occurred'}`,
                origin: 'referrals.fetch_users'
            });
        }
    }

    useEffect(() => {
        fetchUsers();
    }, [searchText]);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`This area shows a list of all the users who have signed up for LifeWipe using your referral code. You can track who’s joined through your outreach and see how your digital legacy network is growing. Click the Share button to generate a QR code that you can share with friends or clients for easy signups.`}
                buttons={getContainerButtons()}
                title={'My Referrals'}>
                    {getSearchField()}
                    {getUsers()}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchUsers();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const ResetPasswordConfirmation = ({ token }, { index, options, utils }) => {

    const layerID = 'reset_password_confirmation';
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(null);

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            setLoading('submit');
            await validateRequiredFields(getFields);

            // verify that passwords match before moving on
            if(edits.password !== edits.confirm_password) {
                throw new Error('Your passwords do not match');
            }

            // submit request to server
            await Request.post(utils, '/users/', {
                password: edits.password,
                token: token,
                type: 'reset_password_confirmation'
            });

            // show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Password Reset',
                message: `Your password has been reset and is ready to use for future login attempts.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your new password. ${e.message || 'An unknown error occurred'}`,
                origin: 'reset_password_confirmation.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'credentials',
            items: [{
                component: 'textfield',
                key: 'password',
                onChange: text => onUpdateTarget({ password: text }),
                props: {
                    isSecure: true
                },
                title: 'New Password',
                value: edits.password
            },{
                component: 'textfield',
                key: 'confirm_password',
                onChange: text => onUpdateTarget({ confirm_password: text }),
                props: {
                    isSecure: true
                },
                title: 'Confirm New Password',
                value: edits.confirm_password
            }]
        }];
    }
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true,
            sizing: 'medium'
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'Time for a Fresh Start'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`Go ahead and enter your new password below — this will become your updated login moving forward. Make sure it’s something strong and memorable to keep your LifeWipe account safe and sound.`}</span>
                </LayerItem>
            </div>
            <div style={{
                padding: 18,
                width: '100%'
            }}>
                <AltFieldMapper
                fields={getFields()}
                utils={utils} />
            </div>
        </Layer>
    )
}

export const runTrustedDeviceRegistration = (utils, userID) => {

    const validateVerificationCode = (code, name) => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server to validate verification code
                let { token } = await Request.post(utils, '/trusted_devices/', {
                    code: code,
                    name: name,
                    type: 'validate_new_device_verification_code',
                    user_id: userID
                });

                // write trusted device token to local storage
                utils.user.device.set(userID, token);
                resolve(token);

            } catch(e) {
                reject(e);
            }
        });
    }

    return new Promise(async (resolve, reject) => {
        try {

            let code = null;
            let name = null;

            // submit request to start a trusted device registration
            let { email_obfuscated, phone_number_last_4 } = await Request.post(utils, '/trusted_devices/', {
                device: utils.device.get(),
                type: 'new',
                user_id: userID
            });

            // show alert with details about registration 
            utils.alert.show({
                title: 'Verification Required',
                message: `It looks like you are using a device that we don't recognize. We've sent a verification code to the ${API.use_email_2fa ? (email_obfuscated || 'email address on file with your account') : `phone number ending in ${phone_number_last_4}`}. Please enter a nickname for your device and the verification code below to continue.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        autoComplete={'off'}
                        containerStyle={{
                            marginBottom: 8
                        }}
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => name = text}
                        placeholder={'Device Nickname'} />
                        <TextField
                        autoComplete={'one-time-code'}
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'Verification Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: async key => {
                    try {
                        await Utils.sleep(0.5);
                        if(key === 'continue') {
                            let token = await validateVerificationCode(code, name);
                            resolve(token);
                            return;
                        }
                        throw new Error('Only trusted devices are able to interact with your account. Please login again and verify your device to continue.');
                    } catch(e) {
                        reject(e);
                    }
                }
            });

        } catch(e) {
            reject(e);
        }
    });
}

export const SetPassword = ({ onComplete }, { index, options, utils }) => {

    const layerID = 'set_password';
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(null);
    const { setUser } = useDataStore();

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            setLoading('submit');
            await validateRequiredFields(getFields);

            // verify that passwords match before moving on
            if(edits.password !== edits.confirm_password) {
                throw new Error('Your passwords do not match');
            }

            // submit request to server
            await Request.post(utils, '/users/', {
                password: edits.password,
                type: 'set_account_password'
            });

            // update user state if password config flag
            setUser(user => {
                user.onboarding.has_configured_password = true;
                return user;
            });

            // show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Password Set',
                message: `Your password has been set and is ready to use for future login attempts.`,
                onShow: () => {
                    setLayerState('close');
                    if(typeof(onComplete) === 'function') {
                        onComplete();
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your password. ${e.message || 'An unknown error occurred'}`,
                origin: 'set_password.on_submit_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'credentials',
            items: [{
                component: 'textfield',
                key: 'password',
                onChange: text => onUpdateTarget({ password: text }),
                props: {
                    isSecure: true
                },
                title: 'Password',
                value: edits.password
            },{
                component: 'textfield',
                key: 'confirm_password',
                onChange: text => onUpdateTarget({ confirm_password: text }),
                props: {
                    isSecure: true
                },
                title: 'Confirm Password',
                value: edits.confirm_password
            }]
        }];
    }
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true,
            sizing: 'medium'
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'Create Your Password'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`You’re just one step away from securing your account. Please create a password to finish setting up your profile and unlock access to your digital legacy tools.`}</span>
                </LayerItem>
            </div>
            <div style={{
                padding: 18,
                width: '100%'
            }}>
                <AltFieldMapper
                fields={getFields()}
                utils={utils} />
            </div>
        </Layer>
    )
}

export const SocialContent = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [sources, setSources] = useState([]);
    const { user } = useDataStore();

    const onManageAccountsClick = () => {
        utils.layer.open({
            Component: ConfigureSocialMediaAccounts,
            id: 'configure_social_media_accounts'
        });
    }

    const onSourceClick = source => {
        utils.layer.open({
            Component: ManageSocialMediaContentPreferences.bind(this, {
                onChange: result => {

                    // update local state for sources
                    let targets = sources.map(s => s.id === source.id ? result : s);
                    setSources(targets);
                },
                source
            }),
            id: 'manage_social_media_content_preferences'
        });
    }

    const onUploadContentClick = () => {
        utils.alert.dev();
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'manage',
            onClick: onManageAccountsClick,
            text: 'Configure Social Accounts'
        },{
            color: 'secondary',
            id: 'upload',
            onClick: onUploadContentClick,
            text: 'Upload Content'
        }];
    }

    const getSources = () => {
        if(sources.length === 0) { 
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    padding: 16,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.header(),
                        fontFamily: 'Zen Dots'
                    }}>{('No Content Found').toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>{'Configure at least one social media account to begin working with Nova outreach content.'}</span>
                </div>
            )
        }
        return sources.map((source, index) => {

            // prepare posts array
            let posts = Object.keys(source.preferences).reduce((array, key) => {
                if(key === 'facebook') {
                    let post = Object.keys(source.preferences[key].pages).find(pageID => source.preferences[key].pages[pageID].post !== false);
                    post && array.push(key);
                }
                if(key !== 'facebook' && source.preferences[key].post !== false) {
                    array.push(key);
                }
                return array;
            }, []);

            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onSourceClick.bind(this, source)}
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    src={source.type === 'video' ? source.poster_image_url : source.url}
                    style={{
                        borderRadius: 8,
                        height: Utils.isMobile() ? 40 : 55,
                        flex: 'none',
                        marginRight: 12,
                        objectFit: 'cover',
                        width: Utils.isMobile() ? 40 : 55
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        textAlign: 'left'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{`${sources.length - index}. ${source.title.toUpperCase()}`}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{source.description}</span>
                    </div>
                    {posts.map((channel, index) => {
                        return (
                            <img
                            className={'d-none d-lg-flex'}
                            key={index}
                            src={`images/${channel}-posts-icon.png`}
                            style={{
                                height: 25,
                                flex: 'none',
                                marginLeft: index === 0 ? 24 : 8,
                                objectFit: 'contain',
                                width: 25
                            }} />
                        )
                    })}
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        flex: 'none',
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const fetchSources = async () => {
        try {
            let { paging, sources } = await Request.get(utils, '/users/', {
                limit: limit,
                offset: offset.current,
                type: 'social_media_sources'
            });

            setLoading(false);
            setPaging(paging);
            setSources(sources.filter(source => {
                return source.enabled === true;
            }).map(source => ({
                ...source,
                date: moment.utc(source.date).local()
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your social media content. ${e.message || 'An unknown error occurred'}`,
                origin: 'social_content.fetch_sources'
            });
        }
    }

    useEffect(() => {
        fetchSources();
        utils.content.subscribe('social_content', ['social_media_content', 'social_media_users'], {
            onFetch: fetchSources
        });
        return () => {
           utils.content.unsubscribe('social_content'); 
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Nova is your command center for all things outreach: past submissions, upcoming scheduled drops, and any posts awaiting your manual approval. Track what's already gone live and review what’s in the launch pipeline. If you've enabled manual approval, this is where you'll approve, edit, or bypass queued posts. Think of this as your command center — the tactical core where timing, visibility, and reach are fully under your control.`}
                buttons={getContainerButtons()}
                title={'Nova Content Queue'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getSources()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            fetchSources();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const SystemEvents = ({ abstract, index, options, utils }) => {

    const layerID = 'system_events';
    const limit = 10;

    const [events, setEvents] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);

    const onDownloadClick = () => {
        return new Promise(async (resolve, reject) => {
            try {

                // request verification data for resource
                let verificationData = await getAuthCredential({ abstract, utils });

                // submit request to server
                let { url } = await Request.post(utils, '/users/', {
                    category: abstract.type,
                    id: abstract.getID(),
                    type: 'download_system_events',
                    verification_data: verificationData
                });

                // end loading and notify origin that promise has been resolved
                resolve({ 
                    title: 'Audit Trail Ready',
                    message: `Your audit trail is now available for download. Click the download button to continue.`,
                    buttons: [{
                        key: 'download',
                        title: 'Download',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        if(key === 'download') {
                            window.open(url);
                            return;
                        }
                    }
                });
    
            } catch(e) {
                let error = new Error(`There was an issue preparing your audit trail download. ${e.message || 'An unknown error occurred'}`);
                reject(error);
            }
        });
    }

    const onEventClick = evt => {
        utils.alert.dev();
    }

    const onNoEventsFoundClick = () => {
        utils.alert.show({
            title: 'No Events Found',
            message: 'There are no system events to display for this resource. We will keep monitoring and log any future events that occur. Please check back later.'
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }
    
    const getContent = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 24,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(events.length === 0) {
            return (
                <AsyncInteractionItem
                description={'No system events were found for this resource'}
                lastItem={true}
                onClick={onNoEventsFoundClick}
                title={'No Events Found'} />
            )
        }
        return (
            <>
            {events.map((event, index) => {
                return (
                    <AsyncInteractionItem
                    description={event.description}
                    key={index}
                    image={getIconForEvent(event)}
                    lastItem={index === events.length - 1}
                    onClick={onEventClick.bind(this, event)}
                    rightContent={(
                        <AltBadge 
                        content={{
                            color: Appearance.colors.grey(),
                            text: Utils.format.date(event.date, { minimal: true, exclude_time: true })
                        }} 
                        style={{
                            marginLeft: 8,
                            marginRight: 0 
                        }} />
                    )}
                    title={event.title} />
                )
            })}
            {paging && (
                <PageControl
                data={paging}
                limit={limit}
                offset={offset}
                onClick={setOffset}
                style={{
                    borderTop: null 
                }} />
            )}
            </>
        )
    }

    const getIconForEvent = evt => {
        switch(evt.category) {
            case 1:
            return 'images/system-events-accounts.png';

            case 2:
            return 'images/system-events-beneficiaries.png';

            case 3:
            return 'images/system-events-blueprints.png';

            case 4:
            return 'images/system-events-data-vault.png';

            case 5:
            return 'images/system-events-devices.png';

            case 6:
            return 'images/system-events-executors.png';

            case 7:
            return 'images/system-events-instructions.png';

            case 8:
            return 'images/system-events-notifications.png';

            case 9:
            return 'images/system-events-payments.png';

            case 10:
            return 'images/system-events-subscriptions.png';

            case 11:
            return 'images/system-events-system-triggers.png';

            case 12:
            return 'images/system-events-trusted-contacts.png';

            case 13:
            return 'images/system-events-trusted-devices.png';

            case 14:
            return 'images/system-events-verbose.png';

            default:
            return 'images/system-events-generic.png';
        }
    }

    const getResources = () => {
        return events?.length > 0 && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={`Download a comprehensive and decrypted log of all the activity that has taken place for "${abstract.getTitle()}".`}
                    image={'images/download-system-events.png'}
                    lastItem={true}
                    onAsyncClick={onDownloadClick}
                    title={'Download'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const fetchSystemEvents = async () => {
        try {

            let { events, paging } = await Request.get(utils, '/users/', {
                category: abstract.type,
                id: abstract.getID(),
                limit: limit,
                offset: offset,
                type: 'system_events'
            });

            setLoading(false);
            setPaging(paging);
            setEvents(events.map(evt => ({
                ...evt,
                date: moment.utc(evt.date).local()
            })));

        } catch(e) {
            setLoading(false);
                utils.alert.error({
                error: e,
                message: `There was an issue retrieving the system events list. ${e.message || 'An unknown error occurred'}`,
                origin: 'system_events.fetch_system_events'
            });
        }
    }

    useEffect(() => {
        fetchSystemEvents();
    }, [offset]);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <LayerItem
            collapsed={false}
            lastItem={events.length === 0}
            title={'Audit Trail'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: Appearance.styles.bottomBorder(),
                        display: 'block',
                        padding: 16,
                        paddingTop: 14
                    }}>{`This is your behind-the-scenes feed. Every update, trigger, or system action tied to "${abstract.getTitle()}" is logged here to keep you fully in the know. This log captures all system-level activity for auditing and accountability. Use it to verify when actions were taken and by whom.`}</span>
                    {getContent()}
                </div>
            </LayerItem>
            {getResources()}
        </Layer>
    )
}

export const SystemEventsAsyncInteractionItem = ({ abstract, lastItem = false, utils }) => {
    
    const onSystemEventsClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: SystemEvents,
            id: 'system_events'
        });
    }
    
    return (
        <AsyncInteractionItem 
        description={'This is a running log of all changes made to this resource—what was updated, when, and by whom.'}
        image={'images/system-events-icon-yellow.png'}
        lastItem={lastItem}
        onClick={onSystemEventsClick}
        title={'Audit Trail'}
        utils={utils} />
    )
}

export const TrustedContacts = ({ utils }) => {

    const limit = 10;
    const offset = useRef(0);

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const { requiem, setRequiem } = useDataStore();

    const onContactClick = contact => {
        utils.layer.open({
            abstract: Abstract.create({
                object: contact,
                type: 'trusted_contacts'
            }),
            Component: AddEditTrustedContact.bind(this, { 
                isNewTarget: false
            }),
            id: `trusted_contacts.${contact.id}`
        });
    }

    const onNewContactClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: {},
                type: 'trusted_contacts'
            }),
            Component: AddEditTrustedContact.bind(this, { 
                isNewTarget: true
            }),
            id: 'new_trusted_contact'
        });
    }

    const onProcessRequiemReviewRequest = async () => {
        try {

            // no further action is needed if there is no target match
            if(requiem.review?.category !== 'trusted_contacts') {
                return;
            }

            // fetch details for contact from server and update user object with changes
            let contact = await TrustedContact.get(utils, requiem.review.id);

            // open configuration for contact after a brief pause while the state updates
            setTimeout(onContactClick.bind(this, contact), 500);

            // reset requiem object for next request
            setRequiem({ ...requiem, review: null });

        } catch(e) {
            console.error(e.message);
        }
    }

    const getBadges = contact => {
        if(contact.metadata?.requiem_generated === true && contact.metadata?.requiem_confirmation_date === null) {
            return (
                <img 
                title={'Requiem Review Required'}
                src={'images/warning-icon-yellow.png'}
                style={{
                    height: 25,
                    marginLeft: 8,
                    width: 25
                }} />
            )
        }
    }

    const getContacts = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(contacts.length === 0) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-xl-flex'}
                    src={window.theme === 'dark' ? 'images/payments-icon-dark.png' : 'images/payments-icon-light.png'}
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{('No Trusted Contacts Found').toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            color: Appearance.colors.subText(),
                            whiteSpace: 'normal'
                        }}>{'We were unable to locate any trusted contacts for your account.'}</span>
                    </div>
                </div>
            )
        }
        return contacts.map((contact, index) => {
            return (
                <div 
                className={`view-entry ${window.theme}`}
                key={index}
                onClick={onContactClick.bind(this, contact)}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== contacts.length - 1 ? `1px solid ${Appearance.colors.divider()}` : 0,
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: 0,
                    padding: 16,
                    width: '100%'
                }}>
                    <img 
                    className={'d-none d-lg-flex'}
                    src={window.theme === 'dark' ? 'images/trusted-contact-dark.png' : 'images/trusted-contact-light.png' }
                    style={{
                        height: 50,
                        marginRight: 16,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <div style={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            ...Appearance.textStyles.header(),
                            fontFamily: 'Zen Dots'
                        }}>{contact.full_name.toUpperCase()}</span>
                        <span style={{
                            ...Appearance.textStyles.defaultText({ color: Appearance.colors.subText() }),
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{contact.email_address}</span>
                    </div>
                    {getBadges(contact)}
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 20,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                </div>
            )
        });
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'new',
            onClick: onNewContactClick,
            text: 'Add Trusted Contact'
        }];
    }

    const fetchContacts = async () => {
        try {
            setLoading(true);
            let { trusted_contacts, paging } = await Request.get(utils, '/users/', {
                limit: limit,
                offset: offset.current,
                type: 'trusted_contacts'
            });

            setLoading(false);
            setContacts(trusted_contacts);
            setPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your list of trusted contacts. ${e.message || 'An unknown error occurred'}`,
                origin: 'trusted_contacts.fetch_contacts'
            });
        }
    }

    useEffect(() => {
        onProcessRequiemReviewRequest();
    }, [requiem]);

    useEffect(() => {
        fetchContacts();
        utils.content.subscribe('trusted_contacts_list', ['trusted_contacts'], {
            onFetch: fetchContacts,
            onUpdate: abstract => {
                setContacts(contacts => {
                    return contacts.map(contact => {
                        return contact.id === abstract.getID() ? abstract.object : contact;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('trusted_contacts_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer 
                body={`In an emergency, you might not be able to hit the button yourself. That’s where your Trusted Contacts come in — handpicked allies who can activate, or halt, your LifeWipe instructions when you can’t. Whether you’re unconscious, missing, or confirmed deceased, these people have the authority (and responsibility) to trigger a remote wipe, release your final messages, or protect your private data. You control who they are. You control what they can do. And you can change them anytime.`}
                buttons={getContainerButtons()}
                title={'My Trusted Contacts'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        maxWidth: '100%',
                        width: '100%'
                    }}>
                        {getContacts()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchContacts();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const TrustedDevices = ({ utils }) => {

    const limit = Utils.isMobile() ? 5 : 10;
    const offset = useRef(0);

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);

    const onDeviceClick = device => {
        utils.layer.open({
            abstract: Abstract.create({
                object: device,
                type: 'trusted_devices'
            }),
            Component: AddEditTrustedDevice,
            id: `edit_trusted_device_${device.id}`
        });
    }

    const onRenderDevice = (device, index, devices) => {
        return (
            <div 
            className={`view-entry ${window.theme}`}
            key={index}
            onClick={onDeviceClick.bind(this, device)}
            style={{
                alignItems: 'center',
                borderBottom: index !== devices.length - 1 && Appearance.styles.bottomBorder(),
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                padding: 16,
                width: '100%'
            }}>
                <img 
                className={'d-none d-lg-flex'}
                src={device.image_url || window.theme === 'dark' ? 'images/logo-badge-dark.png' : 'images/logo-badge-light.png'}
                style={{
                    height: 45,
                    marginRight: 16,
                    objectFit: 'contain',
                    width: 45
                }} />
                <div style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1
                }}>
                    <span style={{
                        ...Appearance.textStyles.header(),
                        fontFamily: 'Zen Dots'
                    }}>{device.name.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        color: Appearance.colors.subText()
                    }}>{`Created: ${Utils.format.date(device.created)}`}</span>
                </div>
                {device.active === false && (
                    <AltBadge content={{
                        color: Appearance.colors.red,
                        text: 'Inactive'
                    }} />
                )}
                <img
                src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                style={{
                    height: 20,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 15
                }} />
            </div>
        )
    }

    const getDevices = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return devices.map(onRenderDevice);
    }

    const fetchDevices = async () => {
        try {

            let { devices, paging } = await Request.get(utils, '/trusted_devices/', {
                limit: limit,
                offset: offset.current,
                type: 'list'
            });

            setLoading(false);
            setDevices(devices.map(device => Device.Trusted.create(device)));
            setPaging(paging);

        } catch(e) {
            setLoading(false);
                utils.alert.error({
                error: e,
                message: `There was an issue retrieving your trusted devices list. ${e.message || 'An unknown error occurred'}`,
                origin: 'trusted_devices.fetch_devices'
            });
        }
    }

    useEffect(() => {
        fetchDevices();
        utils.content.subscribe('trusted_devices_list', ['trusted_devices'], {
            onFetch: fetchDevices,
            onUpdate: abstract => {
                setDevices(devices => {
                    return devices.map(device => {
                        return device.id === abstract.getID() ? abstract.object : device;
                    });
                });
            }
        });
        return () => {
            utils.content.unsubscribe('trusted_devices_list');
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`A trusted device is a mobile phone, tablet, or computer that you've previously used to log into your account. When you login to your account using a new device we'll send a two-factor authentication code to the phone number on your account to verify the login attempt. Once verified, your device becomes trusted and will not require verification codes during future login attempts.`}
                title={'Trusted Devices'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {getDevices()}
                    </div>
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        loading={loading === 'paging'}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            setLoading('paging');
                            fetchDevices();
                        }}
                        style={Utils.isMobile() === false && {
                            borderTop: null,
                            marginTop: 24
                        }} />
                    )}
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const userAccountLegalAgreementIds = ['consent_and_disclosure_acknowledgment','data_processing_and_storage_policy','terms_of_service'];

export const validateTrustedDeviceToken = (utils, { token, user_id }) => {
    return new Promise(async (resolve, reject) => {
        try {

            // submit request to server to validate device token
            let { error_message, validated } = await Request.post(utils, '/trusted_devices/', {
                token: token,
                type: 'validate_token',
                user_id: user_id
            });

            // throw an error if the token is invalid
            if(validated === false) {
                throw new Error(error_message);
            }
            resolve();

        } catch(e) {
            reject(e);
        }
    });
} 

export const runFacebookAuth = ({ config = '9549337748502141', state }) => {

    // prepare redirect uri
    let privateConfig = '1391550469015779';
    let redirectUri = encodeURIComponent(`${API.server}/social-media/auth/facebook/`);

    // open authorization window
    let options = getPopupWindowOptions({ height: 750, width: 750 });
    let url = `https://www.facebook.com/v21.0/dialog/oauth/?client_id=${API.facebook_app_id_nova}&config_id=${config}&redirect_uri=${redirectUri}&response_type=code&state=${state}#weblink`;
    window.open(url, 'Facebook Authorization', options);
}

export const runInstagramAuth = ({ state }) => {

    // prepare scopes and redirect uri
    let redirectUri = `${API.server}/social-media/auth/instagram/`;
    let scopes = ['instagram_business_basic', 'instagram_business_content_publish', /*'instagram_business_manage_comments', 'instagram_business_manage_insights', 'instagram_business_manage_messages'*/].join(',');

    // open authorization window
    let options = getPopupWindowOptions({ height: 750, width: 750 });
    let url = `https://api.instagram.com/oauth/authorize/?client_id=1295066349287454&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}`;
    window.open(url, 'Instagram Authorization', options);
}

export const runPinterestAuth = ({ state }) => {

    // prepare scopes and redirect uri
    let redirectUri = encodeURIComponent(`${API.server}/social-media/auth/pinterest/`);
    let scopes = ['ads:read','boards:read','boards:write','pins:read','pins:write','user_accounts:read','catalogs:read','catalogs:write'].join(',');

    // open authorization window
    let options = getPopupWindowOptions({ height: 750, width: 750 });
    let url = `https://www.pinterest.com/oauth/?client_id=1508237&response_type=code&redirect_uri=${redirectUri}&state=${state}&scope=${scopes}`;
    window.open(url, 'Pinterest Authorization', options);
}

export const runThreadsAuth = ({ state }) => {

    // prepare scopes and redirect uri
    let redirectUri = encodeURIComponent(`${API.server}/social-media/auth/threads/`)
    let scopes = ['threads_basic', 'threads_content_publish'/*, 'threads_manage_insights' */].join(',');

    // open authorization window
    let options = getPopupWindowOptions();
    let url = `https://threads.net/oauth/authorize?client_id=1762439241817667&scope=${scopes}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
    window.open(url, 'Threads Authorization', options);
}

export const runTikTokAuth = ({ state }) => {

    // prepare list of scopes and redirect uri
    let redirectUri = encodeURIComponent(`${API.server}/social-media/auth/tiktok/`)
    let scopes = ['user.info.basic','user.info.profile','user.info.stats','video.list','video.publish','video.upload'].join(',');

    // open authorization window
    let options = getPopupWindowOptions({ height: 1000, width: 500 });
    let url = `https://www.tiktok.com/v2/auth/authorize/?client_key=sbaw9noagztwknwsmv&response_type=code&scope=${scopes}&redirect_uri=${redirectUri}&state=${state}`;
    window.open(url, 'TikTok Authorization', options);
}

export const runYoutubeAuth = ({ state }) => {

    // prepare scopes and redirect uri
    let redirectUri = encodeURIComponent(`${API.server}/social-media/auth/youtube/`);
    let scopes = encodeURIComponent(['https://www.googleapis.com/auth/youtube.readonly','https://www.googleapis.com/auth/youtube.upload'].join(' '));

    // open authorization window
    let options = getPopupWindowOptions({ height: 750, width: 750 });
    let url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=814768281433-fpn7hc0mb0e8vmf299jngesn5cein3gs.apps.googleusercontent.com&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}&prompt=consent&access_type=offline`;
    window.open(url, 'YouTube Authorization', options);
}